{
  "consent": {
    "ariaLabel": "Cookie and analytics consent",
    "body": "We use optional Google Analytics cookies to understand how the site is used. The site works fully without them, and we never sell your information. Would you like to allow analytics cookies?",
    "privacyLink": "Privacy Policy",
    "accept": "Allow analytics",
    "decline": "No thanks",
    "disclaimer": "Educational information only, not medical advice. Always check with your transplant team. In an emergency, call 911."
  },
  "education": {
    "header": {
      "title": "Education & Resources",
      "subtitle": "Learn about programs that help pay for your medicines. Find out which ones work best for you."
    },
    "totalAssist": {
      "badge": "New · Now Live",
      "title": "TotalAssist Is Here, the Nation's Largest Charitable Patient Assistance Program",
      "p1": "<strong>TotalAssist</strong> brings decades of patient assistance experience together in one program. With more than 50 years of combined experience, its team has helped 3.8 million patients and granted over $7 billion in financial assistance.",
      "p2": "As of <strong>July 1, 2026</strong>, TotalAssist combines all financial assistance into one place, with nearly 150 disease-specific and health equity funds, no wait lists, and instant eligibility decisions in most cases. Grants help cover medication copays, coinsurance, deductibles, insurance premiums, office visit charges, and treatment-related costs. Most funds use a 500% Federal Poverty Level income limit, adjusted for your household size and where you live — so many middle-income families qualify. Depending on your diagnosis date, a six-month lookback may reimburse expenses you already paid. <strong>TotalAssist has a fund for solid organ transplant patients — ask about it when you call or apply.</strong>",
      "applyPre": "Apply online or call ",
      "phone": "866-512-3861",
      "applyPost": " (Mon–Fri, 8:30am–5:30pm ET; press 2 for Spanish, or 3 for all other languages).",
      "learnMore": "Learn more about TotalAssist"
    },
    "gotDenied": {
      "title": "Got Denied?",
      "text": "Insurance said no? Don't worry, you can fight back. Learn why denials happen and how to appeal. We have letter templates to help your doctor write on your behalf.",
      "cta": "View Appeal Guide"
    },
    "tabs": {
      "ariaLabel": "Education topics",
      "emergency": "Out of Meds?",
      "generics": "Generics vs. Brand",
      "deductibleTrap": "Deductible Trap",
      "diversion": "Alternative Funding",
      "directory": "Directory",
      "insurance": "Insurance",
      "mental": "Mental Health",
      "oop": "Out-of-Pocket Max"
    },
    "emergency": {
      "title": "Out of medication, or about to be?",
      "intro": "Anti-rejection medicine is not something you can safely skip. If cost or a refill problem means you are running low, act today. Here is how to get a bridge supply while you sort out the cost.",
      "call911": "If this is a medical emergency, call 911.",
      "call911Text": "Signs of rejection, fever, pain over the transplant, less urine, sudden weight gain, or feeling very unwell, need your transplant team right away. Do not wait. <strong>Never change or stop your doses on your own.</strong>",
      "orgEscalation": {
        "title": "Same-day help from {{name}}",
        "text": "Your transplant center set up this direct contact for urgent medication problems. They can act the same day.",
        "callPre": "Call now: "
      },
      "step1Title": "Call your transplant team first, today",
      "step1Text": "This is the most important step. Your transplant center handles this all the time. Ask for the <strong>transplant pharmacist</strong> or <strong>social worker</strong>. They can often send an emergency prescription, give you samples, or start a fast assistance request the same day. Tell them plainly: \"I am about to run out and I cannot afford the refill.\"",
      "step2Title": "Ask your pharmacy for an emergency supply",
      "step2Pre": "Some states let a pharmacist give a few days of a regular medicine in an emergency so you do not miss doses. Ask: ",
      "step2Quote": "\"Can you do an emergency fill while I sort out the cost or the refill?\"",
      "step2Post": " Bring your pill bottle or the medicine name and dose.",
      "step2Note": "Your pharmacy can confirm whether an emergency supply is allowed for your medicine under your state's rules.",
      "step3Title": "Ask the drug maker about a bridge supply",
      "step3Text": "Some manufacturers give a free short-term \"bridge\" or \"quick start\" supply while your Patient Assistance Program application is reviewed, so you are covered during the wait. Your transplant team can request this, or call the drug maker's patient support line.",
      "step3Link": "Find your medication and its manufacturer →",
      "step4Title": "Check foundations for urgent help",
      "step4Text": "Some copay foundations review urgent cases faster. It is worth a call when you are in a pinch. See the trusted groups in our directory.",
      "step4Link": "Open the resource directory →",
      "readyTitle": "Have this ready when you call",
      "readySubtitle": "It makes every call faster:",
      "readyItems": [
        "Your medicine names and doses",
        "Your pharmacy's name and phone",
        "Your insurance card (if you have one)",
        "Your transplant center's phone number"
      ],
      "dontStopTitle": "Do not just stop taking your medicine.",
      "dontStopText": "Missing anti-rejection doses can put your transplant at risk. If you truly cannot get a full supply, ask your transplant team whether a safe short-term plan is possible, but let them make that call, not you.",
      "dontStopCta": "Get help applying for longer-term assistance"
    },
    "oop": {
      "title": "Mixing Programs",
      "intro": "Most transplant patients use a mix of help programs to pay for their medicines. It's like a puzzle.",
      "negotiation": {
        "badge": "Effective January 2026",
        "title": "Medicare Drug Price Negotiation",
        "lead": "For the first time ever, Medicare talked drug makers into lower prices.",
        "p1": "The first new prices start January 1, 2026, with big savings. Examples: <strong>Eliquis</strong> drops from $521 to $231/month (56% less), and <strong>Januvia</strong> drops from $527 to $113/month (79% less). This will save Medicare patients $1.5 billion in 2026 alone.",
        "p2": "The first 10 drugs include medicines for heart failure (Entresto), diabetes (Jardiance, Farxiga, Januvia), blood clots (Eliquis, Xarelto), immune diseases (Stelara, Enbrel), blood cancers (Imbruvica), and insulin (NovoLog/Fiasp).",
        "p3": "About 8.8 million Medicare Part D patients used these medicines in 2023. A second round of 15 drugs was selected for 2027 prices.",
        "round3Title": "Third Round Announced January 27, 2026",
        "round3P1": "CMS selected 15 new drugs for negotiation, including the <strong>first-ever Part B drugs</strong>. New prices take effect January 1, 2028.",
        "round3P2": "<strong>Transplant-relevant picks:</strong> Orencia (abatacept, related to Belatacept), Trulicity (dulaglutide, for post-transplant diabetes), Xolair (omalizumab, lung transplant), Anoro Ellipta (lung transplant). Also includes Botox, Biktarvy, Cimzia, Cosentyx, Entyvio, Erleada, Kisqali, Lenvima, Rexulti, Verzenio, and Xeljanz."
      },
      "glp1": {
        "badge": "New in Mid-2026",
        "title": "Medicare GLP-1 Coverage for Weight Loss",
        "lead": "A big change: Medicare will now cover weight loss drugs.",
        "p1": "Starting mid-2026, Medicare will cover certain GLP-1 drugs for weight loss, ending a rule that kept these drugs out of Part D.",
        "p2": "<strong>Covered drugs:</strong> Wegovy and Zepbound will be covered for patients with weight-related health issues. Your copay should be about <strong>$50/month</strong> after you meet your deductible.",
        "p3": "Eli Lilly and Novo Nordisk agreed to give these drugs at lower prices for Medicare patients."
      },
      "m3p": {
        "title": "Medicare Prescription Payment Plan",
        "lead": "A big change from new laws",
        "p1": "Since 2025, Medicare Part D patients can spread out their drug costs over the year instead of paying large amounts all at once.",
        "p2": "This program makes your costs more steady and easier to manage.",
        "p3": "Ask your Part D plan about this payment plan during open enrollment."
      },
      "pick": {
        "title": "Part D vs Medicare Advantage: Picking Your Plan",
        "intro": "Knowing the differences can save you a lot of money on transplant medicines.",
        "partDTitle": "Medicare Part D (Traditional)",
        "partDDesc": "A drug plan that works with Original Medicare. It's a separate plan just for medicines.",
        "partDItems": [
          "Works with any Medicare-accepting provider",
          "Separate deductible and premium",
          "$2,100 out-of-pocket cap (2026, up from $2,000 in 2025)"
        ],
        "maTitle": "Medicare Advantage (MA-PD)",
        "maDesc": "An all-in-one plan that covers both doctor visits AND medicines.",
        "maItems": [
          "Often lower or $0 premiums",
          "Provider networks may be limited",
          "$2,100 out-of-pocket cap (2026, up from $2,000 in 2025)"
        ]
      },
      "compare": {
        "title": "How to Compare Plans for Your Medicines",
        "step1Title": "Check Drug Lists",
        "step1Text": "Use the Medicare Plan Finder tool at medicare.gov to see which plans cover your drugs and how much they cost.",
        "step1Link": "Medicare Plan Finder",
        "step1LinkAria": "Visit Medicare Plan Finder (opens in new tab)",
        "step2Title": "Compare Total Costs",
        "step2Text": "Look at monthly fees, deductibles, copays, and which pharmacies you can use. Remember the $2,100 yearly limit (2026) when you add up costs.",
        "step3Title": "Look Into Extra Help",
        "step3Text": "If you have low income, the Extra Help program can cut costs for Part D and MA-PD plans. It can remove your monthly fees and deductible ($615 in 2026) and cap copays for both generic and brand-name drugs.",
        "step3Link": "Apply for Extra Help",
        "step3LinkAria": "Apply for Extra Help (opens in new tab)",
        "step4Title": "Get Free Help",
        "step4Text": "Call your State Health Insurance Assistance Program (SHIP) for free help picking a plan.",
        "step4Link": "Find Your Local SHIP",
        "step4LinkAria": "Find Your Local SHIP (opens in new tab)"
      },
      "parts": {
        "title": "Medicare Parts for Transplant Drugs",
        "intro": "Knowing which part of Medicare pays for your transplant medicines is key.",
        "partATitle": "Part A (Hospital)",
        "partADesc": "Pays for drugs given while you're in the hospital.",
        "partAItem": "Induction agents (Thymoglobulin, Simulect)",
        "partBTitle": "Part B (Medical)",
        "partBDesc": "Pays for some drugs you get at clinics. Also covers anti-rejection drugs if you don't have Part D.",
        "partBItem": "IV infusions (Belatacept/Nulojix)",
        "partDTitle": "Part D (Prescription)",
        "partDDesc": "Pays for most of the pills you take at home each day.",
        "partDItem": "Tacrolimus, Mycophenolate, etc."
      },
      "strategies": {
        "title": "Strategies by Insurance Type",
        "commercialTitle": "Commercial Insurance?",
        "commercialItem1": "Use <strong>manufacturer copay cards</strong> to lower what you pay at the pharmacy.",
        "commercialItem2": "Use <strong>Foundations</strong> for deductibles.",
        "medicareTitle": "Medicare?",
        "medicareItem1": "Use <strong>Foundations</strong> for copays.",
        "medicareItem2": "Check <strong>Cash Prices</strong> for generics to compare savings."
      },
      "keyInsightTitle": "Key Insight",
      "keyInsightText": "Many patients mix programs, for example, a Medicare patient might get foundation grants to help with copays. Also check cash prices for generic drugs.",
      "glossary": {
        "title": "Insurance Words Made Simple",
        "intro": "Knowing what these words mean helps you handle costs and find help.",
        "premiumTerm": "Premium",
        "premiumDef": "The amount you pay each month to have insurance. Think of it like a gym membership - you pay every month whether you use it or not.",
        "deductibleTerm": "Deductible",
        "deductibleDef": "The amount you must pay yourself before your insurance starts helping. If your deductible is $500, you pay the first $500 of medicine costs. After that, insurance helps pay.",
        "copayTerm": "Copay",
        "copaySuffix": " (or Co-payment)",
        "copayDef": "A fixed amount you pay each time you get medicine. Example: You might pay $10 for each prescription, and insurance pays the rest.",
        "coinsuranceTerm": "Coinsurance",
        "coinsuranceDef": "The percentage you pay after meeting your deductible. If you have 20% coinsurance, you pay $20 for every $100 of medicine cost. Insurance pays the other $80.",
        "oopMaxPre": "Yearly Spending Limit (",
        "oopMaxTerm": "Out-of-Pocket Maximum",
        "oopMaxPost": ")",
        "oopMaxDef": "The most money you will pay in one year. Once you reach this amount, insurance pays 100% of covered medicines. This is your safety net.",
        "formularyPre": "Covered Medicines List (",
        "formularyTerm": "Formulary",
        "formularyPost": ")",
        "formularyDef": "The list of medicines your insurance will cover. Think of it as the insurance company's menu. If your medicine isn't on the list, you might pay more or need special approval.",
        "tierTerm": "Tier",
        "tierDef": "How insurance groups medicines by cost. Lower tiers (1-2) cost less. Higher tiers (3-4-5) cost more.",
        "tier1": "<strong>Tier 1:</strong> Usually generic drugs (lowest cost)",
        "tier2": "<strong>Tier 2:</strong> Preferred brand drugs",
        "tier3": "<strong>Tier 3-5:</strong> Non-preferred or specialty drugs (highest cost)",
        "paTerm": "Prior Authorization",
        "paDef": "When you need insurance company approval before they will pay for a medicine. Your doctor must explain why you need that specific medicine.",
        "paWiserTitle": "New in 2026: CMS WISeR Pilot",
        "paWiserPre": "The WISeR (Wasteful and Inappropriate Service Reduction) model requires prior authorization for 17 outpatient Medicare Part B services in 6 states: Arizona, New Jersey, Ohio, Oklahoma, Texas, and Washington. While it does not directly affect transplant medications, it may add steps for some procedures. ",
        "paWiserLink": "Learn more on our Appeals page",
        "paWiserPost": ".",
        "genericTerm": "Generic",
        "genericSuffix": " Medicine",
        "genericDef": "A medicine that works the same as a brand name but costs less. Like store-brand cereal versus name-brand - same thing, different package.",
        "brandTerm": "Brand Name Medicine",
        "brandDef": "The original version of a medicine made by the company that invented it. Usually costs more than generic.",
        "specialtyTerm": "Specialty",
        "specialtySuffix": " Medication",
        "specialtyDef": "Expensive medicines that need special handling or monitoring. Most transplant medicines are specialty medications. They often cost $600+ per month.",
        "partDTerm": "Medicare Part D",
        "partDDef": "The part of Medicare that helps pay for prescription medicines. You must sign up for this separately.",
        "oopCapTerm": "Yearly Out-of-Pocket Cap",
        "oopCapDef": "A yearly limit on what you pay out of pocket for Medicare Part D drugs. In 2026 the cap is $2,100. Once you reach it, you pay $0 for covered drugs for the rest of the year.",
        "catastrophicTerm": "Catastrophic Coverage",
        "catastrophicDef": "After you reach the yearly Part D out-of-pocket cap ($2,100 in 2026), Medicare pays 100% and you pay $0 for covered drugs for the rest of the year.",
        "inNetworkTerm": "In-Network Pharmacy",
        "inNetworkDef": "A pharmacy that works with your insurance. You pay less here. Think of it as a preferred partner.",
        "outNetworkTerm": "Out-of-Network Pharmacy",
        "outNetworkDef": "A pharmacy that doesn't have a deal with your insurance. You might pay more or get no coverage here.",
        "pbmTerm": "PBM (Pharmacy Benefit Manager)",
        "pbmDef": "The company that manages prescription drug coverage for your insurance. They decide which medicines are covered and how much they cost.",
        "papPre": "Patient Assistance Program (",
        "papTerm": "PAP",
        "papPost": ")",
        "papDef": "Free or low-cost medicine programs run by drug companies. If you can't afford your medicine, these programs might help.",
        "copayCardTerm": "Copay Card or Coupon",
        "copayCardDef": "Help from drug companies to lower what you pay for brand name medicines. <strong>Important:</strong> These often don't work with Medicare.",
        "mfrTerm": "Manufacturer Assistance",
        "mfrDef": "Help directly from the company that makes your medicine. This can be free medicine, copay help, or discounts.",
        "mailTerm": "Mail Order Pharmacy",
        "mailDef": "A pharmacy that ships medicine to your home. Usually gives you 90 days of medicine at once. Often costs less than getting 30 days at a local pharmacy.",
        "stepTerm": "Step Therapy",
        "stepDef": "When insurance requires you to try a cheaper medicine first before they'll pay for a more expensive one. Like having to try the basic version before getting the premium version.",
        "exclusionTerm": "Exclusion",
        "exclusionDef": "A medicine that insurance will not cover at all. You must pay the full price yourself or find other help.",
        "whyTitle": "Why This Matters for Transplant Patients",
        "whyIntro": "Your anti-rejection medicines are specialty medications. They are expensive. Understanding these words helps you:",
        "whyItems": [
          "Know what you'll pay",
          "Find help when costs are too high",
          "Talk to your transplant team about options",
          "Apply for assistance programs"
        ],
        "askTitle": "Questions to Ask",
        "askItems": [
          "What tier are my transplant medicines?",
          "How much is my deductible?",
          "When do I reach my yearly spending limit?",
          "Are my medicines on the covered medicines list?",
          "Can I use mail order to save money?"
        ]
      }
    },
    "directory": {
      "title": "Trusted Resource Directory",
      "visitAria": "Visit {{name}} (opens in new tab)",
      "emptyTitle": "No resources available",
      "emptyText": "We're working on adding trusted resources. Check back soon for helpful links and organizations.",
      "statusPaused": "Applications are temporarily paused",
        "statusPausedNote": "This foundation is not taking new applications right now. Check back, or ask your transplant social worker about other grants.",
        "categories": {
        "Foundation": "Foundation",
        "Low-Cost Pharmacy": "Low-Cost Pharmacy",
        "Support Group": "Support Group",
        "Medication Database": "Medication Database",
        "Discount Program": "Discount Program",
        "Government": "Government",
        "Caregiver Support": "Caregiver Support",
        "Foundation Directory": "Foundation Directory",
        "Search Engine": "Search Engine"
      }
    },
    "insurance": {
      "wiser": {
        "title": "New in 2026: CMS WISeR Prior Authorization Pilot",
        "intro": "Starting January 2026, CMS launched the <strong>WISeR (Wasteful and Inappropriate Service Reduction)</strong> model. This adds prior authorization for 17 outpatient Medicare Part B services in 6 states.",
        "statesTitle": "Affected States",
        "stateAz": "Arizona",
        "stateNj": "New Jersey",
        "stateOh": "Ohio",
        "stateOk": "Oklahoma",
        "stateTx": "Texas",
        "stateWa": "Washington",
        "knowTitle": "What Transplant Patients Should Know",
        "know1": "WISeR covers outpatient procedures (nerve stimulators, spinal procedures, wound care), <strong>not</strong> transplant medications",
        "know2": "Your immunosuppressant drugs are NOT affected",
        "know3": "If you need any of the 17 listed procedures (e.g., wound care with skin substitutes), your provider must get prior authorization",
        "know4": "Coverage decisions are made within 72 hours (48 hours for urgent cases)",
        "footer": "The WISeR model runs from January 2026 through December 2031. It applies only to Original Medicare (fee-for-service), not Medicare Advantage plans. If you are in one of these states and need a covered procedure, talk to your provider about the prior authorization process."
      },
      "note": "Each type of insurance has its own benefits, costs, and best ways to save.",
      "infographicAlt": "Comparison chart of transplant medication help by insurance type: Commercial, Medicare, Medicaid, and Uninsured",
      "infographicDesc": "This infographic compares what transplant medication help is available across four insurance types. Commercial Insurance: Insurance through your job or that you buy on your own. You may qualify for manufacturer copay cards, patient assistance programs, and copay foundations. You pay monthly premiums, copays, and deductibles, then insurance covers the rest. Common problems include high deductibles, prior authorization requirements, and copay accumulators. Next step: search your medications to find copay cards and assistance programs. Medicare: Government insurance for people 65 and older or with disabilities. You may qualify for patient assistance programs, Extra Help or Low Income Subsidy, and copay foundations. Part D covers most transplant drugs with monthly premiums and copays. Common problems include Part D costs and you cannot use manufacturer copay cards. Next step: check if you qualify for Extra Help and search for patient assistance programs. Medicaid: Government insurance for people with low income. You may qualify for patient assistance programs and state-specific programs. Covers most transplant drugs with very low or no copays. Common problems include limited pharmacy networks, formulary restrictions, and coverage ending if income changes. Next step: search your medications and check with your state Medicaid office. Uninsured or Self-Pay: No current insurance coverage. You may qualify for manufacturer patient assistance programs for free medications, state programs, and discount cards. You pay the full retail price without help. Common problems include very high costs, no negotiated rates, and difficulty affording specialty medications. Next step: apply for patient assistance programs immediately as most offer free medications for qualifying patients. The infographic concludes with an encouraging message: You are not alone and there is help available no matter what type of insurance you have.",
      "medicareGuide": {
        "title": "Medicare Guide",
        "partsTitle": "Medicare Parts for Transplant Drugs",
        "partATitle": "Part A (Hospital)",
        "partAItems": [
          "Induction agents",
          "(Thymoglobulin, Simulect)"
        ],
        "partBTitle": "Part B (Medical)",
        "partBItems": [
          "IV infusions (Belatacept)",
          "Anti-rejection drugs (if Part B-ID eligible)",
          "2026: $202.90/month premium, $283 deductible",
          "Part B-ID premium: $121.60/month (kidney patients)"
        ],
        "partCTitle": "Part C (Medicare Advantage)",
        "partCItems": [
          "Combines A, B, and usually D",
          "May have extra benefits",
          "You must use certain doctors"
        ],
        "partDTitle": "Part D (Prescription)",
        "partDItems": [
          "Tacrolimus",
          "Mycophenolate",
          "Valcyte, etc."
        ],
        "chooseTitle": "Part D vs Medicare Advantage: Choosing Your Plan",
        "tradTitle": "Part D (Traditional)",
        "tradItems": [
          "Works with any Medicare-accepting provider",
          "Separate deductible and premium",
          "$2,100 out-of-pocket cap (2026)"
        ],
        "maTitle": "Medicare Advantage (MA-PD)",
        "maItems": [
          "Often lower or $0 premiums",
          "Provider networks may be limited",
          "$2,100 out-of-pocket cap (2026)"
        ]
      },
      "commercial": {
        "title": "Commercial Insurance",
        "intro": "Your main payer for the first 30 months after transplant.",
        "keyPointsTitle": "Key Points",
        "keyPoints": [
          "Can use most drug maker PAPs",
          "Copay cards often work",
          "Foundations help with high copays",
          "May need to use a certain pharmacy"
        ],
        "strategyTitle": "Best Strategy",
        "strategy": "Start with drug maker PAPs, then use foundations for any copays or deductibles left over."
      },
      "va": {
        "title": "VA Health Care",
        "intro": "For veterans who qualify.",
        "strategyTitle": "Best Strategy",
        "strategy": "Priority Groups 1–6: $0 for medicines. Use the VA pharmacy first. Work with your VA transplant team.",
        "link": "VA Prescription Information",
        "linkAria": "Visit VA Prescription Information (opens in new tab)"
      },
      "tricare": {
        "title": "TRICARE",
        "intro": "For military members and families.",
        "strategyTitle": "Best Strategy",
        "strategy": "Use the military pharmacy ($0 copay). Use TRICARE mail order for ongoing medicines. Check the drug list search tool.",
        "link": "TRICARE Drug List Search",
        "linkAria": "Visit TRICARE Formulary Search (opens in new tab)"
      },
      "noInsurance": {
        "title": "No Insurance",
        "intro": "For patients without insurance.",
        "strategyTitle": "Best Strategy",
        "items": [
          "Start with drug maker PAPs",
          "Check Cost Plus Drugs prices",
          "Use discount tools (GoodRx, SingleCare, etc.)",
          "Look into Medicaid or Marketplace plans"
        ],
        "link": "Find Insurance Options",
        "linkAria": "Explore Insurance Options on Healthcare.gov (opens in new tab)"
      },
      "medicaid": {
        "title": "Medicaid (State-Based)",
        "intro": "A program for people with low income. What it covers depends on your state.",
        "selectLabel": "Select your State:",
        "selectPlaceholder": "-- Choose a State --",
        "go": "Go to Official Site",
        "goAria": "Visit your state's Medicaid website (opens in new tab)",
        "leaving": "You are leaving this app to visit a government website."
      },
      "ihs": {
        "title": "Indian Health Service / Tribal Programs",
        "intro": "For American Indian and Alaska Native patients who qualify.",
        "findTitle": "Find a Facility",
        "findText": "Use the official IHS locator to find clinics and pharmacies near you.",
        "findCta": "Open IHS Locator",
        "findAria": "Find an IHS facility (opens in new tab)",
        "strategyTitle": "Best Strategy",
        "strategy": "Use your local IHS or Urban Indian Program, usually $0 cost."
      }
    },
    "deductibleTrap": {
      "title": "The Deductible Trap",
      "subtitle": "Why Prescription Discount Cards Can Cost Transplant Patients More",
      "warning": {
        "title": "⚠️ Important Warning for Transplant Patients",
        "lead": "Using discount cards like GoodRx or SingleCare can cost you THOUSANDS of extra dollars each year.",
        "boxTitle": "Watch out for the \"deductible trap.\" Here's what happens:",
        "boxP1Pre": "If you use a discount card, ",
        "boxP1Highlight": "the money you pay does NOT count toward your deductible or your yearly out-of-pocket limit",
        "boxP1Post": ".",
        "boxP2": "So insurance never starts paying in full. You keep paying out of pocket — for these medicines and for the rest of your care — all year long.",
        "boxP3": "What to do: Call the customer service number on your insurance card and ask: 'Have I met my deductible and my yearly out-of-pocket limit? Is it better for me to use insurance or a discount card for this medication?'",
        "trumpRxBold": "This applies to TrumpRx.gov too.",
        "trumpRxText": "TrumpRx prices are cash prices outside your insurance — that spending does not count toward your deductible or out-of-pocket maximum either, and many TrumpRx coupons cannot be combined with insurance at all. Cash price is cash price, whoever is offering it.",
        "trumpRxLink": "Read our TrumpRx guide for transplant patients →",
        "trumpRxHref": "/trumprx"
      },
      "example": {
        "title": "Real-World Example",
        "rightBadge": "✅ The Right Way",
        "rightTitle": "Using Insurance",
        "rightM13Label": "Month 1-3:",
        "rightM13": "Pay $2,100 out-of-pocket. Every dollar counts toward your deductible and your yearly out-of-pocket limit — you reach the limit.",
        "rightM412Label": "Month 4-12:",
        "rightM412": "Insurance pays 100% of your medicines, labs, and clinic visits = $0 out-of-pocket",
        "rightTotal": "Total Annual Cost: $2,100",
        "trapBadge": "❌ The Discount Card Trap",
        "trapTitle": "Using GoodRx/SingleCare",
        "trapM112Label": "Month 1-12:",
        "trapM112": "Pay the discount price (~$200/month = $2,400), but NONE of it counts toward your deductible or yearly limit",
        "trapResultLabel": "Result:",
        "trapResult": "You never reach your yearly limit, so insurance never starts paying in full.",
        "trapRestLabel": "The rest:",
        "trapRest": "You keep paying out of pocket for labs, clinic visits, and your other medicines all year — often $3,000–$8,000 more.",
        "trapTotal": "Total Annual Cost: $2,400 + often $3,000–$8,000 more for the rest of your care",
        "takeaway": "The discount price looks like a small saving each month. But nothing counts toward your yearly limit, so insurance never starts paying in full — and that is how the trap can add up to thousands over a year."
      },
      "whenToUse": {
        "title": "When Should Transplant Patients Use Discount Cards?",
        "s1Title": "Scenario 1: Drug NOT Covered by Insurance",
        "s1Text": "For a medicine NOT on your insurance's list and no cheaper generic option is available.",
        "s2Title": "Scenario 2: One-Time, Low-Cost Medications",
        "s2Text": "For short-term medicines not related to your transplant (like a round of antibiotics) where the discount price is less than your copay.",
        "s3Title": "Scenario 3: Extremely High Deductible",
        "s3Text": "If you have a very high deductible AND the cash price is much lower, but even then, think hard about the cost of not reaching your yearly limit.",
        "neverTitle": "🛑 NEVER Use Discount Cards For:",
        "neverP1": "Your daily anti-rejection medicines like Tacrolimus, Mycophenolate, or Cyclosporine.",
        "neverP2": "These are the medicines that will help you reach your yearly limit fast."
      },
      "alternatives": {
        "title": "Better Alternatives",
        "papsTitle": "Patient Assistance Programs (PAPs)",
        "papsText": "Free or low-cost medicines straight from drug makers.",
        "foundationsTitle": "Co-pay Foundations",
        "foundationsText": "Groups that help pay for your copays and deductibles.",
        "copayTitle": "Drug Maker Copay Cards",
        "copayText": "Help from drug makers for brand-name medicines (not for Medicare patients).",
        "counts": "✅ Counts toward your yearly limit",
        "countsCopay": "⚠️ May not count toward your yearly limit",
        "countsCopayAsk": "Many plans use \"copay accumulators\" that keep the card's money from counting. Before you use one, ask your plan: \"Will my copay card count toward what I owe?\""
      },
      "takeaways": {
        "title": "Key Takeaways",
        "t1Bold": "Your goal every year is to hit your yearly spending limit (the most you pay each year).",
        "t1Rest": "Once you reach it, insurance pays 100% for the rest of the year.",
        "t2Bold": "Discount cards stop your payments from counting toward this goal.",
        "t2Rest": "They may save you a few dollars per medicine, but cost you thousands over the year.",
        "t3Bold": "Always use PAPs and co-pay foundations first.",
        "t3Rest": "These programs help you pay for medicines AND help you reach your yearly limit faster.",
        "t4Bold": "Talk to your transplant team.",
        "t4Rest": "They can connect you with people who know these programs and can help you through the system."
      },
      "questionsTitle": "Questions?",
      "questionsText": "Not sure if a discount card is right for you? Ask your transplant center's financial coordinator (the person who helps with money questions) or social worker. They can look at your insurance plan and help you choose."
    },
    "diversion": {
      "title": "When Insurance Sends You Elsewhere",
      "subtitle": "\"Alternative funding programs\" are companies your insurance plan hires to push your costly drugs onto charity and free-drug programs — so the plan avoids paying. Learn how to spot this and what to do.",
      "warning": {
        "title": "Warning: Your Plan May Send You Away",
        "lead": "Some plans don't want to pay for costly drugs. They may make you get free drugs from drug makers first.",
        "boxTitle": "What Does This Mean?",
        "boxP1Pre": "Your plan may say: ",
        "boxP1Highlight": "\"We won't pay for your drug until you ask a drug maker for free medicine first.\"",
        "boxP2": "This saves money for your plan. But it can leave you without medicine while you wait. And you might not get the free drugs."
      },
      "types": {
        "title": "Three Ways Plans Do This",
        "intro": "These tricks have fancy names. But they all do the same thing: make someone else pay for your drugs.",
        "whyHurts": "Why This Hurts:",
        "acc": {
          "title": "Copay Accumulator",
          "pre": "Drug makers give you copay help. Your plan takes that help. But ",
          "highlight": "it does not count toward what you owe for the year",
          "post": ".",
          "why": [
            "Your copay card runs out faster",
            "When it runs out, you owe the full price",
            "You keep paying all year long"
          ]
        },
        "max": {
          "title": "Copay Maximizer",
          "pre": "Your plan spreads your copay card over the year. But ",
          "highlight": "the plan keeps the savings, not you",
          "post": ".",
          "why": [
            "The drug maker's help goes to your plan, not you",
            "You still pay more than you should",
            "When the help runs out, you owe a lot"
          ]
        },
        "afp": {
          "title": "Alternative Funding (AFP)",
          "pre": "Your work plan says: ",
          "highlight": "\"We won't cover this drug. Go get it free from the drug maker.\"",
          "why": [
            "You wait weeks or months for your drugs",
            "You might not get the free drugs",
            "If you don't get free drugs, you pay full price",
            "Your plan saves money. You take the risk."
          ]
        }
      },
      "identify": {
        "title": "How to Tell If This Is Happening to You",
        "signsTitle": "Warning Signs:",
        "signs": [
          "Your plan papers say \"copay accumulator\"",
          "Copay card money is not showing on your plan tracker",
          "They tell you to call another company first",
          "Your drug is \"carved out\" (not covered)",
          "You must ask for free drugs before your plan will help"
        ],
        "askTitle": "Ask Your Plan These Questions:",
        "questions": [
          "\"Do you use a copay accumulator?\"",
          "\"Will my copay card count toward what I owe?\"",
          "\"Is my drug fully covered?\"",
          "\"Do I have to ask for free drugs first?\""
        ]
      },
      "protect": {
        "title": "How to Protect Yourself",
        "s1Title": "Read Your Plan Papers",
        "s1Text": "Look for words like \"accumulator,\" \"maximizer,\" or \"alternative funding.\" These are clues.",
        "s2Title": "Keep Track of What You Pay",
        "s2Text": "Save your receipts. Check if your copay card money shows up on your plan's tracker. If not, you may have an accumulator plan.",
        "s3Title": "Appeal If They Say No",
        "s3Text": "If your plan won't cover your drug, you can fight back. Ask your transplant team to write a letter saying you need this drug.",
        "s4Title": "Call Your State for Help",
        "s4Text": "Many states have laws to protect you. Call your state insurance office. They can tell you your rights and help you file a complaint.",
        "s5Title": "Talk to Your Boss (If You Get Insurance From Work)",
        "s5Text": "Your boss picks the plan. Tell HR how this hurts you. Ask them to pick a better plan."
      },
      "stateLaws": {
        "title": "States That Protect You",
        "intro": "Some states made laws to stop these tricks. In these states, copay card money must count toward what you owe.",
        "listTitle": "States With Laws to Help (as of 2026):",
        "list": "Arizona, Arkansas, California, Connecticut, Delaware, Georgia, Illinois, Kentucky, Louisiana, Maine, Maryland, Minnesota, Missouri, New Jersey, New York, North Carolina, Ohio, Oklahoma, Oregon, Puerto Rico, Tennessee, Texas, Virginia, Washington, West Virginia, and more.",
        "note": "Call your state insurance office to learn about new laws. More states add protections every year."
      },
      "whyMatters": {
        "title": "Why Transplant Patients Must Know This",
        "t1Bold": "You can't skip your drugs.",
        "t1Rest": "Missing doses can hurt your transplant. Waiting weeks for free drugs is risky.",
        "t2Bold": "Free drugs are not a sure thing.",
        "t2Rest": "Drug makers have rules. You might not qualify. The money might run out.",
        "t3Bold": "You pay for your plan.",
        "t3Rest": "You pay every month. But some plans make drug makers pay for your drugs instead.",
        "t4Bold": "Big bills can hit fast.",
        "t4Rest": "When your copay card runs out, you may owe a lot of money all at once."
      },
      "help": {
        "title": "Need Help?",
        "text": "Think this is happening to you? Talk to your transplant center's financial coordinator (the person who helps with money questions) or social worker. They know how to help.",
        "pafPre": "You can also call ",
        "pafLink": "TotalAssist",
        "pafPost": " for free help."
      }
    },
    "mental": {
      "title": "Mental Health Resources",
      "subtitle": "Your mental health matters. Get free, private support when you need it.",
      "crisis": {
        "title": "Need to Talk to Someone Right Now?",
        "name": "National Suicide & Crisis Lifeline",
        "availability": "24/7 • Free • Private",
        "callTitle": "Call or Text",
        "callText": "Dial or text <strong>988</strong> from any phone (Spanish-language support: press 2 / oprima el 2)",
        "chatTitle": "Online Chat (also available in Spanish)"
      },
      "samhsa": {
        "aria": "Visit SAMHSA (opens in new tab)",
        "title": "SAMHSA",
        "badge": "Government Resource",
        "desc": "<strong>Substance Abuse and Mental Health Services</strong> - Find treatment centers, support groups, and mental health help near you.",
        "availability": "Help finding treatment any time (24/7)"
      },
      "helplineLabel": "Helpline:",
      "transplant": {
        "title": "Transplant & Mental Health",
        "intro": "It's normal to feel worried, sad, or stressed during your transplant journey. You're not alone.",
        "items": [
          "Ask your transplant team about counseling services",
          "Many transplant centers have social workers and mental health experts",
          "Medicare and most insurance plans cover mental health services"
        ]
      },
      "nami": {
        "aria": "Visit NAMI Support Groups (opens in new tab)",
        "title": "NAMI Support Groups",
        "desc": "NAMI offers free support groups for people with mental health issues and their families."
      },
      "findTreatment": {
        "aria": "Visit FindTreatment.gov (opens in new tab)",
        "desc": "A government site to help you learn about mental health, find treatment, and find services near you."
      },
      "caregivers": {
        "title": "Support for Caregivers & Care Partners",
        "intro": "You are essential to the transplant journey. Your well-being matters too. Caring for a loved one can feel rewarding but also physically and emotionally demanding. Take care of yourself so you can keep providing the best support.",
        "tipsTitle": "Caregiver Self-Care Tips",
        "tips": [
          "Set boundaries and ask for help when you need it",
          "Take breaks and maintain your own health appointments",
          "Connect with other caregivers who understand your experience",
          "Consider counseling or support groups for caregivers"
        ],
        "nacAria": "Visit National Alliance for Caregiving (opens in new tab)",
        "nacTitle": "National Alliance for Caregiving",
        "nacDesc": "A national nonprofit coalition dedicated to advancing family caregiving through research, innovation, and advocacy. Find resources, research, and support for your caregiving journey.",
        "nacBadge": "Caregiver Resource"
      },
      "remember": {
        "title": "Remember",
        "text": "Seeking help for mental health is a sign of strength, not weakness. The transplant journey is physically and emotionally demanding. Taking care of your mental health is just as important as taking your medications. If you're struggling, reach out, there are people who want to help."
      }
    },
    "simulator": {
      "title": "Insurance Change Simulator",
      "subtitle": "See how your medication options change when switching insurance",
      "currentLabel": "Current Insurance",
      "futureLabel": "Future Insurance",
      "selectCurrent": "-- Select current --",
      "selectFuture": "-- Select future --",
      "cta": "See What Changes",
      "recommendedAction": "📋 Recommended Action",
      "comingSoon": "Detailed transition info for this combination coming soon. Contact your transplant coordinator for guidance.",
      "selectDifferent": "Please select different insurance types to see the transition changes.",
      "insuranceTypes": {
        "commercial": "Commercial/Employer Insurance",
        "medicare": "Medicare (Part D)",
        "medicaid": "Medicaid",
        "marketplace": "ACA Marketplace",
        "uninsured": "Uninsured"
      },
      "transitions": {
        "commercial_medicare": {
          "title": "Commercial → Medicare",
          "timing": "Common at 65 or after 24 months on SSDI",
          "changes": [
            {
              "title": "Copay Cards",
              "desc": "Most manufacturer copay cards are NOT allowed with Medicare. This is a major change."
            },
            {
              "title": "PAP Access",
              "desc": "You may now qualify for Patient Assistance Programs if your income is low enough (many have higher limits for Medicare patients)."
            },
            {
              "title": "Foundation Help",
              "desc": "Foundations like PAN, PANF, and HealthWell CAN help Medicare patients with copays."
            },
            {
              "title": "Part B-ID Option",
              "desc": "Kidney transplant patients may qualify for Part B coverage of immunosuppressants (Part B-ID). Premium: $121.60/month in 2026 (up from $110.40 in 2025), plus 20% coinsurance after $283 deductible."
            },
            {
              "title": "2026 Cap",
              "desc": "Annual out-of-pocket cap on Part D drugs: $2,100 in 2026 (up from $2,000 in 2025)."
            }
          ],
          "action": "Apply to manufacturer PAPs 3 months before your Medicare start date. Register with foundations early as funds run out."
        },
        "commercial_medicaid": {
          "title": "Commercial → Medicaid",
          "timing": "Due to income change or disability",
          "changes": [
            {
              "title": "Copay Cards",
              "desc": "Copay cards typically cannot be used with Medicaid."
            },
            {
              "title": "Low/No Cost",
              "desc": "Medicaid typically has $0-$3 copays for prescriptions."
            },
            {
              "title": "PAP Access",
              "desc": "Some PAPs still available as secondary assistance."
            },
            {
              "title": "Covered Medicines",
              "desc": "May need to switch to Medicaid-preferred medications or get prior authorization."
            }
          ],
          "action": "Work with your transplant coordinator to ensure your medications are on the Medicaid covered medicines list before switching."
        },
        "medicaid_medicare": {
          "title": "Medicaid → Medicare (Dual Eligible)",
          "timing": "At age 65 or qualifying for Medicare",
          "changes": [
            {
              "title": "Dual Eligible Status",
              "desc": "You may qualify as \"dual eligible\" and keep some Medicaid benefits alongside Medicare."
            },
            {
              "title": "Extra Help/LIS",
              "desc": "Likely qualify for Low Income Subsidy (Extra Help) which covers most Part D costs."
            },
            {
              "title": "Primary Payer",
              "desc": "Medicare becomes primary, Medicaid secondary for covered services."
            },
            {
              "title": "MSP Programs",
              "desc": "Medicare Savings Programs can pay your Part B premium ($202.90/month in 2026)."
            }
          ],
          "action": "Contact your State Health Insurance Assistance Program (SHIP) for free counseling on dual eligible benefits."
        },
        "commercial_marketplace": {
          "title": "Commercial → ACA Marketplace",
          "timing": "Job loss, self-employment, or retirement before 65",
          "changes": [
            {
              "title": "Copay Cards OK",
              "desc": "Manufacturer copay cards CAN be used with Marketplace plans."
            },
            {
              "title": "Subsidies Available",
              "desc": "Premium tax credits based on income can significantly reduce costs."
            },
            {
              "title": "Network Changes",
              "desc": "Check if your transplant center and pharmacy are in-network before enrolling."
            },
            {
              "title": "CSR Plans",
              "desc": "If income <250% FPL, Silver plans offer reduced deductibles and copays."
            }
          ],
          "action": "Use healthcare.gov to compare plans. Filter by your medications and doctors to find the best fit."
        },
        "uninsured_medicare": {
          "title": "Uninsured → Medicare",
          "timing": "Turning 65 or qualifying through disability",
          "changes": [
            {
              "title": "Drug Coverage",
              "desc": "Part D provides prescription coverage with $2,100 out-of-pocket cap (2026)."
            },
            {
              "title": "PAPs + Foundations",
              "desc": "Full access to Patient Assistance Programs and foundation copay help."
            },
            {
              "title": "Discount Cards",
              "desc": "GoodRx/SingleCare typically not usable once you have Part D coverage."
            },
            {
              "title": "Part B-ID",
              "desc": "Kidney patients: Part B-ID covers immunosuppressants even without Part A/B. Premium: $121.60/month (2026)."
            }
          ],
          "action": "Enroll during your Initial Enrollment Period. Apply for Extra Help (LIS) if income-limited."
        },
        "uninsured_medicaid": {
          "title": "Uninsured → Medicaid",
          "timing": "Income qualifies (varies by state)",
          "changes": [
            {
              "title": "Coverage",
              "desc": "Comprehensive coverage including prescriptions, often $0-$3 copays."
            },
            {
              "title": "Retroactive",
              "desc": "Coverage goes back up to 3 months before you apply."
            },
            {
              "title": "Covered Medicines",
              "desc": "Must use Medicaid covered medicines list - may need prior approval for some transplant drugs."
            }
          ],
          "action": "Apply at your state Medicaid office or healthcare.gov. Bring proof of income and transplant papers."
        },
        "uninsured_marketplace": {
          "title": "Uninsured → ACA Marketplace",
          "timing": "Open enrollment or qualifying life event",
          "changes": [
            {
              "title": "Subsidies",
              "desc": "Help paying for insurance based on income - may get $0 premium plans."
            },
            {
              "title": "Copay Cards",
              "desc": "Can use drug company help with what you owe on Marketplace plans."
            },
            {
              "title": "Pre-existing OK",
              "desc": "Cannot be denied or charged more for transplant history."
            },
            {
              "title": "Plan Selection",
              "desc": "Silver plans best value if income 100-250% FPL due to cost-sharing reductions."
            }
          ],
          "action": "Apply at healthcare.gov during Open Enrollment (Nov 1 - Jan 15) or after a qualifying event."
        }
      }
    },
    "coverage101": {
      "logoAlt": "Transplant Medication Navigator",
      "eyebrow": "Coverage 101",
      "title": "How you pay for transplant medications.",
      "sub": "The four most common ways patients are covered, what to know, what to watch for, what to do next.",
      "whoQualifies": "Who qualifies",
      "howItWorks": "How it works",
      "watchFor": "Watch for",
      "nextSteps": "Next steps",
      "footnote": "Educational information only. Eligibility rules and coverage details change, verify with your insurer, state Medicaid office, and transplant team.",
      "commercial": {
        "eyebrow": "Commercial",
        "name": "Insurance from your job or that you bought yourself.",
        "tag": "Through an employer, the Marketplace, or a spouse/parent's plan.",
        "who": [
          "Employees of companies that offer benefits",
          "Self-purchased plans through Healthcare.gov",
          "Dependents on a family member's plan"
        ],
        "how": "You pay a monthly premium. Insurance starts paying more after you hit your <strong>deductible</strong>. Copays and coinsurance apply at the pharmacy until you reach your <strong>out-of-pocket max</strong>.",
        "warn": "High out-of-pocket before you hit your deductible. Narrow networks. Prior-authorization delays.",
        "steps": [
          "Check your plan's formulary for transplant medications.",
          "Know your deductible and out-of-pocket max.",
          "Confirm your transplant center is in-network."
        ]
      },
      "medicare": {
        "eyebrow": "Medicare",
        "name": "Federal insurance for 65+ and certain disabilities.",
        "tag": "Includes End-Stage Renal Disease and qualifying long-term disability.",
        "who": [
          "Adults 65+",
          "People with qualifying disabilities (24+ months on SSDI)",
          "Anyone with End-Stage Renal Disease"
        ],
        "how": "<strong>Part A</strong> covers hospital stays. <strong>Part B</strong> covers outpatient care, and your transplant anti-rejection drugs when Medicare helped pay for the transplant. <strong>Part D</strong> covers most other prescription drugs. Many enroll in <strong>Medicare Advantage</strong> for bundled coverage.",
        "warn": "Premiums for Part B and Part D add up. <strong>Copay cards are not allowed</strong> with Medicare.",
        "steps": [
          "Enroll in Medicare Part A, B, and D (or Advantage).",
          "Apply for Extra Help if your income is limited.",
          "Review your Part D drug formulary every fall.",
          "Kidney transplant? Ask Social Security about Part B-ID before your Medicare ends."
        ]
      },
      "medicaid": {
        "eyebrow": "Medicaid",
        "name": "State-and-federal coverage for limited income.",
        "tag": "Eligibility, benefits, and rules vary by state.",
        "who": [
          "Households below your state's income limit",
          "Children, pregnant patients, and people with disabilities",
          "Some long-term care and waiver recipients"
        ],
        "how": "The state pays for most covered services with low or zero out-of-pocket cost. Each state runs its own program, your benefits, prescription rules, and provider list depend on where you live.",
        "warn": "Income and asset limits apply. Exceed them and you can lose coverage. Provider networks may be limited. <strong>Copay cards are not allowed</strong>.",
        "steps": [
          "Contact your state Medicaid office to apply.",
          "Gather income, residency, and household documents.",
          "Re-verify eligibility on the renewal date."
        ]
      },
      "selfpay": {
        "eyebrow": "Uninsured / Self-Pay",
        "name": "Paying full cost yourself, with no insurance.",
        "tag": "Between jobs, pre-Medicare, or otherwise without coverage.",
        "who": [
          "People between jobs or who lost coverage",
          "People waiting for Medicare or Medicaid to start",
          "Anyone without insurance right now"
        ],
        "how": "You receive the full retail bill. Cash prices for transplant medications can run hundreds to thousands per month. Hospitals and pharmacies often negotiate or offer payment plans on request.",
        "warn": "Catastrophic out-of-pocket costs. Risk of medical debt. Skipped doses if cost feels impossible.",
        "steps": [
          "Apply for manufacturer Patient Assistance Programs first.",
          "Negotiate cash prices and ask about payment plans.",
          "Re-check Medicaid and Marketplace plan eligibility."
        ]
      }
    },
    "generics": {
      "eyebrow": "Medication Safety",
      "title": "Generics vs. Brand-Name Medicines",
      "intro": "Generics can save you a lot of money, and for most medicines they are a smart, safe choice. For transplant patients, there is just one important rule that keeps them safe. Here is what to know.",
      "real": {
        "title": "Generics are real medicine",
        "li1": "• A generic has the <strong>same active ingredient</strong> as the brand name.",
        "li2": "• The FDA approves it and requires it to work the same way.",
        "li3": "• It usually costs a lot less.",
        "li4": "• For most medicines, choosing the generic is a safe way to save money."
      },
      "extraCare": {
        "title": "Transplant drugs need extra care",
        "p1": "Anti-rejection drugs like <strong>tacrolimus (Prograf)</strong>, <strong>cyclosporine</strong>,<strong> sirolimus (Rapamune)</strong>, and <strong>everolimus</strong> are \"narrow therapeutic index\" drugs. That means even a small change in how much reaches your blood matters:",
        "li1": "• <strong>Too little</strong> → your body could reject the organ.",
        "li2": "• <strong>Too much</strong> → more side effects and toxicity.",
        "p2": "Different makers can be absorbed a little differently. So the real risk is not the generic itself. The risk is <strong>switching</strong> (brand to generic, or one generic maker to another)."
      },
      "safeWay": {
        "title": "The safe way to save with a generic",
        "li1": "Pick one version and stay on it. A generic is fine. Just keep the <strong>same manufacturer</strong> every time you refill.",
        "li2": "Ask your pharmacy to always give you the same maker, and to call you <strong>before</strong> any switch.",
        "li3": "Ask your doctor to write the maker on your prescription (or <strong>\"Do Not Substitute\"</strong> if your team wants brand-name).",
        "li4": "After <strong>any</strong> change, ask for a blood test within a week.",
        "li5": "Always tell your transplant team <strong>before</strong> you change anything."
      },
      "brand": {
        "title": "When to ask for brand-name",
        "intro": "You may want to ask for brand-name if:",
        "li1": "• Your drug levels have been steady on it.",
        "li2": "• You had problems after a past switch.",
        "li3": "• Your transplant center requires it.",
        "appealLink": "If insurance says no, learn how to appeal"
      },
      "bottomLine": {
        "title": "The bottom line",
        "text": "Generics can save you money <strong>and</strong> be safe. The key is staying consistent: use the same version every time, and keep your transplant team in the loop.<strong> Never switch on your own just to save money.</strong>"
      },
      "footer": "Have a question about a switch? Call your transplant team before your next refill."
    }
  },
  "faq": {
    "cob": {
      "results": {
        "medicare_employer_working": {
          "title": "Your Job Insurance Pays First",
          "primary": "Your employer insurance",
          "secondary": "Medicare",
          "summary": "Good news! Since you or your spouse still works, your job insurance pays first. This means you CAN use copay cards to lower your costs.",
          "tips": [
            "Show your job insurance card at the pharmacy first",
            "You can use copay cards from drug makers",
            "Keep your Medicare card as backup"
          ]
        },
        "medicare_retiree": {
          "title": "Medicare Pays First",
          "primary": "Medicare",
          "secondary": "Your retiree plan",
          "summary": "Since you retired, Medicare now pays first. You cannot use copay cards with Medicare. But you can still get help from Patient Assistance Programs.",
          "tips": [
            "Apply for Patient Assistance Programs (PAPs) for free meds",
            "Check if foundations can help with your costs",
            "Your retiree plan helps pay what Medicare does not"
          ]
        },
        "medicare_medicaid": {
          "title": "You Have Great Coverage",
          "primary": "Medicare + Medicaid",
          "secondary": "Both work together",
          "summary": "You have dual coverage. This usually means very low or no costs for your medicines. Medicaid helps pay what Medicare does not cover.",
          "tips": [
            "Your copays should be very low or $0",
            "Make sure both cards are on file at your pharmacy",
            "Ask about \"Extra Help\" to lower costs even more"
          ]
        },
        "esrd_employer": {
          "title": "Special Rules for Kidney Patients",
          "primary": "Your employer insurance (first 30 months)",
          "secondary": "Medicare",
          "summary": "Kidney patients have special rules. For the first 30 months, your job insurance pays first. After 30 months, Medicare pays first and you cannot use copay cards.",
          "tips": [
            "Use copay cards now while your job insurance pays first",
            "Mark your calendar for when 30 months ends",
            "Plan ahead: apply for PAPs before Medicare takes over"
          ]
        },
        "not_sure": {
          "title": "Let's Figure This Out",
          "primary": "We need more info",
          "secondary": "",
          "summary": "Insurance rules can be tricky. The best way to know for sure is to call both insurance companies and ask: \"Which plan pays first?\"",
          "tips": [
            "Call Medicare: 1-800-MEDICARE",
            "Call your other insurance company",
            "Ask your transplant social worker for help"
          ]
        }
      },
      "start": {
        "title": "Do You Have Two Insurance Plans?",
        "subtitle": "Many transplant patients have more than one insurance. This quiz helps you know which one pays first.",
        "whyTitle": "Why does this matter?",
        "whyText": "Which insurance pays first changes what help you can get. Some savings programs only work with certain insurance types.",
        "reasonsTitle": "Common reasons transplant patients have two plans:",
        "reasons": {
          "kidney": "<strong>Kidney disease</strong>: You may get Medicare even if you're under 65",
          "disability": "<strong>Disability</strong>: After 2 years on Social Security disability benefits (SSDI), you get Medicare",
          "working": "<strong>Still working at 65+</strong>: You have Medicare AND job insurance",
          "spouse": "<strong>Spouse's insurance</strong>: Your spouse's job covers you too"
        },
        "startButton": "Start the Quiz",
        "duration": "Takes about 1 minute"
      },
      "progress": "{{current}} of {{total}}",
      "questionLabel": "Question {{number}}",
      "question1": {
        "title": "Do you have more than one health insurance plan?",
        "subtitle": "For example: Medicare AND insurance from a job",
        "yes": "Yes, I have two or more plans",
        "no": "No, I have just one plan",
        "startOver": "Start over"
      },
      "question2": {
        "title": "Which best describes your situation?",
        "subtitle": "Pick the one that fits you best",
        "options": {
          "medicare_employer_working": {
            "label": "I have Medicare AND job insurance",
            "detail": "I or my spouse still works"
          },
          "medicare_retiree": {
            "label": "I have Medicare AND retiree insurance",
            "detail": "I'm retired but still have coverage from my old job"
          },
          "medicare_medicaid": {
            "label": "I have Medicare AND Medicaid",
            "detail": "I have both government programs"
          },
          "esrd_employer": {
            "label": "I have kidney disease Medicare AND job insurance",
            "detail": "I got Medicare because of kidney failure (ESRD)"
          },
          "not_sure": {
            "label": "I'm not sure / Something else",
            "detail": "My situation is different"
          }
        },
        "goBack": "Go back"
      },
      "result": {
        "onePlanTitle": "You Have One Insurance Plan",
        "onePlanText": "Since you have just one plan, you don't need to worry about which pays first. Use our main quiz to find help programs for your situation.",
        "findHelp": "Find Help Programs",
        "takeAgain": "Take Quiz Again",
        "paysFirst": "PAYS FIRST",
        "paysSecond": "PAYS SECOND",
        "copayQuestion": "Can you use copay cards?",
        "copayCards": "Copay Cards",
        "copayYes": "Yes, you can use them!",
        "copayNo": "No, not with Medicare first",
        "copayDepends": "It depends on your situation",
        "papTitle": "Free Medicine Programs",
        "papYes": "Yes, you can apply for PAPs",
        "nextTitle": "What to do next:"
      }
    },
    "page": {
      "title": "Frequently Asked Questions",
      "subtitle": "Find answers to common questions about transplant medications, assistance programs, and using this site.",
      "searchLabel": "Search for a question",
      "searchPlaceholder": "Search for a question...",
      "clearSearch": "Clear search",
      "startHere": "Start here",
      "startHereText": "New to medication assistance? These five questions cover the basics.",
      "allTopics": "All topics",
      "topicsAria": "Filter questions by topic",
      "resultsCount_one": "{{count}} question matches your search",
      "resultsCount_other": "{{count}} questions match your search",
      "noResultsTitle": "No questions match",
      "noResultsText": "Try a different word, or clear the search to see every question.",
      "emptyTitle": "No FAQs available",
      "emptyText": "Check back later for frequently asked questions.",
      "browseResources": "Browse Resources",
      "stillTitle": "Still have questions?",
      "stillText": "Your transplant center's social worker or financial coordinator is your best resource for personalized guidance.",
      "startQuiz": "Start My Path Quiz"
    }
  },
  "applicationHelp": {
    "header": {
      "title": "Application Education",
      "subtitle": "Master the art of assistance applications with step-by-step guidance and insider tips.",
      "quizPromptTitle": "Not sure which programs to apply to?",
      "quizPromptText": "Take the My Path Quiz first to get personalized recommendations"
    },
    "tabs": {
      "ariaLabel": "Application help sections",
      "start": "Getting Started",
      "income": "Income",
      "steps": "Steps",
      "checklist": "Checklist",
      "letters": "Letters",
      "meds": "Medications"
    },
    "start": {
      "goodNews": {
        "title": "Good News",
        "item1": "PAPs and Foundations ask for the same information.",
        "item2": "Gather documents once → apply to multiple programs."
      },
      "whatArePaps": {
        "title": "What are Patient Assistance Programs?",
        "p1": "Patient Assistance Programs (PAPs) are programs created by drug companies and medical supply makers to help patients access affordable medications. These programs provide prescription medications at no cost or low cost for individuals who need help affording their medications.",
        "p2": "<strong>Important:</strong> Patient Assistance Programs are not only for people without insurance. Most are for patients on Medicare, Medicaid, or with no coverage, and they go by income rather than by whether you are insured. Programs vary, so check each one \u2014 if you have commercial or employer insurance, a copay card is usually your route instead."
      },
      "cards": {
        "pap": {
          "title": "Patient Assistance Programs",
          "text": "Free or low-cost medications straight from the drug company. Most programs are for patients on Medicare, Medicaid, or with no coverage, and they qualify you by income."
        },
        "copay": {
          "title": "Copay Cards",
          "text": "Manufacturer copay cards help reduce your out-of-pocket costs at the pharmacy.",
          "eligibility": "<strong>Who can use copay cards:</strong> Only for insurance you get through work or buy on your own. Not for Medicare, Medicaid, or VA benefits. If you have these, look at Patient Assistance Programs instead."
        },
        "insurance": {
          "title": "Help Paying for Insurance",
          "text": "Ask the foundation to help pay what you owe to your insurance company to keep your medical or prescription coverage."
        }
      },
      "compare": {
        "bestForLabel": "Best for:",
        "approvalTimeLabel": "Approval Time",
        "pap": {
          "title": "Patient Assistance Program (PAP)",
          "subtitle": "Direct from Manufacturer",
          "description": "Free or low-cost medications provided directly by the drug company.",
          "bestFor": [
            "Medicare patients",
            "Medicaid patients",
            "Uninsured",
            "Underinsured"
          ],
          "approvalTime": "2–4 weeks"
        },
        "foundation": {
          "title": "Foundation Grant",
          "subtitle": "Non-profit Assistance",
          "description": "Helps with copays, premiums, deductibles, and out-of-pocket costs.",
          "bestFor": [
            "Medicare patients",
            "High copay patients",
            "Premium burden"
          ],
          "approvalTime": "1–3 weeks"
        }
      },
      "safety": {
        "title": "Important Safety Notice",
        "text": "Remember: Real organizations NEVER ask for payment. If someone asks for payment or credit card details, that's a scam."
      },
      "reminders": {
        "title": "Important Reminders",
        "applyOnceTitle": "Apply Once Only",
        "applyOnceText": "Multiple submissions to the same foundation slow down approval.",
        "applyAnytimeTitle": "Apply Anytime",
        "applyAnytimeText": "There is no \"season.\" Apply when cost becomes a barrier.",
        "peopleHelpTitle": "People Want to Help",
        "peopleHelpText": "Most PAP and foundation teams are kind and patient.",
        "faxTitle": "Fax is Still Used",
        "faxText": "Faxed forms go directly to a secure, dedicated team."
      }
    },
    "income": {
      "title": "Who Can Get Help",
      "intro": "Most drug companies publish clear income guidelines. Here's what you need to know:",
      "dontCountOut": "Don't Count Yourself Out: Many programs have much higher income limits than you might expect.",
      "howLimitsTitle": "How Income Limits Work",
      "howLimitsItems": [
        "Based on Federal Poverty Level (FPL) percentages",
        "Vary by household size",
        "Updated annually with FPL changes"
      ],
      "whereTitle": "Where to Find Guidelines",
      "whereItems": [
        "Manufacturer's website",
        "MAT.org search results",
        "Call the program directly"
      ],
      "checkerTitle": "Quick Income Checker",
      "checkerText": "Use these resources to check current Federal Poverty Level guidelines:",
      "hhsLink": "HHS Poverty Guidelines",
      "hhsAriaLabel": "Visit HHS Poverty Guidelines (opens in new tab)",
      "phrmaLink": "PhRMA Patient Assistance",
      "phrmaAriaLabel": "Visit PhRMA Patient Assistance resources (opens in new tab)"
    },
    "steps": {
      "title": "Apply These Principles to Both PAPs and Foundations",
      "bothRequire": "Both require:",
      "requirements": [
        "Your name and contact info",
        "Doctor / coordinator contact",
        "Income and household size",
        "Diagnosis + medication list",
        "Insurance information",
        "Patient history"
      ]
    },
    "checklist": {
      "title": "\"Before You Apply\" Checklist",
      "printAriaLabel": "Print checklist",
      "print": "Print",
      "yourResponsibility": "Your Responsibility",
      "percentReady": "{{progress}}% Ready",
      "progressAriaLabel": "Application preparation progress: {{progress}} percent complete",
      "gatherNote": "Gather these items before you apply. Click to check them off.",
      "itemsAriaLabel": "Application checklist items",
      "complete": "🎉 You have everything you need! Time to apply.",
      "clinicTitle": "What the Clinic Handles",
      "clinicIntro": "You do <strong>NOT</strong> need to provide these. Your provider will fill them in:",
      "clinicItems": [
        "ICD-10 diagnosis codes",
        "Provider NPI numbers",
        "Prescriber signatures",
        "Medical chart notes",
        "Treatment start dates"
      ],
      "crucialTitle": "Crucial Step",
      "crucialText": "It is <strong>important to follow up</strong> to make sure the provider is getting the paperwork sent back to the program."
    },
    "letters": {
      "builder": {
        "title": "Letter Builder",
        "intro": "Select a letter type, fill in your details, and generate a professional letter you can copy and use.",
        "letterTypeLabel": "Letter Type",
        "typeAppeal": "Insurance Appeal Letter",
        "typePap": "PAP Reconsideration Letter",
        "typeDoctor": "Request Doctor's Support",
        "typeHardship": "Financial Hardship Statement",
        "yourNameLabel": "Your Name",
        "yourNamePlaceholder": "Your full name",
        "medicationLabel": "Medication Name",
        "medicationPlaceholder": "e.g., Tacrolimus",
        "appealReasonLabel": "Reason for Appeal",
        "reasonFinancial": "Financial Hardship",
        "reasonAccess": "Access Issues (Timing/Delivery)",
        "reasonStability": "Clinical Stability (Already stable)",
        "transplantTypeLabel": "Transplant Type",
        "transplantTypePlaceholder": "e.g., Kidney, Liver, Heart",
        "transplantDateLabel": "Transplant Date (optional)",
        "transplantDatePlaceholder": "e.g., January 2023",
        "doctorNameLabel": "Doctor's Name",
        "doctorNamePlaceholder": "e.g., Smith",
        "programNameLabel": "Program Name(s)",
        "programNamePlaceholder": "e.g., Astellas Patient Assistance",
        "situationLabel": "Describe Your Situation",
        "situationPlaceholder": "Briefly describe your financial circumstances...",
        "generate": "Generate Letter",
        "generatedHeading": "Generated Letter:",
        "copied": "Copied!",
        "copyText": "Copy Text"
      },
      "scripts": {
        "title": "Phone Scripts",
        "intro": "Use these scripts when calling manufacturers and foundations.",
        "manufacturerTitle": "Calling Manufacturers",
        "manufacturerPre": "\"I'm a transplant patient. Do you have a Patient Assistance Program for ",
        "manufacturerHighlight": "[drug name]",
        "manufacturerPost": "?\"",
        "foundationTitle": "Calling Foundations",
        "foundationPre": "\"Hi, I am checking to see if the ",
        "foundationHighlight1": "[Disease Fund Name]",
        "foundationMid": " fund is currently open. I have insurance, but I need help with my ",
        "foundationHighlight2": "[Copays / Premiums]",
        "foundationPost": ".\""
      }
    },
    "meds": {
      "title": "Medications & PAP Programs",
      "intro": "View your medications alongside their Patient Assistance Programs. Search for medications to see application links.",
      "loadedBanner_one": "We loaded {{count}} medication from your My Path Quiz or health system import. Add more below or view program details.",
      "loadedBanner_other": "We loaded {{count}} medications from your My Path Quiz or health system import. Add more below or view program details.",
      "clearListAriaLabel": "Clear the medications loaded from your My Path Quiz or health system import",
      "clearList": "Clear list",
      "searchTitle": "Search for Medications",
      "searchIntro": "Search for your transplant medications to see their Patient Assistance Program information.",
      "searchLabel": "Search for medications",
      "searchPlaceholder": "Enter drug name (e.g. Prograf, Tacrolimus)...",
      "searchingAriaLabel": "Searching",
      "clearSearchAriaLabel": "Clear search",
      "added": "Added",
      "add": "Add",
      "noMatches": "No matches found",
      "noMatchesPre": "Try a different spelling or check our ",
      "noMatchesLink": "medication search page",
      "noMatchesPost": ".",
      "insurance": {
        "question": "Do you have commercial insurance?",
        "explanation": "Commercial insurance is coverage you get through an employer, the ACA marketplace, or that you buy privately, not Medicare, Medicaid, or TRICARE. Copay cards are only available with commercial insurance, so this helps us show you the right savings programs.",
        "yes": "Yes",
        "no": "No",
        "yesInfo": "<strong>Copay cards can help!</strong> With commercial insurance, manufacturer copay cards can lower your cost to $0-$50/month for many transplant medications.",
        "noInfo": "<strong>Patient Assistance Programs (PAPs) can help.</strong> Drug manufacturers offer free medication programs for patients who qualify based on income. Foundations may also help with copays."
      },
      "yourMedications": "Your Medications ({{count}})",
      "cardShows": "Each medication card shows:",
      "cardShowsAssistance": "<strong>Assistance tab:</strong> PAP programs and copay cards with direct application links",
      "cardShowsPrice": "<strong>Price tab:</strong> Estimated costs at different pharmacies",
      "cardShowsOverview": "<strong>Overview tab:</strong> Basic medication information",
      "readyTitle": "Ready to Apply?",
      "readyText": "Check out the other tabs on this page for step-by-step application guidance, checklists, and letter templates.",
      "viewSteps": "View Application Steps",
      "viewChecklist": "View Checklist",
      "emptyTitle": "No medications added yet",
      "emptyText": "Use the search box above to find your medications, or take the My Path Quiz to build your list, you can connect to your health system there to import them automatically.",
      "takeQuiz": "Take My Path Quiz",
      "fullSearch": "Full Medication Search"
    }
  },
  "layout": {
    "notFound": {
      "title": "Page Not Found",
      "text": "We couldn't find the page you're looking for. It may have been moved or doesn't exist.",
      "goHome": "Go to Home",
      "goHomeAria": "Return to home page",
      "startPath": "Start Medication Path",
      "startPathAria": "Start medication assistance wizard"
    },
    "banner": {
      "urgentText": "Out of medication, or down to your last few days?",
      "urgentLink": "Get help today"
    },
    "skipLink": "Skip to main content",
    "nav": {
      "brandAriaLabel": "Transplant Medication Navigator™ home",
      "brandLine1": "Transplant Medication",
      "brandLine2": " Navigator",
      "brandTm": "™",
      "logoAlt": "Transplant Medication Navigator logo",
      "mainAriaLabel": "Main navigation",
      "mobileAriaLabel": "Mobile navigation",
      "openMenu": "Open menu",
      "closeMenu": "Close menu",
      "simpleView": "Larger text: off",
      "simplePrompt": {
        "ariaLabel": "Display size option",
        "text": "Need bigger text? We can make every page larger.",
        "tryIt": "Turn on larger text",
        "dismissAria": "Dismiss this message"
      },
      "simpleViewExit": "Larger text: on",
      "links": {
        "home": {
          "label": "Home",
          "ariaLabel": "Go to home page"
        },
        "wizard": {
          "label": "My Path",
          "ariaLabel": "My Path: start the medication path quiz"
        },
        "education": {
          "label": "Resources",
          "ariaLabel": "Browse resources, education, and coverage guides"
        },
        "applicationHelp": {
          "label": "How to apply",
          "ariaLabel": "Learn how to apply for help paying for your medications"
        },
        "faq": {
          "label": "FAQ",
          "ariaLabel": "FAQ: view frequently asked questions"
        },
        "feedback": {
          "label": "Feedback",
          "ariaLabel": "Share your feedback"
        }
      }
    },
    "footer": {
      "disclaimer": "<strong>Disclaimer:</strong> This tool provides educational information to help you navigate medication assistance options. It is not a substitute for professional medical advice. Always consult your transplant team or healthcare provider with any questions about your medical condition or treatment.",
      "nlm": "We use information from the U.S. National Library of Medicine (NLM). The NLM does not support or recommend this tool.",
      "lastUpdated": "Verified quarterly · Last verified {{date}}",
      "copyright": "© 2026 Transplant Medication Navigator™. All Rights Reserved.",
      "createdBy": "Created by Lorrinda Gray-Davis · Established August 2025",
      "nondiscrimination": {
        "label": "Non-Discrimination Notice:",
        "text": " In accordance with Section 504 of the Rehabilitation Act of 1973, TransplantNav LLC does not discriminate on the basis of disability.",
        "link": "Section 504 Notice & Grievance Procedure"
      },
      "links": {
        "terms": "Terms of Service",
        "privacy": "Privacy Policy",
        "accessibility": "Accessibility & Section 504",
        "feedback": "Feedback",
        "hospitals": "Hospitals",
        "pricing": "Plans & Pricing",
        "evidence": "The Evidence",
        "privacyChoices": "Privacy Choices"
      }
    }
  },
  "home": {
    "hero": {
      "title1": "Your transplant team decides your medicine.",
      "title2": "We help you get it.",
      "subtitle": "What it should cost, which programs may be open to you, and what to do when your plan changes the rules.",
      "cta": "Answer 5 questions and see all your options",
      "ctaHint": "About 2 minutes. No name, no account. Checks copay cards, assistance programs, foundations, and prices at once."
    },
    "flow": {
      "ariaLabel": "How it works, in three steps",
      "step1": "Find or import your meds",
      "step2": "Pick your insurance",
      "step3": "See your options"
    },
    "steps": {
      "medsLabel": "1 · Your medications",
      "searchAriaLabel": "Type your medication name to search",
      "searchPlaceholder": "Type a medication...",
      "resultsAriaLabel": "Matching medications",
      "noResults": "We don't have that one yet.",
      "noResultsLink": "Try the full medication search",
      "or": "or",
      "mychartButton": "Import my list from MyChart",
      "mychartFootnote": "Listed in the Epic Connection Hub. You'll sign in on your hospital's own portal — we never store your login. Imports your real list from 400+ Epic health systems, including whether you're on the brand or the generic. Your data stays on your device.",
      "examplesAriaLabel": "Common transplant medications",
      "tagGeneric": "generic",
      "tagBrand": "brand",
      "browseAll": "Browse all {{count}} medications",
      "browseAllAria": "Browse all {{count}} medications (full list)",
      "selectedOne": "Your medication:",
      "selectedMany": "Your {{count}} medications:",
      "removeSelected": "Remove {{name}}",
      "addAnotherHint": "Taking more? Search or tap another to add it to your list.",
      "seeOptions": "See my options",
      "seeOptionsHint": "Pick a medication in step 1 first.",
      "insuranceLabel": "2 · Your insurance",
      "insuranceAriaLabel": "Choose your insurance type",
      "insCommercial": "Through work / Marketplace",
      "insMedicare": "Medicare",
      "insMedicaid": "Medicaid",
      "insUninsured": "No insurance",
      "multiplePre": "Taking more than one?",
      "multipleLink": "Answer 5 questions and we'll do all of them at once",
      "previewSummary": "See the 5 questions first",
      "previewIntro": "Quick and anonymous. About 2 minutes, no name, no account.",
      "previewQ1": "About you — before or after transplant",
      "previewQ2": "Your transplant — which organ",
      "previewQ3": "Your medications — pick from a list or import them",
      "previewQ4": "Your coverage — what insurance you have",
      "previewQ5": "Your costs — how hard they are to manage"
    },
    "trust": "{{count}} transplant medications · Free, no account · Your answers stay on your device · Built by a liver transplant recipient",
    "explainer": {
      "title": "Not sure whether you're on the brand or the generic?",
      "intro": "Many transplant patients are on generics and were never told. Your bottle holds the answer, and it decides which options are yours.",
      "genericTitle": "Your label says “Tacrolimus”",
      "genericText": "Tacrolimus is the drug's own name, so this is the generic. The bottle often names a maker like Sandoz or Accord.",
      "genericAction": "Compare cash prices. No copay card exists.",
      "brandTitle": "Your label says “Prograf”",
      "brandText": "Prograf is a brand name. Same medicine, different price, different programs.",
      "brandAction": "Copay card or manufacturer assistance.",
      "neverTitle": "We never tell you to change your medication.",
      "neverText": "That's your transplant team's decision — including switching between brand and generic, or between generic manufacturers. We show you what the medicine they prescribed should cost you."
    },
    "cards": {
      "ariaLabel": "Guides and programs",
      "learn": {
        "title": "Learn the process",
        "text": "Fill out assistance applications yourself, free. Checklists and phone scripts.",
        "ariaLabel": "View the guide: how to apply",
        "link": "View the guide"
      },
      "coverage": {
        "title": "Coverage education",
        "text": "Medicare Part B-ID, Part D, Medicaid, VA and IHS benefits explained.",
        "ariaLabel": "Learn about coverage options",
        "link": "Learn about coverage"
      },
      "grants": {
        "title": "Grants & foundations",
        "text": "{{programs}} assistance programs and foundations, plus {{copayCards}} copay cards. Verified, direct links, no scams. Updated {{date}}.",
        "ariaLabel": "Browse programs: grants and foundations",
        "link": "Browse programs"
      }
    },
    "story": {
      "ariaLabel": "Real patient savings story",
      "badge": "If you're on a brand-name medicine with job insurance",
      "quotePre": "“My prescription was ",
      "price1": "$624",
      "quoteMid1": ". With insurance it dropped to ",
      "price2": "$195",
      "quoteMid2": ". But nobody told me about copay cards. I found one myself and now I pay just ",
      "price3": "$10",
      "quotePost": ".”",
      "attribution": "Liver transplant recipient, 2025. Name withheld at the patient's request. Savings vary by medication and insurance."
    },
    "testimonial": {
      "ariaLabel": "Patient testimonial",
      "quote": "\u201cThe Transplant Medication Navigator has saved me money and given me peace of mind. When my transplant hepatologist changed one of my immunosuppressant medications, I was able to use the Navigator right there during my visit to see what the new medication could cost. It puts valuable information in the hands of transplant recipients when we need it most.\u201d",
      "name": "Mike Birdsong",
      "role": "Liver transplant recipient",
      "disclaimer": "Shared with permission. Savings vary by medication and insurance."
    },
    "evidence": {
      "ariaLabel": "What the research shows",
      "stat": "1 in 4",
      "text": "<strong>liver transplant candidates spend more than 10% of their income on medical costs — before they're even listed.</strong> Two national peer-reviewed studies document what this tool exists to fix.",
      "link": "See the evidence"
    },
    "founder": {
      "photoAlt": "Lorrinda Gray-Davis, founder of Transplant Medication Navigator",
      "name": "Lorrinda Gray-Davis",
      "role": "Liver transplant recipient · Vice Chair, OPTN Patient Affairs Committee",
      "bio": "Lorrinda built this from her own experience at the pharmacy counter, with research on transplant patients' costs published in peer-reviewed journals.",
      "link": "Read her story"
    },
    "hotline": {
      "title": "Struggling with more than the cost?",
      "intro": "Being a transplant patient can be hard on your feelings. You can get free, private help any time, day or night.",
      "lifeline": "National Suicide & Crisis Lifeline",
      "availability": "24/7 • Confidential",
      "encouragement": "Asking for help shows strength, not weakness. Life after a transplant is hard on your body and your feelings. Taking care of how you feel is just as important as taking your medicine. Having a hard time? Please reach out. We want to help you.",
      "callTitle": "Call or Text",
      "callText": "Dial or text <strong>988</strong> from any phone (Spanish-language support: press 2 / oprima el 2)",
      "chatTitle": "Online Chat (also available in Spanish)"
    }
  },
  "about": {
    "back": "Back to home",
    "heading": "Created by Someone Who's Been There",
    "photoAlt": "Lorrinda Gray-Davis, founder of Transplant Medication Navigator",
    "name": "Lorrinda Gray-Davis",
    "role": "Liver cancer survivor, liver transplant recipient, and President of Transplant Recipients International Organization (TRIO)",
    "bio1": "In 2017, Lorrinda was diagnosed with end-stage liver disease and inoperable liver cancer. She received her liver transplant in 2018 — and built Transplant Medication Navigator because she knows what it's like to face a pharmacy counter and a price you can't pay.",
    "bio3": "She serves as President of Transplant Recipients International Organization (TRIO) and Vice Chair of the OPTN Patient Affairs Committee. She also serves on two HRSA bodies: the Technical Expert Panel on Safety and Quality Standards for Organ Procurement Organizations (OPOs) and the Allocation Out of Sequence (AOOS) Committee.",
    "stat1Label": "Transplant Recipient Since",
    "stat4Label": "Published Research Papers & Presentations",
    "credsTitle": "Leadership & Recognition",
    "credentials": [
      "Vice Chair, OPTN Patient Affairs Committee",
      "Technical Expert Panel Member, HRSA Safety & Quality Standards for Organ Procurement Organizations (2025–Present)",
      "Member, HRSA Allocation Out of Sequence (AOOS) Committee (2025–Present)",
      "HRSA Transitional Nominating Committee (2025)",
      "President, TRIO (1,500+ members)",
      "Harrison Fellow, Fatty Liver Foundation",
      "AASLD Patient Advisory Group",
      "HRSA Letter of Commendation (2025)",
      "Co-First Author, Hepatology Communications",
      "Featured in Cancer Today magazine (2025)"
    ],
    "pubsTitle": "Published Research & Presentations (7 Publications)",
    "pubsNote": "Publication titles are shown in English, as originally published.",
    "badgePeer": "Peer-Reviewed Journal",
    "badgeDistinction": "Poster of Distinction",
    "badgePoster": "Conference Poster",
    "badgeWtc": "World Transplant Congress",
    "pub1Venue": "Hepatology Communications, Vol. 10, Issue 2 (2026) · Co-First Author",
    "pub2Venue": "Liver Transplantation, 2026 · First Author",
    "pub3Venue": "Hepatology, Vol. 82, S1 · AASLD The Liver Meeting 2025 (Washington, DC) · First Author",
    "pub4Venue": "Hepatology, Vol. 82, S1 · AASLD The Liver Meeting 2025 (Washington, DC) · Co-Author",
    "pub5Venue": "Hepatology, Vol. 80, S1 · AASLD The Liver Meeting 2024 (San Diego, CA) · First Author",
    "pub6Venue": "Hepatology, Vol. 80, S1 · AASLD The Liver Meeting 2024 (San Diego, CA) · First Author",
    "pub7Venue": "American Journal of Transplantation, Vol. 25, S1 · WTC 2025 (San Francisco, CA) · Co-Author",
    "whyTitle": "Why We Built This: The Independent Evidence",
    "whyText": "Two national peer-reviewed studies now document what this tool was built to solve: 1 in 4 liver transplant candidates faces high financial burden before listing, and nearly 40% of transplant recipients miss medication fills because of cost.",
    "evidenceLink": "Read the evidence",
    "learnMore": "Learn more about Lorrinda"
  },
  "evidence": {
    "hero": {
      "title": "The Evidence: Why Medication & Financial Navigation Matters",
      "intro": "New peer-reviewed research confirms what transplant patients have always known — the cost of survival is a clinical problem, not just a personal one. Two national studies now document it across the entire transplant journey: before transplant, financial burden threatens candidacy; after transplant, medication costs threaten the graft itself."
    },
    "burden": {
      "title": "1 in 4 Liver Transplant Candidates Faces High Financial Burden",
      "intro": "A 2026 multicenter U.S. study published in <em>Hepatology Communications</em> — spanning more than a dozen transplant centers including Massachusetts General Hospital, University of Minnesota, USC, Duke, and Montefiore Einstein — surveyed 453 adults undergoing liver transplant evaluation. The findings were stark:",
      "stat1": "reported high financial burden — out-of-pocket medical costs of 10% or more of household income",
      "stat2": "were still employed at the time of transplant evaluation",
      "stat3": "of high-burden patients delayed or went without medical care (vs. 33.5% of low-burden patients)",
      "stat4": "could not pay for basic necessities like food, heat, or rent (vs. 11.3%)",
      "stat5": "of high-burden patients lost savings or assets",
      "stat6": "of high-burden patients faced medical debt or bankruptcy"
    },
    "odds": {
      "title": "Financial Burden Predicts Distress Across Every Dimension",
      "intro": "Financial burden is not a side issue. After adjusting for age, income, insurance, disease severity, and other factors, patients with high financial burden had dramatically higher odds of harm:",
      "caption": "Adjusted odds of financial distress outcomes for high-burden vs. low-burden liver transplant candidates",
      "colOutcome": "Outcome",
      "colOdds": "Adjusted Odds",
      "colOddsQualifier": " (high vs. low burden)",
      "row1": "Problems paying medical bills",
      "row2": "Loss of savings or assets",
      "row3": "Unable to pay for basic necessities",
      "row4": "Any material financial distress",
      "row5": "Any psychological financial distress",
      "row6": "Any behavioral financial distress (delaying or skipping care)",
      "row7": "Medical debt or bankruptcy",
      "work": "Financial burden also reached the workplace: employed patients with high financial burden missed twice as much work as their low-burden peers (34.1% vs. 16.9% absenteeism). For transplant candidates, employment is not incidental — it is often the source of the insurance and income that candidacy depends on."
    },
    "graft": {
      "title": "After Transplant, the Burden Doesn't End — It Threatens the Graft",
      "intro": "The largest patient survey ever commissioned by the American Society of Transplantation, published in the <em>American Journal of Transplantation</em> in 2025, gathered 10,091 responses from transplant recipients across 232 U.S. transplant centers — spanning every organ, all ages, and every insurance type, at an average of 6.6 years post-transplant. Its cost findings should alarm every transplant program:",
      "stat1Value": "~40%",
      "stat1Label": "of recipients missed a medication fill or refill in the past year because of cost",
      "stat2Value": "1 in 4",
      "stat2Label": "skipped or reduced immunosuppressant doses due to cost \"sometimes\" or \"often\"",
      "p1": "For transplant recipients, these are not ordinary adherence lapses. Missed immunosuppressant doses are among the strongest known drivers of rejection, graft loss, and return to the waitlist — outcomes that carry enormous human and financial cost for patients, programs, and payers alike.",
      "p2": "Read together, the two studies describe a single continuum: financial burden that surfaces during transplant evaluation does not resolve after surgery — it converts into cost-related nonadherence that puts the transplanted organ at risk, years down the road."
    },
    "gap": {
      "title": "What the Researchers Recommend — and Where the System Falls Short",
      "quote": "The study's authors conclude that <strong>routine screening for financial burden is critical in liver transplant candidates and should be paired with targeted strategies to mitigate its impact.</strong>",
      "text": "That second half is the gap. Screening tells a transplant program <em>which</em> patients are drowning. It does not throw them a rope. A patient identified as high-burden still faces a fragmented maze of manufacturer programs, foundations, insurance rules, and paperwork — the same maze that caused 66% of high-burden patients to delay or forgo care in the first place."
    },
    "tmn": {
      "title": "Transplant Medication Navigator Is the Targeted Strategy",
      "intro": "TMN was built to be the intervention that the research calls for — the step between identifying financial burden and resolving it.",
      "s1Title": "Integrated where care happens",
      "s1Text": "TMN is a SMART on FHIR application listed on the Epic Connection Hub, with 500+ organizational downloads, so navigation can live inside the transplant center's own workflow.",
      "s2Title": "Insurance-aware routing",
      "s2Text": "The right program depends on coverage. TMN routes commercially insured patients toward pharmaceutical copay cards and Medicare patients toward patient assistance programs (PAPs) and charitable foundations — two distinct pathways that patients routinely confuse, at real cost.",
      "s3Title": "Beyond medications",
      "s3Text": "Because the research shows the burden extends to premiums, bills, travel, and basic necessities, TMN also connects patients to copay foundations, case-management services, and low-cost pharmacy options such as Cost Plus Drugs.",
      "s4Title": "Built from lived experience",
      "s4Text": "Created by a liver transplant recipient and national patient advocate — designed around how patients actually encounter these barriers, not how the system assumes they do."
    },
    "whyNow": {
      "title": "Why This Matters to Transplant Programs Now",
      "p1": "Value-based accountability is arriving in transplantation. CMS's IOTA model now ties kidney transplant program payment to transplant volume and post-transplant outcomes — and the direction of travel is clear across all organs. This research shows that financial burden drives delayed care, missed work, and psychological distress: precisely the forces that erode candidacy, adherence, and graft survival.",
      "p2": "Programs that screen for financial burden without an operational answer are documenting risk, not managing it.",
      "scopeLabel": "A note on scope:",
      "scopeText": " the financial burden study examined liver transplant candidates specifically, so its exact figures should not be assumed identical for other organs. The AST survey, however, spanned all solid organ types — and its finding that cost drives missed fills and skipped doses in roughly 4 in 10 recipients confirms the problem is transplant-wide, not organ-specific.",
      "ctaPrograms": "See the Solution for Transplant Programs",
      "ctaBriefing": "Request an Evidence Briefing"
    },
    "patient": {
      "title": "Facing these costs yourself?",
      "p1": "You don't have to navigate the maze alone. Take the free 2-minute quiz to find copay cards, patient assistance programs, and foundation grants for your medications.",
      "p2": "And if you're not able to work right now — in the study, only about 1 in 4 candidates were still employed by the time of transplant evaluation — you may qualify for programs you didn't know existed.",
      "cta": "Find your path to affordable medications"
    },
    "sources": {
      "title": "Sources",
      "openAccess": "(open access)",
      "study1Summary": "",
      "study2Summary": "",
      "disclaimer": "This page summarizes independently conducted, peer-reviewed research. Transplant Medication Navigator was not involved in the studies. This content is educational and is not medical, legal, or financial advice."
    }
  },
  "wizard": {
    "progress": {
      "labels": [
        "About You",
        "Transplant",
        "Medications",
        "Coverage",
        "Costs"
      ],
      "ariaLabel": "Quiz progress"
    },
    "nav": {
      "back": "Back",
      "backAria": "Go back to previous section",
      "nextSection": "Next Section",
      "nextAria": "Continue to next section"
    },
    "organs": {
      "Kidney": "Kidney",
      "Liver": "Liver",
      "Heart": "Heart",
      "Lung": "Lung",
      "Pancreas": "Pancreas",
      "Intestine": "Intestine",
      "Multi-organ": "Multi-organ",
      "Other": "Other"
    },
    "aboutYou": {
      "title": "About You",
      "intro": "Let's start with some basics to personalize your experience.",
      "note": "<strong>Note:</strong> This tool provides educational information to help you navigate medication assistance options. It is not a substitute for professional medical advice. Always consult your transplant team or healthcare provider with any questions about your medical condition or treatment.",
      "roleBadge": "1a",
      "roleQuestion": "Who am I helping today?",
      "roleAria": "Select your role",
      "rolePatient": "Patient",
      "roleCarepartner": "Carepartner / Family",
      "roleSocialWorker": "Social Worker / Coordinator",
      "statusBadge": "1b",
      "statusQuestion": "Where are you in the transplant process?",
      "statusAria": "Select your transplant status",
      "statusPreEval": "Pre-transplant (Evaluation/Waitlist)",
      "statusPostAcute": "Post-transplant (Within 1st year)",
      "statusPostStable": "Post-transplant (1+ years)",
      "confidenceBadge": "1c",
      "confidenceQuestionSelf": "How confident are you that you can afford your transplant medications?",
      "confidenceQuestionOther": "How confident are you that the person you are helping can afford their transplant medications?",
      "confidenceAria": "Rate your confidence from 1, not at all confident, to 5, very confident",
      "confidenceOptionAria": "{{score}} out of 5",
      "confidenceLow": "Not at all confident",
      "confidenceHigh": "Very confident",
      "confidenceNote": "We ask this once now and once at the end to see if this tool helps. Your answer is anonymous and never linked to you."
    },
    "transplant": {
      "title": "Your Transplant",
      "intro": "Select your organ type(s) - choose all that apply.",
      "groupAria": "Select organ types"
    },
    "meds": {
      "title": "Your Medications",
      "intro": "Connect to your health system.",
      "selectedHeading": "Your Selected Medications ({{count}})",
      "removeAria": "Remove {{name}}",
      "separatorAria": "Or add medications yourself",
      "separatorLabel": "Or add your medications below",
      "searchHeadingPre": "Type in medication and hit the ",
      "searchHeadingPost": " button to add to your list",
      "searchLabel": "Search for medications",
      "searchPlaceholder": "Type medication name (e.g. Prograf, tacrolimus)...",
      "searchingAria": "Searching",
      "clearSearchAria": "Clear search",
      "added": "Added",
      "add": "Add",
      "noResults": "No medications found. Try a different spelling or browse the list below.",
      "medicalInfoTitle": "Important Medical Information",
      "medicalInfoText": "Lifelong medication adherence is essential for transplant success. Always consult your transplant team before changing medications or adding any new ones, including over-the-counter drugs and supplements.",
      "continueButton": "My medications look right, continue →",
      "noMedsTitle": "You haven't added a medication yet",
      "noMedsText": "Your results match programs to the medications on your list. Without one, the next pages can only show general options.",
      "noMedsAdd": "Add a medication",
      "noMedsContinue": "Continue without medications",
      "guide": {
        "title": "Common Medications by Organ Type",
        "basedOnPre": "Based on your ",
        "and": " and ",
        "basedOnPost": " transplant, we've filtered to the most common medications.",
        "selectBelow": "Select your medications from the options below.",
        "selectCore": "First select your core immunosuppressants, then add any other transplant-related medications you take.",
        "clickPre": "Click an organ type to see typical medications, then tap ",
        "addWord": "Add",
        "clickPost": " next to each one you take. You can also use the search bar below to find any medication.",
        "thMedication": "Medication",
        "thClass": "Class",
        "thNotes": "Notes",
        "thAdd": "Add",
        "addToListAria": "Add {{name}} to your list",
        "removeFromListAria": "Remove {{name}} from your list"
      },
      "preGuide": {
        "title": "Common Medications Pre-Transplant by Organ Type",
        "intro": "Click on an organ type to see typical pre-transplant medications for managing your condition while awaiting transplant.",
        "thClass": "Medication Class",
        "thExamples": "Examples",
        "thPurpose": "Purpose",
        "addedAria": "{{name}} added to your list"
      }
    },
    "coverage": {
      "title": "Your Coverage",
      "tip": "Having insurance doesn't mean you can't get additional help!",
      "insuranceBadge": "3a",
      "insuranceQuestion": "What's your insurance type?",
      "insuranceAria": "Select your insurance type",
      "commercialLabel": "Commercial Insurance",
      "commercialDescription": "From my job, my spouse's job, or I bought it myself",
      "commercialHelp": "Can use manufacturer copay cards + PAPs",
      "medicareLabel": "Medicare",
      "medicareDescription": "The program for people 65+ or with disabilities",
      "medicareHelp": "Part B-ID important for kidney patients; can use PAPs but NOT copay cards",
      "medicaidLabel": "Medicaid",
      "medicaidDescription": "State program for people with lower income",
      "medicaidHelp": "May have full coverage; check state formulary",
      "tricareLabel": "TRICARE or VA (Veterans)",
      "tricareDescription": "Military or Veterans benefits",
      "tricareHelp": "Gov't insurance can use assistance programs",
      "ihsLabel": "Indian Health Service",
      "ihsDescription": "Tribal health programs",
      "ihsHelp": "Can use assistance programs",
      "uninsuredLabel": "I don't have insurance",
      "uninsuredDescription": "No current insurance coverage",
      "uninsuredHelp": "Manufacturer PAPs are your primary option",
      "pharmacyBadge": "3b",
      "pharmacyQuestion": "Does your plan require a specific specialty pharmacy?",
      "pharmacyIntro": "Some commercial plans require you to use a specific pharmacy for transplant medications.",
      "pharmacyAria": "Specialty pharmacy requirement",
      "yes": "Yes",
      "no": "No",
      "notSure": "Not Sure"
    },
    "costs": {
      "title": "Your Costs",
      "intro": "How would you describe your current medication costs?",
      "note": "This helps us prioritize the best assistance programs for you. Your answer is anonymous and never linked to you.",
      "groupAria": "Select your financial status",
      "manageableLabel": "Manageable",
      "manageableDesc": "I can afford my medications but would like to save money",
      "challengingLabel": "Challenging",
      "challengingDesc": "Medication costs are a significant burden",
      "unaffordableLabel": "Unaffordable",
      "unaffordableDesc": "I struggle to pay for my medications",
      "crisisLabel": "I've already skipped doses or gone without",
      "crisisDesc": "I need help right away to keep taking my medications"
    },
    "results": {
      "backAria": "Go back to previous step",
      "title": "Your Medication Strategy",
      "subtitle": "Based on your inputs, here is how to navigate your costs.",
      "printAria": "Print your medication plan",
      "printButton": "Print Plan",
      "bestNextStep": {
        "title": "Best next step",
        "crisis": "If you are out of medication or about to run out, call your transplant team today and use the emergency guide. Everything else on this page can wait.",
        "default": "Start with the first program listed under your medications below. It is matched to your insurance, and one application is enough to begin."
      },
      "medicareAlert": {
        "title": "Important: Medicare Part B-ID",
        "text": "Since you are a kidney transplant recipient on Medicare, you may qualify for <strong>Medicare Part B-ID</strong>. This extends coverage for immunosuppressive drugs for life. The 2026 premium is <strong>$121.60/month</strong> (up from $110.40 in 2025), plus 20% coinsurance after a $283 deductible.",
        "cta": "Check Eligibility on Medicare.gov"
      },
      "extraHelp": {
        "title": "Medicare Extra Help can cut your drug costs the most",
        "text": "<strong>Extra Help</strong> (also called the Low-Income Subsidy) is a Medicare program for people with limited income and savings. It can remove your Part D monthly premium and deductible and leave you with small set copays for each prescription, generic or brand. In 2026 the income limit is about {{single}} a year for one person or {{couple}} for a couple, and many people who qualify never apply.",
        "note": "Applying online with Social Security takes about 10 minutes, or call 1-800-772-1213. If you do not qualify, ask whether your state has a State Pharmaceutical Assistance Program.",
        "cta": "Apply for Extra Help",
        "ctaAria": "Apply for Medicare Extra Help on ssa.gov (opens in new tab)"
      },
      "programs": {
        "title": "Your Programs, By Medication",
        "subtitle": "Matched to your medication list and your insurance. These are the first programs to check based on the information you entered.",
        "papBadge": "Free medication",
        "copayBadge": "Copay card",
        "genericBadge": "Generic",
        "likelyQualify": "You likely qualify: {{insurance}}, income up to {{income}}.",
        "likelyQualifyNoIncome": "You likely qualify with {{insurance}} — check the program's income rules.",
        "checkEligibility": "Check eligibility with the program.",
        "copayGenericBenefit": "Can lower your copay, often to $0-$50 per month.",
        "papFallbackName": "{{manufacturer}} Patient Assistance Program",
        "copayFallbackName": "{{manufacturer}} Copay Card",
        "applyButton": "Apply",
        "applyAria": "Apply to {{program}} for {{med}} (opens in a new tab)",
        "genericNote": "This is a generic, and manufacturer programs are usually limited to brand-name versions. Good news: generics usually cost much less. Compare cash prices with the link below.",
        "noProgramNote": "No manufacturer program in our database for this medication. Compare cash prices with the link below.",
        "medicareCallout": "Because you have Medicare, manufacturer copay cards don't apply to you (it's a federal rule). Go to foundations instead:",
        "uninsuredCallout": "Without insurance, the manufacturer programs above are your best route. They can provide medication completely free if you qualify.",
        "foundationIncome": "Copay help, typically income up to {{income}}. Funds open and close — check back if closed.",
        "foundationIncomeGeneric": "Copay help — eligibility is based on need. Funds open and close — check back if closed.",
        "foundationAria": "Apply to {{program}} (opens in a new tab)",
        "othersTitle": "Your other medications ({{count}})",
        "othersTitleAll": "Your selected medications ({{count}})",
        "othersNote": "These are generics or have no manufacturer program — that's normal, and generics usually cost much less. Compare their cash prices with the link below.",
        "detailsLink": "See prices and full details for these medications",
        "insuranceLabels": {
          "commercial": "commercial insurance",
          "medicare": "Medicare",
          "medicaid": "Medicaid",
          "uninsured": "no insurance"
        }
      },
      "medListTitle": "Your Medication List",
      "addMoreLabel": "Add more medications",
      "addMorePlaceholder": "Add more medications...",
      "noResults": "No medications found",
      "viewPricesAria": "View price estimates for your selected medications",
      "viewPricesButton": "View Price Estimates for These Meds",
      "toolsTitle": "Helpful Tools",
      "toolsIntro": "Once you have identified the program you need (PAP or Foundation), use our guide to help you apply.",
      "appEducationAria": "View application education guide",
      "appEducationTitle": "Application Education",
      "appEducationDesc": "Scripts, checklists, and templates",
      "insuranceAria": "View insurance and resources",
      "insuranceTitle": "Insurance & Resources",
      "insuranceDesc": "Medicaid directory, Medicare guides",
      "manageable": {
        "title": "Maximize Your Savings",
        "priorityBadge": "Priority",
        "priorityAria": "Priority recommendation",
        "copayPre": "Use Manufacturer ",
        "copayTerm": "Copay",
        "copayPost": " Cards.",
        "copayText": "Even if you can afford the copay, these cards can lower it to as little as $0. Look up each of your brand name meds.",
        "compareBadge": "Compare",
        "compareAria": "Comparison tip",
        "cashTitle": "Check Cash Prices.",
        "cashText": "Compare your insurance copay against cash prices at Mark Cuban Cost Plus Drugs or using GoodRx.",
        "verifyBadge": "Verify",
        "verifyAria": "Verification step",
        "spTerm": "Specialty Pharmacy",
        "spPost": ".",
        "spText": "Ensure you are using the mandated pharmacy to avoid surprise full-price bills.",
        "tipPre": "Tip: You may still qualify for ",
        "tipTerm": "PAPs",
        "tipPost": " based on income, even if costs feel manageable right now."
      },
      "challenging": {
        "title": "Reduce Your Burden",
        "step1Badge": "Step 1",
        "step1Aria": "Step one",
        "papTitle": "Check Manufacturer PAPs.",
        "papText": "Apply to your matched programs listed above — each Apply button goes straight to the manufacturer's program. If eligible, the med is free.",
        "papTitleGeneric": "Check Generic Prices First.",
        "papTextGeneric": "Manufacturer PAPs are usually limited to brand-name meds, and your medications are generics. Good news: generics usually cost much less. Ask your pharmacy for the cash price, then compare in Step 3.",
        "step2Badge": "Step 2",
        "step2Aria": "Step two",
        "foundationsPre": "Apply to ",
        "foundationsTerm": "Foundations",
        "foundationsPost": " for copay help.",
        "foundationsText": "Organizations like HealthWell or TotalAssist help pay for copays. Apply to them for your specific disease fund.",
        "step3Badge": "Step 3",
        "step3Aria": "Step three",
        "cashTitle": "Compare vs. Cash.",
        "cashText": "Sometimes the cash price (e.g. Cost Plus Drugs) is cheaper than your insurance copay."
      },
      "crisis": {
        "title": "Immediate Assistance Path",
        "callTeamTitle": "Call your transplant team or pharmacy today",
        "callTeamText": "If you cannot pay for your medication right now, your transplant team's social worker or pharmacist can often arrange an emergency supply or bridge assistance the same day.",
        "notEmergency": "This questionnaire is educational and is not an emergency service. If you think you may have a medical emergency, call your doctor or 911.",
        "emergencyLink": "Out of medication, or down to your last few days? See emergency steps →",
        "notAlone": "You are not alone. Help is available. Please follow these steps in order.",
        "papTitle": "Manufacturer PAPs (Free Drug).",
        "papText": "Your matched programs are listed above under \"Your Programs, By Medication\" — apply to each manufacturer program today. If eligible, the medication is free.",
        "papTitleGeneric": "Low-Cost Generics.",
        "papTextGeneric": "Manufacturer programs are usually limited for generic medicines. Check each program's current rules, and compare foundation and cash-price options (e.g. Cost Plus Drugs). Your transplant team can also help.",
        "papLinkText": "See prices and full details",
        "papLinkPost": " for each of your medications.",
        "papLinkPostGeneric": " to compare cash prices.",
        "medicaidTitle": "Check Medicaid Eligibility.",
        "medicaidText": "If you have low income, check if you qualify for state Medicaid or \"Extra Help\" (if on Medicare).",
        "ihsTitle": "Contact IHS / Tribal Health.",
        "ihsText": "You likely have coverage for these medications at $0 cost at IHS facilities.",
        "notQualifyTitle": "If you don’t qualify — or a fund is closed",
        "notQualifyIntro": "A “no” from one program is not the end. Try these next:",
        "tryPrograms": "<strong>Apply to several programs at once.</strong> Income limits and open funds change often, so don’t stop at one.",
        "tryCharity": "<strong>Ask your transplant center about charity care.</strong> Many centers have their own financial-assistance or hospital discount programs — your social worker can start one.",
        "tryCash": "<strong>Ask the pharmacy about cash and sliding-scale prices.</strong> Cost Plus Drugs and pharmacy discount programs are sometimes cheaper than a copay.",
        "appealTitle": "Appeal a denial.",
        "appealMid": " Many denials are overturned. ",
        "appealLink": "See how to appeal",
        "appealPost": ".",
        "getHelpLink": "Get step-by-step help applying →"
      },
      "confidence": {
        "title": "One last question",
        "questionSelf": "Now that you have your plan, how confident are you that you can afford your transplant medications?",
        "questionOther": "Now that you have the plan, how confident are you that the person you are helping can afford their transplant medications?",
        "aria": "Rate your confidence now, from 1, not at all confident, to 5, very confident",
        "thanks": "Thank you. Your answer helps us make this tool better.",
        "before": "Before",
        "now": "Now",
        "note": "Anonymous, and never linked to you."
      },
      "restart": "Restart Wizard",
      "restartAria": "Restart the wizard from beginning"
    },
    "email": {
      "errorRequired": "Please enter your email address",
      "errorInvalid": "Please enter a valid email address"
    },
    "loading": "Loading..."
  },
  "appeals": {
    "header": {
      "back": "Back to Education & Resources",
      "title": "Got Denied? Don't Give Up.",
      "tagline": "You can fight back, and WIN!",
      "intro": "Insurance says \"no\" to almost everyone at some point. The good news? <strong>Many people win when they appeal.</strong> This interactive guide shows you how, step by step.",
      "stats": {
        "winRate": "40-75%",
        "winRateLabel": "Of appealed denials are overturned",
        "days": "60+",
        "daysLabel": "Days to appeal — your letter has your deadline",
        "free": "Free",
        "freeLabel": "To file an appeal",
        "sourcesIntro": "Sources: ",
        "source1": "HHS OIG (2018): Medicare Advantage plans overturned 75% of appealed denials",
        "source2": "KFF: about 4 in 10 appealed marketplace denials were overturned",
        "deadlineNote": "Deadlines depend on your plan: Medicare gives 65 days and many employer and marketplace plans give up to 180 — your denial letter lists your exact deadline."
      },
      "guideTitle": "Free Appeal Guide",
      "guideSubtitle": "Print-ready guide with checklists & sample letters",
      "guideDownload": "Download Free Guide"
    },
    "nav": {
      "ariaLabel": "Page sections",
      "prompt": "Click any section to explore:",
      "whyDenied": "Why Denied?",
      "stepTherapy": "Step Therapy",
      "generics": "Generic vs Brand",
      "howToAppeal": "How to Appeal"
    },
    "whyDenied": {
      "title": "Why Insurance Says No",
      "intro": "Your denial letter tells you why they said no. Here are the most common reasons:",
      "reasons": {
        "priorAuth": {
          "title": "Prior Auth Needed",
          "description": "Your doctor needs to ask permission first before the insurance will cover it."
        },
        "notOnList": {
          "title": "Not on the List",
          "description": "Your drug isn't on their approved list (called a formulary)."
        },
        "stepTherapy": {
          "title": "Try Something Else First",
          "description": "They want you to try a cheaper drug first (step therapy)."
        },
        "quantityLimits": {
          "title": "Quantity Limits",
          "description": "They say you're getting too much of this drug."
        },
        "generic": {
          "title": "Use the Generic",
          "description": "They want you to use a cheaper generic version."
        }
      },
      "remember": "Remember: A denial is NOT the final answer. It's just the first \"no\", and you can fight it!"
    },
    "stepTherapy": {
      "title": "Step Therapy: \"Try This First\"",
      "intro": "<strong>Step therapy</strong> means your insurance wants you to try a cheaper drug before they'll pay for the one your doctor picked. Some people call this \"fail first.\"",
      "risky": {
        "title": "Why This Can Be Risky",
        "items": [
          "Anti-rejection drugs need exact blood levels. Small changes matter a lot.",
          "Trying the wrong drug could hurt your transplant.",
          "Your transplant team picked your drug for good reasons.",
          "Switching means more blood tests and doctor visits."
        ]
      },
      "canDo": {
        "title": "What You Can Do",
        "items": [
          "You can ask them to skip step therapy.",
          "Transplant drugs often get approved when your doctor explains why.",
          "A letter from your transplant doctor helps a lot.",
          "Many states have laws that protect patients."
        ]
      },
      "proTip": "Pro Tip: Ask your doctor about \"step therapy exception\", they can request this on your behalf!"
    },
    "generics": {
      "title": "Generic vs. Brand Name Drugs",
      "basics": {
        "title": "The Basics",
        "items": [
          "Generic drugs have the same main ingredient as brand-name drugs.",
          "The FDA says they work the same way.",
          "For most drugs, generics are fine."
        ]
      },
      "different": {
        "title": "Why Transplant Drugs Are Different",
        "intro": "Anti-rejection drugs are special. Even tiny changes can cause problems:",
        "tooLittle": "<strong>Too little:</strong> Your body might reject the transplant",
        "tooMuch": "<strong>Too much:</strong> Side effects and toxicity"
      },
      "brandNeed": {
        "title": "When You Might Need Brand Name",
        "items": [
          "Your drug levels have been steady on this brand",
          "You had problems when you switched before",
          "Your transplant center requires brand-name",
          "Your body has trouble absorbing the drug"
        ]
      }
    },
    "howToAppeal": {
      "title": "How to Appeal: Step by Step"
    },
    "tracker": {
      "progressLabel": "Your Progress",
      "progress": "{{completed}} of {{total}} steps",
      "instruction": "Click each step when you've completed it to track your progress!",
      "tipLabel": "Tip:",
      "steps": [
        {
          "title": "Get It in Writing",
          "description": "Ask for a letter that says exactly why they said no. You need this to know what to say in your appeal.",
          "tips": "Call the number on your insurance card and ask for a 'written denial letter' or 'explanation of benefits.'"
        },
        {
          "title": "Watch the Clock",
          "description": "You have at least 60 days to appeal — your denial letter lists your exact deadline. Mark the date on your calendar. Don't miss it.",
          "tips": "Set a phone reminder 2 weeks before your deadline!"
        },
        {
          "title": "Get Your Papers Together",
          "description": "Ask your transplant team for: a letter from your doctor, your transplant records, recent lab results, and notes about why other drugs won't work.",
          "tips": "Use the letter builder below to help your doctor write a support letter!"
        },
        {
          "title": "Send Your Appeal",
          "description": "Send everything together. Keep copies of everything you send.",
          "tips": "Send by fax or certified mail so you have proof it was received."
        },
        {
          "title": "Ask for a Fast Review If You Need It",
          "description": "If you're going to run out of your medicine soon, ask for an expedited (fast) review. For transplant drugs, this almost always counts as urgent.",
          "tips": "Say 'I need an expedited appeal because running out of my transplant medication is life-threatening.'"
        },
        {
          "title": "Know What Comes Next",
          "description": "If they say no again, you can ask for an external review by someone outside the company. Your state insurance office can also help.",
          "tips": "Don't give up after the first appeal denial, external reviews often overturn decisions!"
        }
      ],
      "congratsTitle": "Congratulations! You've completed all the steps!",
      "congratsText": "You're well on your way to winning your appeal. Stay strong!"
    },
    "letter": {
      "title": "Write a Letter to Your Doctor",
      "subtitle": "Ask your doctor to help with your appeal",
      "why": "<strong>Why write to your doctor?</strong> Your doctor has the medical knowledge to explain why you need your specific medication. A letter of medical necessity from your doctor is often the key to winning an appeal.",
      "note": "<strong>Note:</strong> Your doctor already has access to a detailed medical necessity letter template. This letter is for <em>you</em> to request their help and provide them with the information they need.",
      "yourName": "Your Name",
      "yourNamePlaceholder": "Your full name",
      "doctorName": "Doctor's Name",
      "doctorNamePlaceholder": "e.g., Smith",
      "medicationName": "Medication Name",
      "medicationNamePlaceholder": "e.g., Tacrolimus, Prograf",
      "transplantType": "Transplant Type",
      "transplantTypePlaceholder": "e.g., Kidney, Liver, Heart",
      "transplantDate": "Transplant Date ",
      "transplantDatePlaceholder": "e.g., January 2023",
      "denialReason": "Denial Reason ",
      "denialReasonPlaceholder": "e.g., Not on formulary, Step therapy required",
      "additionalInfo": "Additional Information ",
      "additionalInfoPlaceholder": "Any other details that might help your doctor (e.g., previous drug reactions, why you need this specific medication, etc.)",
      "optional": "(optional)",
      "ifKnown": "(if known)",
      "blankPrompt": "Prefer not to type your details online? Download a blank letter to print and fill in by hand.",
      "blankButton": "Download blank letter",
      "englishNote": "Your letter is generated in English because it is addressed to your insurance company.",
      "generate": "Generate My Letter",
      "previewTitle": "Your Letter to Dr. ",
      "doctorFallback": "[Doctor's Name]",
      "copied": "Copied!",
      "copyText": "Copy Text",
      "nextStep": "<strong>Next step:</strong> Print this letter or email it to your doctor along with a copy of your denial letter."
    },
    "resources": {
      "title": "Free Downloadable Resources",
      "subtitle": "Print these guides to help with your appeal",
      "appealGuide": {
        "title": "Complete Appeal Guide",
        "description": "Step-by-step instructions, checklists, sample letters, and tips for winning your appeal. Written in plain language.",
        "items": [
          "Printable checklists",
          "Sample appeal letter template",
          "Phone scripts for calling insurance"
        ],
        "download": "Download Appeal Guide"
      },
      "doctorTemplate": {
        "title": "Doctor's Letter Template",
        "description": "Share this with your transplant doctor. A ready-to-use medical necessity letter template they can customize for your appeal.",
        "items": [
          "Professional medical format",
          "Clinical guideline references",
          "Fill-in-the-blank sections"
        ],
        "download": "Download Free Template"
      }
    },
    "spGuide": {
      "title": "Specialty Pharmacy Guide",
      "subtitle": "Know your rights and choices when your insurance makes you use a certain pharmacy.",
      "builder": {
        "title": "Specialty Pharmacy Appeal Letter Builder",
        "intro": "Fill in your details below to make a letter you can copy and send to your insurance company.",
        "yourName": "Your Name",
        "yourNamePlaceholder": "Your Name",
        "medicationName": "Medication Name",
        "medicationNamePlaceholder": "Medication Name",
        "reason": "Reason for Appeal",
        "reasonFinancial": "Financial Hardship",
        "reasonAccess": "Access Issues (Timing/Delivery)",
        "reasonClinical": "Clinical Stability (Already stable)",
        "generate": "Generate Letter",
        "generateAria": "Generate specialty pharmacy appeal letter",
        "preview": "Preview:",
        "copyAria": "Copy letter text to clipboard"
      },
      "howTo": {
        "title": "How to Appeal: A Step-by-Step Guide",
        "medicare": {
          "title": "Medicare Appeals",
          "note": "Important: Act on time.",
          "step1": "<strong>Coverage Decision:</strong> Ask your plan to pay for your choice of pharmacy.",
          "step2": "<strong>Level 1 (Second Look):</strong> If they say no, you have 65 days from the date on the denial letter to file your appeal.",
          "step3": "<strong>Level 2 (Outside Review):</strong> An outside group looks at your case if denied again.",
          "link": "Official Medicare Guide",
          "linkAria": "Visit Official Medicare Guide (opens in new tab)"
        },
        "medicaid": {
          "title": "Medicaid Appeals",
          "text": "Each state runs its own appeals. Call your state's Medicaid office."
        },
        "private": {
          "title": "Private Insurance",
          "text": "You can ask your company to look again. If they still say no, you can ask for an outside review."
        }
      }
    },
    "footer": {
      "title": "Remember: You Are Not Alone",
      "text": "Thousands of transplant patients successfully appeal their denials every year. Your health matters, and you have every right to fight for the medications you need. Stay strong and don't give up!"
    }
  },
  "medications": {
    "search": {
      "title": "Search Medications",
      "subtitle": "Search for your medications to build a shareable price list.",
      "safety": {
        "title": "Important Safety Note for Transplant Patients",
        "text": "Never switch from brand name to generic (or between different generic manufacturers) without your transplant team's approval. In transplant medicine, slight variations in bioavailability can cause organ rejection. <strong>\"Cheaper\" isn't always \"safe\"</strong> if the manufacturer changes.",
        "link": "Learn about generics vs. brand-name "
      },
      "inputLabel": "Search for medications",
      "placeholder": "Enter drug name (e.g. Prograf, Ozempic)...",
      "instructions": "Type medication name and press enter or click search button",
      "searchingAria": "Searching",
      "clearAria": "Clear search",
      "button": "Search",
      "loadingResultsAria": "Loading search results",
      "searchingText": "Searching medications...",
      "resultsAria": "Search results",
      "helpBanner": "Help is here, let's find it together. Enter your medications and we'll search for savings, patient assistance programs, grants, and affordable pharmacy options.",
      "resultsHeading": "Search Results",
      "alreadyAddedAria": "Already added",
      "added": "Added",
      "add": "Add",
      "noMatches": "No matches in our transplant database",
      "noMatchesHint": "Try a different spelling, or use the option below to add it as a custom medication.",
      "addCustomLabel": "Add \"{{term}}\" to list",
      "checkExternal": "Check price on external sites",
      "addCustom": "Add Custom",
      "addAsCustom": "Add \"{{term}}\" as custom medication",
      "emptyTitle": "Your list is empty",
      "emptyText": "Search above to add medications to your list, or pick one from the full list below. You can add standard transplant drugs or any other medication you take.",
      "browseAllTitle": "Browse all {{count}} medications, A to Z",
      "browseAllText": "Every medication in our database. Open one to see its prices and the programs that help pay for it."
    },
    "verify": {
      "title": "Your Medications",
      "subtitle": "Here are the medications you selected.",
      "printAria": "Print your medication list",
      "print": "Print",
      "alertTitle": "Please Verify Your Medications",
      "alertText": "Review the medication list below carefully. If you take other transplant medications not shown, add them using the search box.",
      "alertNote": "Accurate medication information helps us find the best savings options for you.",
      "addMoreLabel": "Add more medications",
      "addMorePlaceholder": "Add another medication...",
      "removeAria": "Remove {{name}}",
      "ready": {
        "title": "Ready to find savings?",
        "text": "Click the <strong>\"My Medication Savings\"</strong> button below to see where you can save money on each medication. We'll show you copay cards, patient assistance programs, discount pharmacies, and more."
      },
      "savingsButton": "My Medication Savings",
      "selectInsuranceFirst": "Please select your insurance type above to continue",
      "back": "Back to Medications",
      "options": {
        "title": "Your Options",
        "howToRead": "How to read this page",
        "subtitle": "Each card shows one of your medications. Click the tabs to see:",
        "assistance": "Assistance",
        "assistanceDesc": "Free medicine programs and copay cards",
        "price": "Price",
        "priceDesc": "Estimates vary by pills prescribed or dosage",
        "overview": "Overview",
        "overviewDesc": "Basic info about the medication",
        "print": "Print",
        "printDesc": "Print-friendly summary"
      },
      "fpl": {
        "aria": "Income eligibility reference",
        "title": "100% Federal Poverty Level (annual income)",
        "familyOf_one": "Family of {{size}}:",
        "familyOf_other": "Family of {{size}}:",
        "dontDisqualify": "Don't disqualify yourself",
        "papRange": ", Patient Assistance Programs (PAPs) often go up to 300%, 400%, or 500% of these amounts. If you're not sure, apply anyway — it costs nothing."
      },
      "help": {
        "title": "Need Help Applying for Assistance?",
        "text": "Learn how to fill out Patient Assistance Program applications step-by-step.",
        "aria": "Grants & Foundations: step-by-step application guide",
        "button": "Grants & Foundations"
      }
    },
    "insurance": {
      "heading": "What is your insurance type?",
      "text": "We need your insurance type to show the correct savings options. Copay cards are only available for commercial insurance, while other programs vary by coverage.",
      "selectAria": "Select {{label}} as your insurance type",
      "options": {
        "commercial": "Commercial / Employer",
        "medicare": "Medicare",
        "medicaid": "Medicaid (State)",
        "tricare_va": "TRICARE / VA",
        "ihs": "Indian Health Service",
        "uninsured": "Uninsured / Self-pay"
      }
    },
    "priceReport": {
      "errorInvalid": "Please enter a valid price greater than $0",
      "errorSaving": "Error saving price report. Please try again.",
      "title": "Report a Price",
      "via": "{{name}} via {{source}}",
      "closeAria": "Close dialog",
      "priceLabel": "Price Paid (USD) ",
      "locationLabel": "Pharmacy/Location (Optional)",
      "locationPlaceholder": "e.g., CVS in Seattle, WA",
      "dateLabel": "Date Purchased",
      "note": "Your report helps other transplant patients estimate costs. Data is stored locally on your device only.",
      "cancel": "Cancel",
      "submitting": "Submitting...",
      "submit": "Submit Report"
    },
    "card": {
      "cash": {
        "badge": "LOWEST CASH PRICE",
        "source": {
          "costplus": "Mark Cuban Cost Plus Drugs",
          "goodrx": "GoodRx discount price",
          "singlecare": "SingleCare discount price"
        },
        "text": "No manufacturer copay card applies to you, but the cash price is often lower than a copay. You do not need insurance to use it.",
        "compare": "Compare all pharmacy prices",
        "sourceTypical": "Typical cash price for this type of medication",
        "estimateTypical": "A typical range for this drug class, not a quote for this medication. Compare live prices before you buy.",
        "estimate": "Estimate only — your pharmacy's price may differ."
      },
      "perMo": "/mo",
      "trumpRxGuideHref": "/trumprx",
      "context": {
        "insuranceLabel": "Insurance:",
        "commercial": "Commercial / Employer",
        "medicare": "Medicare",
        "medicaid": "Medicaid",
        "tricare": "TRICARE",
        "ihs": "Indian Health Service",
        "uninsured": "Uninsured",
        "transplantLabel": "Transplant:",
        "stagePost1yr": "1+ years",
        "stageUnder1yr": "<1 year",
        "situationLabel": "Situation:",
        "struggling": "Struggling",
        "challenging": "Challenging",
        "managing": "Managing"
      },
      "header": {
        "lowCost": "Low Cost",
        "mediumCost": "Medium Cost",
        "highCost": "High Cost",
        "specialtyTier": "Specialty Tier",
        "tier": "Tier {{tier}}",
        "generic": "Generic",
        "genericAvailable": " Available",
        "readAloud": "Read Aloud",
        "removeAria": "Remove {{name}} from list",
        "removeTitle": "Remove from list"
      },
      "savings": {
        "title": "Your Potential Savings",
        "withoutHelp": "Without Help",
        "withCopayCard": "With Copay Card",
        "annual": "Annual Savings"
      },
      "withdrawn": "{{name}} was withdrawn from the US market in {{year}}. Ask your transplant team what to take instead.",
      "nextStep": {
        "label": "Best next step",
        "copay": "Ask for this medication's copay card. Most patients with commercial insurance pay $0-$10 a month.",
        "pap": "Apply to the manufacturer's Patient Assistance Program. It may provide this medication at no cost if you qualify.",
        "cash": "Compare cash prices. If you have insurance, cash payments don't count toward your deductible. Check both before you pay.",
        "medicaid": "Medicaid usually covers this medication. If it is denied, ask your transplant team about prior authorization or an appeal.",
        "foundation": "Look into foundation grants for help with medication costs."
      },
      "filters": {
        "label": "Filter:",
        "all": "All Options",
        "eligible": "✓ Eligible Only",
        "free": "Free Options",
        "under50": "Under $50"
      },
      "tabs": {
        "aria": "Information tabs for {{name}}",
        "assistance": "Assistance Programs",
        "price": "Price Estimates",
        "overview": "Overview",
        "print": "Print Summary"
      },
      "fallbacks": {
        "copayCardName": "{{manufacturer}} Copay Card",
        "papName": "{{manufacturer}} Patient Assistance",
        "brandCopayCard": "{{name}} Co-Pay Card",
        "manufacturerPap": "{{manufacturer}} Patient Assistance Program",
        "manufacturerPapShort": "{{manufacturer}} PAP",
        "visitManufacturerProgram": "Visit Manufacturer Program",
        "searchOnDrugsCom": "Search for Program on Drugs.com"
      },
      "overview": {
        "manufacturerLabel": "Manufacturer: ",
        "commonlyPrescribed": "Commonly prescribed for: ",
        "recipients": " recipients.",
        "stageLabel": "Stage: ",
        "tip": "<strong>Tip:</strong> Always verify with your doctor if you can switch between Brand and Generic versions.",
        "drugFactsAria": "Read full drug facts on Drugs.com for {{name}} (opens in new tab)",
        "drugFactsLink": "Read full drug facts on Drugs.com ",
        "pharmaciesTitle": "Pharmacies (Where to fill your prescription)",
        "thPharmacy": "Pharmacy",
        "thDescription": "Description",
        "costPlusDesc": "Online pharmacy with cost-plus pricing model; often lowest for generics",
        "trumpRxDesc": "Government-negotiated cash prices on 800+ brand-name and generic drugs; cash-pay only, does not count toward your deductible, some exclude Medicare/Medicaid. ",
        "trumpRxGuideLink": "See our transplant guide",
        "walmartDesc": "Consistently low prices; $4 generic list available",
        "costcoDesc": "Low markup; no membership required for pharmacy",
        "cvsDesc": "Wide availability; accepts most discount cards",
        "walgreensDesc": "Large national chain; accepts most discount cards",
        "krogerDesc": "Competitive pricing; convenient if you're already shopping",
        "discountToolsTitle": "Discount Tools (Compare prices & get coupons)",
        "thTool": "Tool",
        "goodRxDesc": "Coupons and price comparisons at 70,000+ pharmacies nationwide",
        "singleCareDesc": "Free discount card with prices at 35,000+ pharmacies",
        "rxSaverDesc": "Coupons and price comparisons; no sign-up required",
        "scriptSaveDesc": "Free savings card accepted at most U.S. pharmacies"
      },
      "assistance": {
        "takesGenericTitle": "You take the generic",
        "takesGenericText": "Generic medications don't have manufacturer copay cards, those are only for brand-name drugs. The good news: generics are usually much cheaper. Compare the cash prices below, especially <strong>Mark Cuban Cost Plus Drugs</strong>, GoodRx, and SingleCare.",
        "recommended": "RECOMMENDED FOR YOU",
        "eligible": "You're Eligible",
        "copayFor": "For commercial/employer insurance • Manufacturer program",
        "perMonth": "/month",
        "getCardAria": "Get Card: copay card for {{name}} (opens in new tab)",
        "getCard": "Get Card ",
        "linkVerified": "Link verified {{date}}",
        "incomeBased": "Income-Based",
        "checkEligibility": "Check eligibility with the program",
        "papNotEligible": "Not for your insurance",
        "papUseCopayInstead": "This program is for people on Medicare, Medicaid, or without insurance. With commercial or employer insurance, the copay card above is your route.",
        "papNotEligibleText": "This program is for people on Medicare, Medicaid, or without insurance. Programs vary, so it is still worth asking your transplant team what applies to you.",
        "papFor": "For Medicare, Medicaid, or no insurance \u2022 Income up to {{multiple}}\u00d7 the federal poverty level \u2014 about {{amount}} for one person",
        "free": "FREE",
        "ifEligible": "if eligible",
        "applyAria": "Apply for Patient Assistance for {{name}} (opens in new tab)",
        "apply": "Apply ",
        "foundations": "Foundations",
        "andGrants": " & Grants",
        "foundationsText": "These groups may help pay for your medicine. Check HealthWell and TotalAssist.",
        "fundFinderAria": "Check TotalAssist funds (opens in new tab)",
        "fundFinderLink": "Check TotalAssist Funds ",
        "partDTitle": "Medicare Part D Information",
        "partDText": "Learn about Medicare Part D coverage changes and savings options for {{name}}.",
        "partDAria": "View Details: Medicare Part D information for {{name}} (opens in new tab)",
        "viewDetails": "View Details ",
        "copayCommercialOnly": "Copay cards are for commercial/employer insurance only",
        "pricesNote": "Prices are for a 30 day supply. Your price may be different. Call the pharmacy for exact pricing.",
        "formsTitle": "Need Help Filling Out the Forms?",
        "formsText": "Our guide shows you step-by-step how to fill out the forms. We have checklists and examples to help you.",
        "viewGuideAria": "View Grants & Foundations: complete application guide",
        "viewGrants": "View Grants & Foundations"
      },
      "warning": {
        "title": "Important: Discount Cards Don't Help Your Insurance",
        "word": "Warning:",
        "pre": " Discount cards (GoodRx, SingleCare) or cash payments do ",
        "notCount": "NOT count toward your ",
        "deductible": "deductible",
        "notCountPost": " or out-of-pocket max.",
        "text2": "If you use a card too early, you might pay more money over the year. The card pays for you, but that money doesn't count. You still have to pay your full deductible later.",
        "usingInsurance": "✅ Using Insurance",
        "usingInsuranceDesc": "Pay more early → then $0 the rest of the year",
        "discountCards": "❌ Discount Cards & Cash",
        "discountCardsDesc": "Pay less each time → but pay all year long",
        "learnMore": "Learn more "
      },
      "price": {
        "guideTitle": "Price Options Guide",
        "legendCopay": "Copay Cards",
        "legendCopayDesc": "Best savings: $0-$10/mo",
        "legendPap": "Patient Assistance",
        "legendPapDesc": "Free if eligible",
        "legendDiscount": "Discount Cards",
        "legendCash": "Cash Price",
        "legendCashDesc": "Baseline comparison",
        "legendTrumpRxDesc": "Gov't discounted cash",
        "estimatesNotice": "<strong>These are estimates only.</strong> Your actual price may be different. Always check with the pharmacy for the real price.",
        "caption": "Price estimates for {{name}}",
        "thPharmacyTool": "Pharmacy / Tool",
        "thEstimatedPrice": "Estimated Price",
        "thAction": "Action",
        "copayForShort": "For commercial/employer insurance",
        "copayRange": "$0 - $10/month",
        "manufacturerProgram": "Manufacturer program",
        "viewCopayAria": "Get Card: view copay card details for {{name}}",
        "papForShort": "For Medicare, Medicaid, or no insurance",
        "incomeRules": "You must meet income rules",
        "viewPapAria": "Apply: view Patient Assistance details for {{name}}",
        "goodRxDesc": "Free coupons at 70,000+ pharmacies",
        "singleCareDesc": "Free card at 35,000+ pharmacies",
        "costPlusDesc": "Online pharmacy with low prices",
        "community": "Community: ${{min}}-${{max}} ({{num}} reports)",
        "estUpdated": "Est. updated {{date}}",
        "checkLiveGoodRxAria": "Check live price on GoodRx for {{name}} (opens in new tab)",
        "checkLiveSingleCareAria": "Check live price on SingleCare for {{name}} (opens in new tab)",
        "checkLiveCostPlusAria": "Check live price on Cost Plus Drugs (opens in new tab)",
        "checkLive": "Check Live ",
        "reportPrice": "Report Price",
        "trumpRxDesc": "Government discounted cash price",
        "trumpRxExclude": "May exclude Medicare/Medicaid",
        "ourGuideAria": "Our Guide: TrumpRx for transplant patients",
        "ourGuide": "Our Guide ",
        "visitTrumpRxAria": "Visit TrumpRx.gov (opens in new tab)",
        "footerNote": "These prices are for a 30-day supply. Your price may be different. Call the pharmacy to get your exact price. (Last updated: {{date}})",
        "communityNote": "<strong>Community prices</strong> are real prices that other people paid in the last 90 days. Help others by telling us what you paid!",
        "costPlusNote": "<strong>About Cost Plus Drugs:</strong> This is a cash pharmacy. What you pay there does not count toward your deductible. But the price might still be lower than your insurance copay. Compare before you decide!",
        "trumpRxNotePre": "<strong>About TrumpRx.gov:</strong> This is a cash-pay discount, it does NOT count toward your deductible or out-of-pocket max.",
        "trumpRxNoteMedicare": " Some TrumpRx coupons may exclude Medicare/Medicaid patients.",
        "trumpRxNoteCompare": " Always compare with your insurance copay first. ",
        "readFullGuide": "Read our full guide"
      },
      "print": {
        "detailsTitle": "Medication Details",
        "brandName": "Brand Name:",
        "genericName": "Generic Name:",
        "category": "Category:",
        "manufacturer": "Manufacturer:",
        "organs": "Organs:",
        "stage": "Stage:",
        "notAvailable": "N/A",
        "priceTitle": "Price Estimates",
        "copayCardFallback": "Manufacturer Copay Card",
        "papFallback": "Patient Assistance (PAP)",
        "freeIfEligible": "FREE (if eligible)",
        "assistanceTitle": "Assistance Programs",
        "copayAvailableFallback": "Copay Card Available",
        "copayEligibilityFallback": "For commercial/employer insurance only.",
        "papProgramFallback": "Patient Assistance Program (PAP)",
        "papEligibilityFallback": "For Medicare, Medicaid, uninsured, or underinsured patients.",
        "foundationGrants": "Foundation Grants",
        "foundationCheck": "Check HealthWell and TotalAssist.",
        "checklistTitle": "Action Checklist",
        "applyFor": "Apply for ",
        "checkFoundation": "Check foundation eligibility (HealthWell, TotalAssist)",
        "comparePrices": "Compare prices on GoodRx and SingleCare",
        "askTeam": "Ask transplant team about generic alternatives",
        "printButton": "Print This Summary"
      }
    },
    "external": {
      "label": "External / Custom Search",
      "note": "<strong>Note:</strong> This drug is not in our education database. Use the links below to find pricing directly.",
      "navAria": "External price check options for {{name}}",
      "checkCostPlusAria": "Check {{name}} price on Cost Plus Drugs (opens in new tab)",
      "checkGoodRxAria": "Check {{name}} price on GoodRx (opens in new tab)",
      "checkSingleCareAria": "Check {{name}} price on SingleCare (opens in new tab)",
      "searchDrugsAria": "Search \"{{name}}\" on Drugs.com for Assistance Programs (opens in new tab)",
      "searchDrugsLink": "Search \"{{name}}\" on Drugs.com for Assistance Programs "
    },
    "detail": {
      "meta": {
        "title": "How to Afford {{name}}: Copay Cards & Assistance | Transplant Medication Navigator™",
        "description": "Find copay cards, patient assistance programs, and foundation grants for {{name}}. See ways to lower the cost of your transplant medication.",
        "notFoundTitle": "Medication Not Found | Transplant Medication Navigator™",
        "notFoundDescription": "This medication page could not be found. Search our full list of transplant medications and assistance programs."
      },
      "priceRangeSingle": "{{price}}/month",
      "priceRangeBetween": "{{min}} to {{max}}/month",
      "notFound": {
        "title": "Medication not found",
        "text": "We couldn't find that medication. Search our full list to find copay cards and assistance programs.",
        "button": "Search medications"
      },
      "breadcrumbAria": "Breadcrumb",
      "home": "Home",
      "medications": "Medications",
      "heading": "How to Afford {{name}}",
      "introIs": " is ",
      "introUsedBy": " medication used by transplant patients",
      "introTail": ". Here are the ways to lower what you pay, from copay cards to programs that provide it for free.",
      "estPricePre": "Estimated discount cash price: ",
      "estPricePost": " (varies by pharmacy; your price may be lower with insurance or assistance).",
      "waysTitle": "Ways to save on {{name}}",
      "copayTitle": "Manufacturer copay card",
      "copayText": "For commercial or employer insurance. Copay cards can bring your cost down to as little as $0–$10 a month. Not valid with Medicare or Medicaid.",
      "copayLink": "Get the copay card ",
      "papTitle": "Patient Assistance Program (free medication)",
      "papText": "If you are uninsured, underinsured, or on Medicare/Medicaid and meet income limits, you may get {{name}} at no cost.",
      "papAsk": " Ask your transplant team about the manufacturer or foundation options for this medication.",
      "papLink": "Apply for assistance ",
      "grantsTitle": "Foundation grants",
      "grantsText": "Charitable foundations can help cover copays and other costs, based on income and diagnosis.",
      "grantsLink": "See grants & how to apply ",
      "genericTitle": "Generic version",
      "genericText": "A generic {{generic}} is available and usually costs far less. For anti-rejection drugs, stay on the same manufacturer and talk to your transplant team before switching.",
      "isGenericTitle": "You are already on the generic",
      "isGenericText": "{{generic}} is the generic of {{brand}}, and it usually costs far less. Generics have no manufacturer copay card, so compare cash prices below. For anti-rejection drugs, stay on the same manufacturer and talk to your transplant team before switching.",
      "isGenericNoBrandText": "{{generic}} is the generic, and it usually costs far less than a brand-name version. Generics have no manufacturer copay card, so compare cash prices below.",
      "genericLink": "Generics vs. brand-name: what to know",
      "discountTitle": "Discount cards & cash prices",
      "discountText": "Compare GoodRx, SingleCare, Amazon Pharmacy, and Mark Cuban Cost Plus Drugs. For some medications the cash price beats your insurance copay.",
      "discountLink": "Compare prices for {{name}} ",
      "ctaTitle": "Find out what you could pay for {{name}}",
      "ctaText": "Answer a few questions and we'll match you to the copay cards, assistance programs, and prices you qualify for.",
      "ctaQuiz": "Take the free 2-minute quiz ",
      "ctaSearch": "Search all medications",
      "faqTitle": "Common questions about {{name}}",
      "faq": {
        "copayQ": "Is there a copay card for {{name}}?",
        "copayA": "Yes. If you have commercial or employer insurance, the manufacturer copay card for {{name}} can lower your monthly cost, often to as little as $0–$10. Copay cards cannot be used with Medicare, Medicaid, or other government insurance.",
        "freeQ": "How can I get {{name}} for free?",
        "freeA": "If you are uninsured, underinsured, or on Medicare/Medicaid and meet income limits, a Patient Assistance Program (PAP) may provide {{name}} at no cost. Foundation grants can also cover your share of the cost. Take the free 2-minute quiz to see which programs you qualify for.",
        "genericQ": "Is there a generic for {{name}}?",
        "genericA": "Yes, a generic version of {{generic}} is available and usually costs much less. For transplant anti-rejection drugs, stay on the same manufacturer and talk to your transplant team before switching, because small changes can affect your drug levels.",
        "isGenericQ": "Is this the generic?",
        "isGenericA": "Yes. {{generic}} is the generic of {{brand}}, and it usually costs far less. For transplant anti-rejection drugs, stay on the same manufacturer and talk to your transplant team before switching, because small changes can affect your drug levels.",
        "isGenericNoBrandA": "Yes. {{generic}} is the generic, so there is no brand-name version to switch away from, and it usually costs far less. Talk to your transplant team before you change how or where you fill it.",
        "costQ": "How much does {{name}} cost?",
        "costA": "Cash prices vary by pharmacy, but an estimated discount price is around {{price}}. Your actual cost depends on your insurance, and copay cards or assistance programs can reduce it further."
      },
      "relatedPre": "Other ",
      "relatedPost": " medications",
      "genericTag": "Generic",
      "disclaimer": "Educational information only, not medical advice. Prices are estimates and vary by pharmacy and plan. Always talk to your transplant team before changing how or where you fill your medications."
    },
    "categories": {
      "Immunosuppressant": "Immunosuppressant",
      "Induction": "Induction",
      "Steroid": "Steroid",
      "Anti-viral": "Anti-viral",
      "Antibiotic": "Antibiotic",
      "Anti-fungal": "Anti-fungal",
      "Hepatitis B": "Hepatitis B",
      "Hepatitis C": "Hepatitis C",
      "Liver Support": "Liver Support",
      "Enzymes": "Enzymes",
      "Heart Failure": "Heart Failure",
      "Heart/Kidney Support": "Heart/Kidney Support",
      "Kidney Support": "Kidney Support",
      "Diuretic": "Diuretic",
      "Beta Blocker": "Beta Blocker",
      "Stomach Protection": "Stomach Protection",
      "Pulmonary Hypertension": "Pulmonary Hypertension",
      "Anemia (ESRD)": "Anemia (ESRD)",
      "Iron Supplement (ESRD)": "Iron Supplement (ESRD)",
      "Vitamin D (ESRD)": "Vitamin D (ESRD)",
      "Hyperparathyroidism (ESRD)": "Hyperparathyroidism (ESRD)",
      "Phosphate Binder (ESRD)": "Phosphate Binder (ESRD)",
      "Acute Rejection": "Acute Rejection",
      "Antibody-Mediated Rejection": "Antibody-Mediated Rejection",
      "ACE Inhibitor": "ACE Inhibitor",
      "ARB": "ARB",
      "Calcium Channel Blocker": "Calcium Channel Blocker",
      "Statin": "Statin",
      "Antiplatelet": "Antiplatelet",
      "Anticoagulant": "Anticoagulant",
      "Cardiac Glycoside": "Cardiac Glycoside",
      "Diabetes": "Diabetes",
      "Insulin": "Insulin",
      "Thyroid": "Thyroid",
      "Bronchodilator": "Bronchodilator",
      "Respiratory": "Respiratory",
      "Inhaled Corticosteroid": "Inhaled Corticosteroid",
      "Asthma Biologic": "Asthma Biologic",
      "Asthma Biologic / Allergy": "Asthma Biologic / Allergy",
      "Leukotriene Modifier": "Leukotriene Modifier",
      "Antihistamine": "Antihistamine",
      "Nasal Corticosteroid": "Nasal Corticosteroid",
      "Nasal Antihistamine": "Nasal Antihistamine",
      "Mast Cell Stabilizer": "Mast Cell Stabilizer",
      "Proton Pump Inhibitor": "Proton Pump Inhibitor",
      "H2 Blocker": "H2 Blocker",
      "Pain Relief": "Pain Relief",
      "Nerve Pain": "Nerve Pain",
      "NSAID": "NSAID",
      "Antidepressant": "Antidepressant",
      "Antidepressant/Sleep": "Antidepressant/Sleep",
      "Anxiety": "Anxiety",
      "Sleep Aid": "Sleep Aid",
      "Antipsychotic": "Antipsychotic",
      "Vitamin": "Vitamin",
      "Gout": "Gout",
      "Fatty Liver Disease (NASH)": "Fatty Liver Disease (NASH)",
      "GI Support": "GI Support",
      "Supplement": "Supplement",
      "Antiarrhythmic": "Antiarrhythmic",
      "Antiemetic": "Antiemetic",
      "Electrolyte": "Electrolyte",
      "Pulmonary Fibrosis": "Pulmonary Fibrosis",
      "Cystic Fibrosis": "Cystic Fibrosis",
      "Inhaled Antibiotic": "Inhaled Antibiotic",
      "Mucolytic": "Mucolytic",
      "Anemia": "Anemia",
      "Anticoagulation": "Anticoagulation",
      "Antifungals": "Antifungals",
      "Antivirals": "Antivirals",
      "Blood Pressure": "Blood Pressure",
      "Blood Support": "Blood Support",
      "Cholesterol": "Cholesterol",
      "Diuretics": "Diuretics",
      "GI / Acid Suppression": "GI / Acid Suppression",
      "Hepatitis B/C": "Hepatitis B/C",
      "Immunosuppressants": "Immunosuppressants",
      "Induction Agents": "Induction Agents",
      "Infection Prevention": "Infection Prevention",
      "Mental Health": "Mental Health",
      "Pain Management": "Pain Management",
      "Steroids": "Steroids"
    },
    "conditions": {
      "rejection-prevention": "Preventing Transplant Rejection",
      "rejection-treatment": "Treating Transplant Rejection",
      "infection": "Infection",
      "hepatitis": "Hepatitis B & C",
      "liver-disease": "Liver Disease",
      "kidney-disease": "Kidney Disease",
      "anemia": "Anemia",
      "low-white-cells": "Low White Blood Cell Count",
      "blood-clots": "Blood Clots",
      "high-blood-pressure": "High Blood Pressure",
      "heart-failure": "Heart Failure",
      "heart-rhythm": "Irregular Heartbeat",
      "high-cholesterol": "High Cholesterol",
      "fluid-retention": "Fluid Retention",
      "pulmonary-hypertension": "Pulmonary Hypertension",
      "asthma-copd": "Asthma & COPD",
      "cystic-fibrosis": "Cystic Fibrosis",
      "pulmonary-fibrosis": "Pulmonary Fibrosis",
      "allergies": "Allergies",
      "diabetes": "Diabetes",
      "thyroid": "Thyroid Disease",
      "gout": "Gout",
      "acid-reflux": "Acid Reflux & Ulcers",
      "digestive": "Digestive Symptoms",
      "pancreatic-insufficiency": "Pancreatic Insufficiency",
      "pain": "Pain",
      "mental-health": "Mental Health & Sleep",
      "nutrition": "Nutrition & Supplements",
      "electrolytes": "Electrolyte Imbalance"
    }
  },
  "savings": {
    "page": {
      "backLink": "My Medications",
      "backAria": "Go back to My Medications page",
      "title": "Savings Calculator",
      "subtitle": "See how much you could save with assistance programs",
      "tablistAria": "Savings options",
      "tabCalculator": "Estimate Savings",
      "tabTracker": "Track Actual Savings",
      "syncMessage_one": "Synced {{count}} pending entry",
      "syncMessage_other": "Synced {{count}} pending entries",
      "tipsTitle": "Tips for Tracking",
      "tip1": "Log each prescription fill as you pick it up",
      "tip2": "Include what you would have paid without assistance",
      "tip3": "Check your pharmacy receipt for the \"You Saved\" amount",
      "tip4": "Track different program types to see which save you most",
      "ctaTitle": "Want to save more?",
      "ctaText": "Use our medication search to find Patient Assistance Programs, foundation grants, and copay cards for your specific medications.",
      "ctaButton": "Search Medications"
    },
    "calculator": {
      "title": "Estimate Your Medication Savings",
      "intro": "Transplant medications are expensive, but you shouldn't have to overpay. Use our calculator to see how much you could save with assistance programs.",
      "listTitle": "Medication List",
      "listSubtitle": "Add your current prescriptions to calculate potential savings",
      "medicationLabel": "Medication {{number}}",
      "selectPlaceholder": "Select a medication...",
      "quantityLabel": "Qty/mo",
      "removeAria": "Remove {{name}} from list",
      "removeFallbackName": "medication",
      "medicationError": "Please select a medication",
      "quantityError": "Quantity must be between 1 and 12",
      "addButton": "Add Another Medication",
      "calculateButton": "Calculate My Savings",
      "annualSavingsLabel": "Estimated Annual Savings",
      "annualSavingsNote": "Estimated range from typical cash and discount prices, not a quote",
      "monthlyRetailLabel": "Monthly Retail Cost",
      "monthlyNewLabel": "New Monthly Cost",
      "caveatBold": "These are estimates, not a quote.",
      "caveatText": "Your real cost depends on your insurance, pharmacy, and dose.",
      "caveatRough": "Some medications use category averages because we don't have drug-specific pricing for them yet, so their range is wider.",
      "caveatSearch": "Search the assistance programs below to see your actual options.",
      "priceDataUpdated": "Price data last updated {{date}}.",
      "breakdownTitle": "Savings Breakdown",
      "perMonth": "/mo",
      "perYear": "/yr",
      "roughEstimate": "(rough estimate)",
      "unlockTitle": "Unlock These Savings",
      "unlockText": "Find the specific assistance programs, copay cards, and patient support for your medications.",
      "unlockButton": "Search Assistance Programs",
      "trustVerifiedTitle": "Verified Programs",
      "trustVerifiedText": "We only list verified patient assistance programs and manufacturer discounts.",
      "trustDataTitle": "Real Price Data",
      "trustDataText": "Our estimates use current NADAC and retail pricing data for accuracy.",
      "trustPatientTitle": "Patient First",
      "trustPatientText": "Created by transplant patients, for transplant patients. Always transparent."
    },
    "form": {
      "title": "Log Your Savings",
      "intro": "Did you just fill a prescription using an assistance program? Log it here to track your savings!",
      "anonymousBold": "Anonymous tracking:",
      "anonymousText": "Your data is stored locally on your device only.",
      "requiredSr": "(required)",
      "medicationNameLabel": "Medication Name",
      "medicationNamePlaceholder": "e.g., Tacrolimus, Eliquis",
      "programNameLabel": "Program Used",
      "programNamePlaceholder": "e.g., HealthWell Foundation",
      "programTypeLabel": "Program Type",
      "programTypePlaceholder": "Select type...",
      "originalPriceLabel": "Original Price",
      "originalPriceHint": "(without assistance)",
      "paidPriceLabel": "What You Paid",
      "paidPriceHint": "(after assistance)",
      "savedPreviewLabel": "You saved",
      "fillDateLabel": "Fill Date",
      "notesLabel": "Notes",
      "notesHint": "(optional)",
      "notesPlaceholder": "Any additional details...",
      "submitButton": "Log Savings",
      "submitLoading": "Saving...",
      "errorMedication": "Please enter a medication name",
      "errorOriginalPrice": "Please enter the original price",
      "errorPaidPrice": "Please enter what you paid (can be $0)",
      "errorPaidExceeds": "Paid price cannot exceed original price",
      "successMessage": "Saved ${{amount}}! Total savings: ${{total}}",
      "offlineWarning": "Failed to save. Entry stored locally and will sync later."
    },
    "dashboard": {
      "errorLoading": "Having trouble loading right now. Try again in a moment.",
      "tryAgain": "Try Again",
      "retry": "Retry",
      "deleteTitle": "Delete Entry",
      "deleteMessage": "Are you sure you want to delete this savings entry? This cannot be undone.",
      "deleteConfirm": "Delete",
      "deleteCancel": "Cancel",
      "deleteErrorTitle": "Error",
      "deleteErrorMessage": "Failed to delete entry. Please try again.",
      "shareText": "I've saved ${{total}} on my medications with Transplant Med Navigator! Track your savings too: transplantmednavigator.com",
      "shareCopiedTitle": "Copied!",
      "shareCopiedMessage": "Your savings summary has been copied to clipboard.",
      "shareCopiedOk": "OK",
      "totalTitle": "Your Total Savings",
      "refreshAria": "Refresh savings data",
      "refreshTitle": "Refresh",
      "loadingSr": "Loading...",
      "shareAria": "Share your savings on social media",
      "shareTitle": "Share your savings",
      "fills_one": "across {{count}} fill",
      "fills_other": "across {{count}} fills",
      "progressTo": "Progress to {{label}}",
      "milestone100": "First $100",
      "milestone500": "$500 Club",
      "milestone1000": "$1K Saved",
      "milestone2500": "$2.5K Hero",
      "milestone5000": "$5K Legend",
      "milestone10000": "$10K Champion",
      "avgPerFill": "Avg per fill",
      "medications": "Medications",
      "totalFills": "Total fills",
      "milestones": "Milestones",
      "monthlyTitle": "Monthly Savings",
      "byProgramTitle": "Savings by Program Type",
      "byProgramFills": "({{count}} fills)",
      "recentTitle": "Recent Savings",
      "hideEntries": "Hide entries",
      "showAll": "Show all ({{count}})",
      "deleteEntryAria": "Delete savings entry for {{name}}",
      "emptyTitle": "No savings logged yet",
      "emptyText": "Use the form above to log your first medication savings and start tracking your progress!"
    },
    "programTypes": {
      "copay_card": "Copay Card",
      "pap": "Patient Assistance Program",
      "foundation": "Foundation Grant",
      "discount_card": "Discount Card (GoodRx, etc.)",
      "negotiated_price": "Medicare Negotiated Price",
      "medicare_negotiated": "Medicare Negotiated Price",
      "generic": "Generic Switch",
      "other": "Other"
    }
  },
  "feedback": {
    "header": {
      "title": "Share Your Feedback",
      "subtitle": "Help us understand how this tool is working for patients"
    },
    "q1": {
      "title": "Did you find a program that helped?",
      "found_copay_card": "Yes - found a copay card",
      "found_pap": "Yes - found a patient assistance program (PAP)",
      "found_not_tried": "Found options, but haven't tried yet",
      "no_didnt_qualify": "No - didn't qualify for programs",
      "no_not_listed": "No - my medication wasn't listed"
    },
    "q2": {
      "title": "How much did this save you?",
      "subtitle": "Estimate your monthly savings from the program you found.",
      "unsure": "Not sure yet"
    },
    "rescue": {
      "title": "Let's get you covered today",
      "intro": "Anti-rejection medicine is not safe to skip or stretch. Your transplant center handles this all the time, and there are options that work the same day.",
      "step1": "<strong>Call your transplant team today.</strong> Ask for the transplant pharmacist or social worker. Tell them plainly that you are about to run out and cannot afford the refill.",
      "step2": "<strong>Ask your pharmacy for an emergency fill</strong> while you sort out the cost. Bring your pill bottle or the medicine name and dose.",
      "cta": "See how to get medication today",
      "continue": "Not now, finish the question",
      "stillTitle": "Still need medication today?",
      "stillText": "Do not skip or stretch doses while you wait. Here is how to get a bridge supply."
    },
    "q3": {
      "title": "What would you have done without this tool?",
      "paid_full": "Paid full price",
      "skipped_rationed": "Skipped or rationed doses",
      "called_coordinator": "Called my transplant coordinator",
      "not_filled": "Not filled the prescription",
      "other": "Other"
    },
    "q4": {
      "title": "Share Your Story (Optional)",
      "subtitle": "Your story can help other patients and improve the system. Feedback is anonymous — please don't include your name or contact information.",
      "commentLabel": "Your comment or experience",
      "commentPlaceholder": "Tell us about your experience finding medication assistance...",
      "submit": "Submit Feedback",
      "submitting": "Submitting...",
      "skip": "Skip & Submit"
    },
    "submitted": {
      "title": "Thank You!",
      "text": "Your feedback helps us improve and helps transplant centers understand patient needs.",
      "backHome": "Back to Home"
    },
    "privacyBold": "Privacy:",
    "privacyText": "Your feedback is anonymous unless you provide an email"
  },
  "legal": {
    "terms": {
      "title": "Terms and Conditions",
      "lastUpdated": "Last Updated: July 6, 2026",
      "intro1": "Welcome to the Transplant Medication Navigator website (the \"Website\"), operated by TransplantNav LLC (\"we,\" \"us,\" or \"our\"). These Terms and Conditions (\"Terms\") govern your access to and use of the Website and its content, which is intended to be an educational resource for transplant patients and their caregivers.",
      "intro2": "By accessing or using the Website, you agree to be bound by these Terms. If you do not agree to these Terms, you may not access or use the Website.",
      "s1Title": "1. No Medical Advice",
      "s1P1": "This tool provides educational information to help you navigate medication assistance options. It is not a substitute for professional medical advice. Always consult your transplant team or healthcare provider with any questions about your medical condition or treatment.",
      "s1P2": "Never disregard professional medical advice or delay in seeking it because of something you have read on this Website.",
      "s1Emergency": "If you think you may have a medical emergency, call your doctor or 911 immediately.",
      "s2Title": "2. Use of Information",
      "s2P1": "The Website provides information about patient assistance programs, copay cards, and other resources. We do not endorse, and are not responsible for, the accuracy or reliability of any information on the Website. We are not responsible for any decisions you make based on the information on the Website.",
      "s3Title": "3. Third-Party Links",
      "s3P1": "The Website may contain links to third-party websites or services that are not owned or controlled by TransplantNav LLC. We have no control over, and assume no responsibility for, the content, privacy policies, or practices of any third-party websites or services. You further acknowledge and agree that TransplantNav LLC shall not be responsible or liable, directly or indirectly, for any damage or loss caused or alleged to be caused by or in connection with the use of or reliance on any such content, goods or services available on or through any such websites or services.",
      "s4Title": "4. Intellectual Property",
      "s4P1": "The Website and its original content, features, and functionality are and will remain the exclusive property of TransplantNav LLC and its licensors. The Website is protected by copyright, trademark, and other laws of both the United States and foreign countries. Our trademarks and trade dress may not be used in connection with any product or service without the prior written consent of TransplantNav LLC.",
      "s5Title": "5. Limitation of Liability",
      "s5P1": "In no event shall TransplantNav LLC, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from (i) your access to or use of or inability to access or use the Website; (ii) any conduct or content of any third party on the Website; (iii) any content obtained from the Website; and (iv) unauthorized access, use, or alteration of your transmissions or content, whether based on warranty, contract, tort (including negligence), or any other legal theory, whether or not we have been informed of the possibility of such damage, and even if a remedy set forth herein is found to have failed of its essential purpose.",
      "s6Title": "6. Disclaimer",
      "s6P1": "Your use of the Website is at your sole risk. The Website is provided on an \"AS IS\" and \"AS AVAILABLE\" basis. The Website is provided without warranties of any kind, whether express or implied, including, but not limited to, implied warranties of merchantability, fitness for a particular purpose, non-infringement, or course of performance.",
      "s6P2": "TransplantNav LLC its subsidiaries, affiliates, and its licensors do not warrant that a) the Website will function uninterrupted, secure, or available at any particular time or location; b) any errors or defects will be corrected; c) the Website is free of viruses or other harmful components; or d) the results of using the Website will meet your requirements.",
      "s7Title": "7. Governing Law",
      "s7P1": "These Terms shall be governed and construed in accordance with the laws of the State of Texas and applicable federal law, without regard to conflict of law provisions.",
      "s7P2": "Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights. If any provision of these Terms is held to be invalid or unenforceable by a court, the remaining provisions of these Terms will remain in effect. These Terms constitute the entire agreement between us regarding our Website, and supersede and replace any prior agreements we might have between us regarding the Website.",
      "s8Title": "8. Changes",
      "s8P1": "We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material we will try to provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.",
      "s8P2": "By continuing to access or use our Website after those revisions become effective, you agree to be bound by the revised terms. If you do not agree to the new terms, please stop using the Website.",
      "s9Title": "9. Contact Us",
      "s9Pre": "If you have any questions about these Terms, please contact us at",
      "asideTitle": "Have Questions?",
      "asideText": "If you have any questions about these terms or how to use our site, we're here to help.",
      "asideFaq": "View FAQ",
      "asideHome": "Back to Home"
    },
    "privacy": {
      "title": "Privacy Policy",
      "lastUpdated": "Last Updated: July 6, 2026",
      "intro1": "TransplantNav LLC (\"we,\" \"us,\" or \"our\") operates the Transplant Medication Navigator website (the \"Website\"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our Website.",
      "intro2": "Please read this Privacy Policy carefully. By using the Website, you agree to the collection and use of information in accordance with this policy.",
      "s1Title": "1. Information We Collect",
      "s1Intro": "We designed the Website so that information about your health stays with you. Here is exactly what is collected and where it goes:",
      "cardDeviceTitle": "Information that stays on your device",
      "cardDeviceText": "Your My Path Quiz answers, saved medication lists, savings entries, and progress are stored only in your own browser (local storage). They are not sent to our servers. We do not require an account, and we do not collect your name, email address, or payment information.",
      "cardUsageTitle": "Anonymous usage data",
      "cardUsageText": "We count events like page views, quiz starts and completions, medication searches, and clicks on assistance-program links, along with anonymous answer counts (for example, how many visitors selected each insurance type). These counts contain no name, email, IP address, or other identifier, and our collection endpoint actively rejects any request containing identifying fields.",
      "cardFeedbackTitle": "Feedback, surveys, and price reports",
      "cardFeedbackText": "If you answer our feedback questions, surveys, or submit a community price report (medication, price paid, and pharmacy location), your responses are stored anonymously. We do not collect names, dates of birth, medical record numbers, or contact information with them, and we ask you not to include identifying details in free-text comments.",
      "cardEpicTitle": "Health record import (Epic / MyChart)",
      "cardEpicText": "If you choose to connect your health system account, your health system sends your active medication list through our secure server directly to your browser. We do not store your medication list, your record identifiers, or your access credentials on our servers. We record only that a login was completed at a given health system, with no information about who logged in.",
      "cardCookiesTitle": "Optional analytics cookies",
      "cardCookiesText": "Google Analytics runs only if you accept it in our consent banner. If you decline, or if your browser sends a Global Privacy Control signal, no Google Analytics script loads and no analytics cookies are set. You can change your choice anytime using the \"Privacy Choices\" link in the footer.",
      "s2Title": "2. How We Use Your Information",
      "s2Intro": "We use the information we collect in the following ways:",
      "s2Items": [
        "To provide, operate, and maintain the Website",
        "To improve the Website and understand which resources help patients most",
        "To share aggregate, anonymous statistics (never individual data) with transplant centers and partners who use the tool",
        "To comply with legal obligations"
      ],
      "s3Title": "3. Disclosure of Your Information",
      "s3Intro": "We may share your information in the following situations:",
      "s3ProvidersLabel": "Service Providers:",
      "s3ProvidersText": "The Website runs on the following providers, who process data on our behalf: Netlify (website hosting and serverless functions), Neon (database for anonymous usage data, feedback, and price reports), and Google Analytics (only if you accept analytics cookies). If you connect a health record, your own health system (for example, via Epic MyChart) is also involved in that exchange.",
      "s3LegalLabel": "Legal Requirements:",
      "s3LegalText": "We may disclose information if required by law or in response to valid requests by public authorities.",
      "s3TransfersLabel": "Business Transfers:",
      "s3TransfersText": "If we are involved in a merger, acquisition, or sale of assets, information may be transferred as part of that transaction.",
      "s3NoSell": "We do not sell your personal information, and we do not share it for advertising.",
      "s4Title": "4. Cookies and Tracking Technologies",
      "s4P1": "The only tracking cookies we use are the optional Google Analytics cookies described above, and they are off until you accept them in the consent banner. We honor the Global Privacy Control browser signal as a decline. The Website also uses your browser's local storage to save your own quiz progress and medication lists on your device; that information never leaves your browser and you can clear it at any time through your browser settings. You can change your analytics choice anytime via the \"Privacy Choices\" link in the footer.",
      "s5Title": "5. Data Security",
      "s5P1": "We use administrative, technical, and physical security measures to protect your personal information. While we have taken reasonable steps to secure the information you provide to us, please be aware that no security measures are perfect or impenetrable, and we cannot guarantee that your information will not be accessed, disclosed, altered, or destroyed.",
      "s6Title": "6. Data Retention",
      "s6P1": "Anonymous usage events, feedback responses, and community price reports are retained for up to 24 months, after which they are deleted or kept only as aggregate totals. Chat conversations are not stored in our systems. Information saved in your browser's local storage stays on your device until you clear it. We may retain information longer where necessary to comply with legal obligations or resolve disputes.",
      "s7Title": "7. Your Privacy Rights",
      "s7Intro": "Depending on where you live, state privacy laws (such as the California Consumer Privacy Act or Washington's My Health My Data Act) may give you rights over your personal information and consumer health data, including:",
      "s7AccessLabel": "Access:",
      "s7AccessText": "You may request access to the personal information we hold about you.",
      "s7CorrectionLabel": "Correction:",
      "s7CorrectionText": "You may request that we correct inaccurate or incomplete information.",
      "s7DeletionLabel": "Deletion:",
      "s7DeletionText": "You may request that we delete your personal information, subject to certain exceptions.",
      "s7OptOutLabel": "Opt-out of analytics:",
      "s7OptOutText": "Decline or withdraw analytics consent anytime via the \"Privacy Choices\" link in the footer, or by using a browser that sends the Global Privacy Control signal.",
      "s7P2": "Because we do not store your name, contact information, or account data, in most cases we hold nothing that can be linked to you. To exercise any of these rights or ask what we hold, contact us using the information below and we will respond within the time required by applicable law. You may also authorize an agent to submit a request for you, and we will not discriminate against you for exercising your rights.",
      "s8Title": "8. Health Information and HIPAA",
      "s8P1": "We are not a healthcare provider, health plan, or healthcare clearinghouse, so we are not a \"covered entity\" under HIPAA, and information you enter on this Website is not protected by HIPAA. Instead, it is protected by this Privacy Policy, by the design choices described above (health-related information stays on your device or is collected without identifiers), and by consumer-protection laws including the FTC Act and the FTC Health Breach Notification Rule. Your medical records at your hospital or clinic remain protected by HIPAA at those organizations; connecting your health record here is your choice and copies only your active medication list into your own browser.",
      "s9Title": "9. Third-Party Websites",
      "s9P1": "The Website may contain links to third-party websites and services that are not owned or controlled by us. We are not responsible for the privacy practices of these third parties. We encourage you to review the privacy policies of any third-party sites you visit.",
      "s10Title": "10. Children's Privacy",
      "s10P1": "The Website is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us so that we can delete such information.",
      "s11Title": "11. Changes to This Privacy Policy",
      "s11P1": "We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the \"Last Updated\" date. You are advised to review this Privacy Policy periodically for any changes.",
      "s12Title": "12. Contact Us",
      "s12Pre": "If you have any questions about this Privacy Policy or our privacy practices, please contact us at",
      "asideTitle": "Have Questions?",
      "asideText": "If you have any questions about our privacy practices or how we handle your data, we're here to help.",
      "asideTerms": "View Terms & Conditions",
      "asideHome": "Back to Home"
    },
    "a11y": {
      "title": "Accessibility & Section 504 Compliance",
      "lastUpdated": "Last Updated: February 12, 2026",
      "s504Title": "Section 504 Non-Discrimination Notice",
      "s504NoticePre": "In accordance with",
      "s504NoticeStrong": "Section 504 of the Rehabilitation Act of 1973",
      "s504NoticeMid": "and the",
      "s504NoticeLink": "U.S. Department of Health and Human Services (HHS) Final Rule",
      "s504NoticePost": ", TransplantNav LLC does not discriminate on the basis of disability in its programs, services, or activities. This website is designed to be accessible to all users, including people with disabilities.",
      "s504P1Pre": "We are committed to ensuring that our digital content meets the accessibility standards required by the HHS Section 504 final rule, which mandates conformance with the",
      "s504WcagLink": "Web Content Accessibility Guidelines (WCAG) 2.1 Level AA",
      "s504P1Post": "for patient-facing web content by May 11, 2026.",
      "commitTitle": "Our Commitment",
      "commitP1": "TransplantNav LLC is committed to making the Transplant Medication Navigator website accessible to everyone, including people with disabilities. We believe that all transplant patients deserve equal access to medication assistance information, regardless of ability.",
      "commitP2": "We work to follow the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA. These guidelines help make web content more usable for people with disabilities and better for everyone.",
      "confTitle": "Conformance Status",
      "confBadgeStrong": "Partially conformant",
      "confBadgeRest": "with WCAG 2.1 Level AA. \"Partially conformant\" means that some parts of the content do not fully meet the standard. We continuously work to maintain and improve accessibility across all features.",
      "confP1": "We performed a self-assessment of this website on February 12, 2026 and found that it meets the majority of WCAG 2.1 Level AA success criteria. As of July 6, 2026, we also run automated accessibility tests (axe-core, WCAG 2.1 A and AA rules) on our key pages before each release. No serious or critical problems are currently detected. Automated tests cannot catch every barrier. We continue manual reviews and welcome your reports. A summary of our findings is listed below.",
      "featTitle": "Accessibility Features",
      "featIntro": "This website includes the following accessibility features:",
      "featKeyboardTitle": "Keyboard Navigation",
      "featKeyboardText": "All interactive elements can be reached and used with a keyboard. Use <kbd>Tab</kbd> to move between items, <kbd>Enter</kbd> or <kbd>Space</kbd> to activate buttons, and <kbd>Escape</kbd> to close dialogs and tooltips.",
      "featScreenReaderTitle": "Screen Reader Support",
      "featScreenReaderText": "We use semantic HTML and ARIA attributes so screen readers can understand the page structure. We test with JAWS, NVDA, VoiceOver, and TalkBack.",
      "featRouteTitle": "Route Change Announcements",
      "featRouteText": "When you navigate to a new page, the page title is announced to screen readers through a live region. This ensures that screen reader users know when the page has changed.",
      "featSkipTitle": "Skip Navigation",
      "featSkipText": "A \"Skip to main content\" link appears at the top of each page when you press <kbd>Tab</kbd>. This lets keyboard users skip past the navigation menu.",
      "featContrastTitle": "Color and Contrast",
      "featContrastText": "Text colors meet WCAG AA contrast requirements (at least 4.5:1 for normal text and 3:1 for large text). We use a custom high-contrast color palette designed for transplant patients who may have vision changes from medications. Color is never the only way to convey information.",
      "featFocusTitle": "Focus Indicators",
      "featFocusText": "All interactive elements show a visible green outline when focused via keyboard. On dark backgrounds, the outline changes to white for better visibility.",
      "featMotionTitle": "Reduced Motion",
      "featMotionText": "We respect the \"prefers-reduced-motion\" system setting. If you have this enabled in your operating system, animations and transitions are turned off.",
      "featHcTitle": "High Contrast and Forced Colors",
      "featHcText": "We support the \"prefers-contrast\" media query and Windows High Contrast Mode (forced colors). Focus indicators, buttons, and form inputs adapt to your system theme.",
      "featSpacingTitle": "Text Spacing",
      "featSpacingText": "Content remains readable and functional when text spacing is adjusted by the user. Line height, letter spacing, word spacing, and paragraph spacing can be increased without loss of content or functionality (WCAG 1.4.12).",
      "featFormsTitle": "Forms",
      "featFormsText": "All form fields have visible labels. Required fields are clearly marked. Error messages are announced to screen readers.",
      "featTouchTitle": "Touch Targets",
      "featTouchText": "Buttons and links have a minimum size of 44 by 44 pixels, meeting WCAG 2.1 requirements. Most are 48 pixels for easier tapping on mobile devices.",
      "featPlainTitle": "Plain Language",
      "featPlainText": "Content is written at a 5th to 7th grade reading level. Medical terms are explained in plain language using inline tooltips with definitions.",
      "featSimpleTitle": "Larger Text Mode",
      "featSimpleText": "A \"Larger text\" toggle in the header increases font sizes, raises contrast, hides decorative elements, and simplifies the layout. The toggle shows whether larger text is on or off, so the current state is always visible.",
      "featReadTitle": "Read Aloud",
      "featReadText": "A \"Read Aloud\" button is available on key pages, using your browser's text-to-speech feature to read content out loud.",
      "techTitle": "Technical Details",
      "techIntro": "This website is built with React and uses semantic HTML5 elements. Accessibility features include:",
      "techItem1": "Landmark roles: <code>banner</code>, <code>navigation</code>, <code>main</code>, <code>contentinfo</code>",
      "techItem2": "ARIA attributes for dynamic content (<code>aria-live</code>, <code>aria-expanded</code>, <code>aria-pressed</code>, <code>aria-modal</code>)",
      "techItem3": "Focus management and focus trapping in modal dialogs",
      "techItem4": "Page title announcements on route changes via <code>aria-live</code> region",
      "techItem5": "Minimum 16px base font size, scalable with browser zoom up to 200%",
      "techItem6": "Text spacing support per WCAG 1.4.12 (content adapts to user-modified spacing)",
      "techItem7": "Responsive single-column reflow at narrow viewports (WCAG 1.4.10)",
      "techItem8": "Print stylesheet optimized for medication and program information",
      "compatTitle": "Compatibility",
      "compatIntro": "This website is designed to work with:",
      "compatItems": [
        "Screen readers: JAWS, NVDA, VoiceOver (macOS/iOS), TalkBack (Android)",
        "Screen magnification software",
        "Speech recognition software",
        "Keyboard-only navigation",
        "Browser zoom up to 200%",
        "Windows High Contrast Mode"
      ],
      "compatP2": "We recommend using the latest version of Chrome, Firefox, Safari, or Edge for the best experience.",
      "limitsTitle": "Known Limitations",
      "limitsIntro": "We know about the following accessibility issues and are working to fix them:",
      "limitsInfographicLabel": "Infographic images:",
      "limitsInfographicText": "Two informational graphics contain text that is not available as HTML. We plan to add long text descriptions.",
      "limitsThirdPartyLabel": "Third-party content:",
      "limitsThirdPartyText": "Some embedded content from external services may not fully meet accessibility standards.",
      "limitsP2": "If any of these issues affect your use of the site, please contact us and we will provide the information in an alternative format.",
      "testTitle": "How We Test",
      "testIntro": "We assess accessibility through:",
      "testItems": [
        "Self-evaluation against WCAG 2.1 Level AA success criteria",
        "Automated testing with Lighthouse and axe-core",
        "Manual keyboard-only navigation testing",
        "Screen reader testing with NVDA and VoiceOver",
        "Color contrast analysis of all text and interactive elements",
        "Reading level checks using Flesch-Kincaid tools",
        "Feedback from users, including people with disabilities"
      ],
      "testP2": "Our most recent assessment was completed on February 12, 2026.",
      "grievTitle": "Section 504 Grievance Procedure",
      "grievIntro": "TransplantNav LLC has adopted an internal grievance procedure for the prompt and equitable resolution of complaints alleging any action prohibited by Section 504 of the Rehabilitation Act of 1973.",
      "grievFileTitle": "How to File a Grievance",
      "grievFileText": "Any person who believes they have been subjected to discrimination on the basis of disability may file a grievance. Grievances must be submitted in writing within 60 days of the date the person filing the grievance becomes aware of the alleged discriminatory action.",
      "grievSubmitTitle": "Submit Your Grievance To",
      "grievCoordinator": "Section 504 Coordinator",
      "grievCompany": "TransplantNav LLC",
      "grievEmailLabel": "Email:",
      "grievResTitle": "Resolution Process",
      "grievResSteps": [
        "A written acknowledgment of your grievance will be sent within 5 business days.",
        "An investigation will be conducted, which may include interviews and review of relevant documents.",
        "A written determination will be issued within 30 days of receiving the grievance.",
        "If you are not satisfied with the resolution, you may appeal within 15 days of receiving the determination."
      ],
      "grievAltTitle": "Alternative Formats",
      "grievAltText": "This grievance procedure is available in alternative formats upon request. If you need this information in a different format due to a disability, please contact us and we will provide it promptly.",
      "fbTitle": "Feedback",
      "fbIntro": "We welcome your feedback on the accessibility of this website. If you find a barrier or have a suggestion, please contact us:",
      "fbEmailLabel": "Email:",
      "fbSubjectLabel": "Subject Line:",
      "fbSubjectValue": "\"Accessibility Feedback\"",
      "fbHelpIntro": "When reporting an issue, it helps if you include:",
      "fbHelpItems": [
        "The web page address (URL) where the issue happened",
        "What you were trying to do",
        "What happened instead",
        "The assistive technology you use (if any)",
        "Your browser and operating system"
      ],
      "fbResponse": "We aim to respond to accessibility feedback within 5 business days and will work to fix valid issues promptly.",
      "extTitle": "External Complaints",
      "extP1Pre": "If you are not satisfied with our response to your accessibility feedback or Section 504 grievance, you may file a complaint with the",
      "extP1Link": "U.S. Department of Health and Human Services, Office for Civil Rights",
      "extP1Post": ". Complaints must generally be filed within 180 days of the alleged discriminatory act.",
      "extOcr": "HHS Office for Civil Rights",
      "extPhoneLabel": "Phone:",
      "extTddLabel": "TDD:",
      "extOnlineLabel": "Online:",
      "extPortal": "HHS OCR Complaint Portal",
      "asideTitle": "Need Assistance?",
      "asideText": "If you need help accessing any content on this website or have accessibility concerns, we are here to help.",
      "asideContact": "Contact Us",
      "asideHome": "Back to Home"
    }
  },
  "privacyNotice": {
    "title": "Before you continue",
    "epic": "If you connect your health system account, your health system sends your current medication list through our secure server directly to your browser. We do not store your medication list, your record identifiers, or your login credentials on our servers — we record only that a login was completed at a given health system, not who logged in. The imported list is saved on this device only, and you can remove it at any time by deleting medications or clearing your browser data. Only include information that is needed.",
    "appeals": "The details you type here (your name, medication, transplant type and date, and denial details) are used only on this device to build your letter. They are not sent to or stored on our servers. Do not include information that is not needed for the letter.",
    "policyLink": "Read our full Privacy Policy"
  },
  "epic": {
    "title": "Connect to My Health System",
    "description": "Securely connect to your health system to automatically import your current medications. This saves time and helps us find the right assistance programs for you.",
    "errorSelectFirst": "Please select your health system first.",
    "errorInvalidSelection": "Invalid health system selection.",
    "errorNotConfigured": "Epic integration is not configured correctly:",
    "errorConnect": "Could not connect to your health system. Please try again.",
    "errorConnectNetwork": "Could not connect to your health system. Please check your connection and try again.",
    "imported_one": "Imported {{count}} medication from your health system.",
    "imported_other": "Imported {{count}} medications from your health system.",
    "unmatchedIntro_one": "We could not match {{count}} of your medications. If it is a transplant medication, please search for it below and add it by hand:",
    "unmatchedIntro_other": "We could not match {{count}} of your medications. If any of these are transplant medications, please search for them below and add them by hand:",
    "programsFound_one": "{{count}} assistance program found for your medications.",
    "programsFound_other": "{{count}} assistance programs found for your medications.",
    "doseNote": "Copay card and patient assistance searches are based on the medication name, not the dose or strength (mg).",
    "notInDb_one": "{{count}} medication from your chart is not in our transplant assistance database yet, you'll need to look this one up on your own:",
    "notInDb_other": "{{count}} medications from your chart are not in our transplant assistance database yet, you'll need to look these up on your own:",
    "notInDbHint": "We don't have copay card or patient assistance details for these. Ask your pharmacist, or check the manufacturer's website for a copay card or patient assistance program.",
    "selectorLabel": "Select Your Health System",
    "selectorHelp": "Start typing your hospital or health system name, close spellings still work, so don't worry if you're not sure how to spell it.",
    "searchPlaceholder": "Search for your hospital or health system...",
    "noMatches": "No matching health systems found. Contact us to add yours.",
    "moreResults": "{{count}} more, keep typing to narrow your search.",
    "sandboxTag": "(Testing Only)",
    "connectButton": "Connect to My Health System",
    "connecting": "Connecting...",
    "connectAria": "Connect to your health system to import your medications",
    "securityNote": "Your data is transmitted securely using PKCE. We do not store your login credentials."
  },
  "myMeds": {
    "privacyTitle": "Your data stays on this device",
    "privacyText": "Medications are stored in your browser only. They are not sent to any server. Clearing your browser data will remove this list. Use Export to save a backup.",
    "calcTitle": "Savings Calculator",
    "calcSubtitle": "See how much you could save",
    "remindersTitle": "Copay Card Reminders",
    "remindersSubtitle": "Never miss a renewal",
    "title": "My Medications",
    "subtitle": "Track your transplant medications and renewal dates",
    "export": "Export",
    "exportTitle": "Export medications",
    "import": "Import",
    "formReady": "Form ready",
    "addTitle": "Add a Medication",
    "requiredSr": "(required)",
    "optional": "(optional)",
    "nameLabel": "Medication name",
    "namePlaceholder": "e.g., Tacrolimus",
    "nameHint": "Enter the generic or brand name of your medication",
    "brandLabel": "Brand name",
    "brandPlaceholder": "e.g., Prograf",
    "dosageLabel": "Dosage",
    "dosagePlaceholder": "e.g., 1mg twice daily",
    "costLabel": "Monthly cost ($)",
    "costPlaceholder": "e.g., 150.00",
    "costHint": "Enter the amount in US dollars",
    "renewalLabel": "Renewal date",
    "renewalTypeLabel": "Renewal type",
    "renewalTypeSelect": "-- Select --",
    "renewalCalendarOption": "Calendar year (Jan 1)",
    "renewalAnniversaryOption": "Enrollment anniversary",
    "renewalMaxOption": "After max benefit reached",
    "renewalCalendar": "Calendar year",
    "renewalAnniversary": "Enrollment anniversary",
    "renewalMax": "After max reached",
    "saveButton": "Save Medication",
    "errorName": "Please enter a medication name",
    "savedMessage": "Medication saved!",
    "imported_one": "Imported {{count}} medication(s)",
    "imported_other": "Imported {{count}} medication(s)",
    "errorInvalidFile": "Invalid file format",
    "errorReadFile": "Error reading file",
    "insuranceQuestion": "Do you have commercial insurance?",
    "insuranceHelp": "Commercial insurance is coverage you get through an employer, the ACA marketplace, or that you buy privately, not Medicare, Medicaid, or TRICARE. Copay cards are only available with commercial insurance, so this helps us show you the right savings programs.",
    "insuranceAria": "Commercial insurance status",
    "yes": "Yes",
    "no": "No",
    "insuranceYesBold": "Copay cards can help!",
    "insuranceYesText": "With commercial insurance, manufacturer copay cards can lower your cost to $0-$50/month for many transplant medications.",
    "insuranceNoBold": "Patient Assistance Programs (PAPs) may help.",
    "insuranceNoText": "Drug manufacturers offer free medication programs for patients who qualify based on income. Foundations may also help with copays.",
    "listTitle": "Your Medications",
    "answerPrompt": "Answer the insurance question above to see savings programs for your medications.",
    "emptyList": "No medications saved yet.",
    "brandField": "Brand:",
    "dosageField": "Dosage:",
    "costField": "Monthly cost:",
    "renewalField": "Renewal:",
    "typeField": "Type:",
    "deleteAria": "Delete {{name}}",
    "deleteTitle": "Delete Medication",
    "deleteMessage": "Are you sure you want to delete this medication from your list?",
    "deleteConfirm": "Delete",
    "deleteCancel": "Cancel",
    "copayAvailable": "Copay Card Programs Available",
    "papAvailable": "Patient Assistance Programs Available",
    "phoneLabel": "Phone:",
    "incomeLimitLabel": "Income limit:",
    "apply": "Apply",
    "assistanceTitle": "Assistance Programs",
    "noPapText": "No specific manufacturer PAP found for this medication. Check these foundations that may help:",
    "healthwellDesc": "- copay and premium assistance",
    "totalAssistDesc": "- nearly 150 funds for copays and treatment costs",
    "copayCardsTitle": "Copay Cards",
    "noCopayPre": "No specific copay card found for this medication name. Try searching with the exact brand name, or visit",
    "noCopayLink": "PhRMA Patient Assistance",
    "noCopayPost": "to search for available programs.",
    "adherenceQuestion": "Did you get your medication today?",
    "adherenceAria": "Medication adherence check-in",
    "adherenceYes": "Yes",
    "adherenceNoExpensive": "No – still too expensive",
    "adherenceNoPharmacy": "No – will try another pharmacy",
    "adherenceNoOther": "No – other reason",
    "adherenceYesText": "Great! Staying on track with your medications is one of the most important things you can do for your transplant health.",
    "adherenceExpensiveText": "Missing doses can put your transplant at risk. Cost should not be the reason — here is where to get help:",
    "adherenceSave": "See ways to save",
    "adherenceApply": "Help applying for assistance",
    "adherenceFind": "Find programs for my meds",
    "adherenceExpensiveNote": "The copay cards and assistance programs for your medications are also listed with each one above. If you are about to run out, call your transplant team's pharmacist or social worker today.",
    "adherenceOtherText": "Missing doses can put your transplant at risk. If cost is a barrier, check the assistance programs listed with your medications above or talk to your transplant team."
  },
  "share": {
    "prompt": "Know another transplant patient who is paying too much?",
    "button": "Send this to someone",
    "copied": "Link copied",
    "copiedAnnounce": "Link and message copied to your clipboard.",
    "title": "Transplant Medication Navigator",
    "message": "Free help paying for transplant medications: copay cards, assistance programs, and what your prescription should really cost. No account needed."
  },
  "widgets": {
    "readAloud": {
      "label": "Read Aloud",
      "stop": "Stop",
      "stopAria": "Stop reading aloud"
    },
    "feedbackThanks": "Your feedback helps transplant centers understand patient needs.",
    "chat": {
      "tabChat": "Chat",
      "tabQuiz": "My Path Quiz",
      "openAria": "Open medication assistance",
      "launcherContinue": "Continue where you left off",
      "launcherHelp": "Need help with medication costs?",
      "dialogAria": "Medication assistance",
      "headerTitle": "Medication Navigator",
      "headerSubtitle": "Automated guide · Find assistance programs",
      "startOver": "Start over",
      "close": "Close",
      "fallbackGreeting": "Hi! I'm your Medication Navigator. I'll help you find assistance programs for your transplant medications. Let's get started!\n\nFirst, who am I helping today?",
      "resultsError": "Sorry, I had trouble finding programs. Please try again.",
      "resultsErrorMessage": "I apologize, but I'm having trouble connecting to our database right now. Please try again in a moment, or visit our Resources page to browse assistance programs manually.",
      "freeTextError": "I'm sorry, I couldn't process that. Please try selecting from the options above, or ask your question again.",
      "finding": "Finding the best options for you...",
      "generalAssistanceFull": "General assistance (no specific medication)",
      "generalAssistance": "General assistance",
      "selectedMeds": "Selected Medications ({{count}}):",
      "selectedMedsAria": "Selected medications",
      "removeMedAria": "Remove {{name}} from selection",
      "searchLabel": "Search for a medication",
      "searchPlaceholderAdd": "Add another medication...",
      "searchPlaceholderType": "Type medication name...",
      "searchingSr": "Searching for medications...",
      "resultsAria": "Medication search results",
      "resultsCount_one": "{{count}} medication found",
      "resultsCount_other": "{{count}} medications found",
      "addedTag": "Added",
      "customAddTitle": "Add \"{{name}}\"",
      "customAddDesc": "Add this medication to your list",
      "customEntry": "Custom entry",
      "continueWith_one": "Continue with {{count}} medication",
      "continueWith_other": "Continue with {{count}} medications",
      "skipTitle": "Skip this step",
      "skipDesc": "Show general assistance programs instead",
      "introTitle": "Find Your Medication Pathway",
      "introSubtitle": "Answer a few quick questions and we'll show you assistance programs you may qualify for.",
      "introDiscover": "What you'll discover:",
      "introItem1": "Copay assistance and savings cards",
      "introItem2": "Patient assistance programs (free meds)",
      "introItem3": "Foundation grants for out-of-pocket costs",
      "introItem4": "Low-cost pharmacy alternatives",
      "introPrivacy": "Your answers stay on your device. No account needed.",
      "introStart": "Start My Path Quiz",
      "introTime": "Takes about 2 minutes • {{count}} simple questions",
      "progressSr": "Question {{current}} of {{total}}. {{percent}}% complete.",
      "progressLabel": "Question {{current}} of {{total}}",
      "progressPercent": "{{percent}}% complete",
      "progressAria": "Quiz progress: {{percent}}% complete",
      "urgentTag": "Urgent",
      "helpfulTip": "Helpful Tip",
      "previousQuestion": "Previous question",
      "previousAria": "Go back to question {{number}}",
      "kbdNavigate": "to navigate",
      "kbdSelect": "to select",
      "resultsTitle": "Your Personalized Results",
      "resultsSubtitle": "Based on your profile, here are programs you may qualify for",
      "profileTitle": "Your Profile",
      "profileInsurance": "Insurance:",
      "profileStage": "Stage:",
      "profileOrgan": "Organ:",
      "profileCost": "Cost:",
      "lowCostBadge": "Low Cost",
      "discountCardBadge": "Discount Card",
      "benefitLabel": "Benefit:",
      "costPlusBenefit": "Wholesale cost + 15% + $5 pharmacy fee",
      "costPlusDesc": "Mark Cuban's transparent pricing pharmacy - often 50-90% less than retail",
      "costPlusDescShort": "Low-cost transparent pricing",
      "goodrxDesc": "Free coupons accepted at 70,000+ pharmacies, often 50-80% off retail",
      "goodrxDescShort": "Free coupons at 70,000+ pharmacies",
      "singlecareDesc": "Free card at 35,000+ pharmacies, compare and save on generic prescriptions",
      "singlecareDescShort": "Free card at 35,000+ pharmacies",
      "checkPrice": "Check Price",
      "apply": "Apply",
      "badgeCopay": "Copay Card",
      "badgePap": "Free Meds",
      "badgeFoundation": "Foundation",
      "foundationTip": "💡 Tip: Funds open throughout the year, check back if currently closed!",
      "contactSocialWorker": "Contact your transplant center social worker for assistance options.",
      "contactCenterShort": "Contact your transplant center.",
      "noPrograms": "No specific programs found. Contact your transplant center for assistance.",
      "printTitle": "Medication Assistance Action Plan",
      "printGenerated": "Generated:",
      "printProfile": "Your Profile",
      "printInsurance": "Insurance:",
      "printStage": "Transplant Stage:",
      "printOrgan": "Organ Type:",
      "printNotSpecified": "Not specified",
      "printRecommended": "Recommended Programs",
      "printBenefit": "Benefit:",
      "printEligibility": "Eligibility:",
      "printApply": "Apply:",
      "printContactSw": "Contact your transplant center social worker for assistance.",
      "printFooter": "Generated by TransplantMedicationNavigator.com",
      "popupBlockedTitle": "Popup blocked",
      "popupBlockedText": "Please allow popups in your browser settings to print your action plan.",
      "dismissAria": "Dismiss warning",
      "printButton": "Print Action Plan",
      "newSearch": "Start New Search",
      "goToWizard": "Start My Path Quiz",
      "inputPlaceholder": "Or type a question...",
      "sendAria": "Send message",
      "switchToChat": "Switch to Chat mode to ask questions",
      "disclaimer": "AI assistant: educational information only, not medical advice. It can make mistakes — confirm details with your transplant team. Please don’t include your name or contact info."
    }
  },
  "survey": {
    "landing": {
      "title": "Share Your Journey",
      "subtitle": "Your experience can change the system",
      "transplantTitle": "Transplant Journey Survey",
      "transplantText": "For transplant patients dealing with anti-rejection drugs, pharmacies, and insurance.",
      "transplantMeta": "8 topic areas - complete only the ones relevant to you",
      "transplantStart": "Start Transplant Survey",
      "generalTitle": "General Medication Survey",
      "generalText": "For anyone who has trouble getting or paying for medicine.",
      "generalMeta": "5 topic areas - takes about 5 minutes",
      "generalStart": "Start General Survey",
      "whyTitle": "Why We're Asking",
      "whyQuote": "\"When you share your story, you help other patients. Together, we are building a guide that we wish we had. Your voice helps us fix a broken system.\"",
      "whyAttribution": "— Lorrinda, TRIO President & Liver Transplant Recipient",
      "privacyLabel": "Privacy:",
      "privacyText": "No names, dates, or medical records collected"
    },
    "common": {
      "yes": "Yes",
      "no": "No",
      "na": "N/A",
      "intro": "Choose the topics that matter most to you. Each section can be submitted independently.",
      "completedCount": "{{completed}} of {{total}} sections completed",
      "thankYou": "- Thank you for sharing your experience!",
      "submitted": "✓ Submitted",
      "questionCount": "{{count}} questions",
      "answeredCount": "({{count}} answered)",
      "backToTopicsAria": "Back to topics",
      "backToTopics": "← Back to Topics",
      "update": "Update Responses ✓",
      "submitSection": "Submit This Section ✓",
      "privacyTitle": "Your privacy is protected:",
      "privacyLine1": "No names, dates, or medical record numbers are collected.",
      "privacyLine2": "All responses are anonymous and help us advocate for better medication access.",
      "poweredBy": "Powered by TransplantMedicationNavigator.com, By patients, for patients"
    },
    "transplant": {
      "title": "Transplant Medication Journey Survey",
      "sections": {
        "discharge": {
          "title": "Leaving the Hospital",
          "description": "What happened when you went home after your transplant",
          "q": {
            "meds_in_hand_at_discharge": {
              "label": "Did you leave the hospital with all your medicines?"
            },
            "discharge_med_education": {
              "label": "How well did the hospital teach you about your medicines?",
              "options": [
                "Great - I felt ready",
                "Good - I had most info I needed",
                "OK - There were gaps",
                "Poor - I felt unprepared",
                "None - I figured it out myself"
              ]
            },
            "knew_costs_before_discharge": {
              "label": "Did you know what your medicines would cost before you left?"
            },
            "social_worker_met": {
              "label": "Did a social worker talk to you about medicine costs?"
            },
            "first_refill_gap": {
              "label": "Did you run out of medicine before your first refill?"
            }
          }
        },
        "immunosuppressants": {
          "title": "Anti-Rejection Drugs",
          "description": "Problems with your anti-rejection medicines",
          "q": {
            "generic_switch_forced": {
              "label": "Were you ever switched to a different brand without being asked?"
            },
            "generic_switch_problems": {
              "label": "If switched, did you have problems with blood levels or side effects?"
            },
            "trough_timing_issues": {
              "label": "Have you had trouble timing your blood tests with your medicine?"
            },
            "dose_change_delay": {
              "label": "After your dose changed, how long until you got the new prescription?",
              "options": [
                "Same day",
                "1-2 days",
                "3-5 days",
                "More than 5 days",
                "Had to use old dose while waiting"
              ]
            },
            "ever_missed_immunosuppressant": {
              "label": "Have you ever missed a dose because you could not get your medicine (not because you forgot)?"
            },
            "missed_reason": {
              "label": "If yes, what was the main reason?",
              "options": [
                "Cost",
                "Pharmacy delay",
                "Insurance approval needed",
                "Out of stock",
                "Shipping issue",
                "Insurance said no",
                "Does not apply"
              ]
            }
          }
        },
        "specialty": {
          "title": "Specialty Pharmacy",
          "description": "Your experience with mail-order or specialty pharmacies",
          "q": {
            "uses_specialty_pharmacy": {
              "label": "Do you get any transplant drugs from a specialty pharmacy?"
            },
            "specialty_choice": {
              "label": "Did you get to pick your specialty pharmacy?",
              "options": [
                "Yes, I chose",
                "No, insurance picked it",
                "No, transplant center picked it",
                "Not sure",
                "I do not use one"
              ]
            },
            "specialty_shipping_issues": {
              "label": "Have you had problems with shipping delays or drugs arriving too hot or cold?"
            },
            "specialty_refill_calls": {
              "label": "How many calls does it take to refill your medicine?",
              "options": [
                "None - it is automatic",
                "1 call",
                "2-3 calls",
                "4+ calls",
                "I do not use one"
              ]
            },
            "specialty_coordinator_helpful": {
              "label": "If you have a pharmacy helper, how helpful are they?",
              "options": [
                "Very helpful",
                "Somewhat helpful",
                "Not helpful",
                "Never talked to one",
                "I do not have one"
              ]
            }
          }
        },
        "insurance": {
          "title": "Insurance & Coverage",
          "description": "Insurance problems after your transplant",
          "q": {
            "insurance_changed_post_tx": {
              "label": "Has your insurance changed since your transplant?"
            },
            "coverage_gap_experienced": {
              "label": "Have you had a time with no insurance after your transplant?"
            },
            "medicare_36_month_aware": {
              "label": "Did you know that Medicare used to stop covering anti-rejection drugs after 36 months?",
              "options": [
                "Yes, it affected me",
                "Yes, but did not affect me",
                "No, I did not know",
                "I am not on Medicare"
              ]
            },
            "prior_auth_immunosuppressants": {
              "label": "Has your insurance ever made you get approval before filling your anti-rejection drugs?"
            },
            "step_therapy_post_tx": {
              "label": "Has insurance ever made you try a different drug than what your doctor wanted?"
            },
            "annual_oop_range": {
              "label": "About how much do you pay each year for transplant drugs (out of your pocket)?",
              "options": [
                "Under $500",
                "$500-$1,000",
                "$1,000-$2,500",
                "$2,500-$5,000",
                "$5,000-$10,000",
                "Over $10,000"
              ]
            }
          }
        },
        "center": {
          "title": "Transplant Center Help",
          "description": "Help you get from your transplant team",
          "q": {
            "center_has_pharmacy": {
              "label": "Does your transplant center have its own pharmacy?",
              "options": [
                "Yes, and I use it",
                "Yes, but I do not use it",
                "No",
                "Not sure"
              ]
            },
            "coordinator_helps_with_meds": {
              "label": "Does your transplant coordinator help you get your medicine?",
              "options": [
                "Yes, a lot",
                "Sometimes",
                "Rarely",
                "Never",
                "I do not have a coordinator"
              ]
            },
            "center_told_about_assistance": {
              "label": "Did your transplant center tell you about programs that help pay for drugs?"
            },
            "center_helped_apply": {
              "label": "Did someone at your center help you apply for help programs?"
            },
            "knows_who_to_call": {
              "label": "Do you know who to call at your center if you have a medicine problem?",
              "options": [
                "Yes, a specific person",
                "Yes, a general number",
                "Not sure who to call",
                "No one to call"
              ]
            }
          }
        },
        "pap": {
          "title": "Help Programs",
          "description": "Programs that help pay for your medicine",
          "q": {
            "uses_pap": {
              "label": "Do you use any programs that help pay for your transplant drugs?"
            },
            "pap_types_used": {
              "label": "Which types have you used? (Pick all that apply)",
              "options": [
                "Drug company copay card",
                "Foundation grant",
                "State program",
                "Hospital charity",
                "Pharmacy discount card",
                "None"
              ]
            },
            "pap_found_how": {
              "label": "How did you first hear about help programs?",
              "options": [
                "Transplant center",
                "Social worker",
                "Support group",
                "Found online",
                "Pharmacy",
                "Just found out now"
              ]
            },
            "pap_gap_experienced": {
              "label": "Have you ever run out of help because a program ended before you could renew?"
            },
            "pap_application_burden": {
              "label": "How hard is it to apply for and keep your help programs?",
              "options": [
                "Easy",
                "Some work",
                "Hard - takes a lot of time",
                "Very hard - almost gave up",
                "Have not applied"
              ]
            }
          }
        },
        "communication": {
          "title": "Communication",
          "description": "How well people keep you informed",
          "q": {
            "pharmacy_center_communicate": {
              "label": "Do your pharmacy and transplant center talk to each other well?",
              "options": [
                "Yes, very well",
                "Usually",
                "Sometimes",
                "Rarely",
                "I have to be the go-between"
              ]
            },
            "notified_of_changes": {
              "label": "Do you get warned ahead of time when your drug costs or coverage will change?",
              "options": [
                "Always",
                "Usually",
                "Sometimes",
                "Rarely",
                "Never"
              ]
            },
            "understood_all_meds": {
              "label": "Do you know what each of your medicines does?",
              "options": [
                "Yes, completely",
                "Mostly",
                "Somewhat",
                "Not really",
                "No"
              ]
            },
            "feels_like_burden": {
              "label": "How often does managing your medicines feel like a second job?",
              "options": [
                "Never",
                "Sometimes",
                "Often",
                "Always"
              ]
            }
          }
        },
        "about": {
          "title": "About You",
          "description": "Optional - helps us understand who is taking this survey",
          "q": {
            "organ_type": {
              "label": "What kind of transplant did you get?",
              "options": [
                "Kidney",
                "Liver",
                "Heart",
                "Lung",
                "Pancreas",
                "More than one organ",
                "Other",
                "Prefer not to say"
              ]
            },
            "years_post_transplant": {
              "label": "How long ago was your transplant?",
              "options": [
                "Less than 1 year",
                "1-2 years",
                "3-5 years",
                "5-10 years",
                "10+ years",
                "Still waiting"
              ]
            },
            "num_medications": {
              "label": "About how many medicines do you take each day?",
              "options": [
                "1-5",
                "6-10",
                "11-15",
                "16-20",
                "More than 20"
              ]
            },
            "insurance_type": {
              "label": "What is your main insurance?",
              "options": [
                "From my job",
                "Marketplace (ACA)",
                "Medicare only",
                "Medicaid only",
                "Medicare + Medicaid",
                "Medicare + extra plan",
                "VA",
                "No insurance",
                "Other"
              ]
            },
            "region": {
              "label": "Where do you live?",
              "options": [
                "Northeast",
                "Southeast",
                "Midwest",
                "Southwest",
                "West Coast",
                "Outside US"
              ]
            }
          }
        }
      }
    },
    "general": {
      "title": "General Medication Survey",
      "sections": {
        "affordability": {
          "title": "Paying for Medicine",
          "description": "How drug costs affect you",
          "q": {
            "cost_barrier": {
              "label": "Have you ever skipped a prescription because it cost too much?"
            },
            "cost_frequency": {
              "label": "How often does cost affect your medicine choices?",
              "options": [
                "Never",
                "Rarely",
                "Sometimes",
                "Often",
                "Always"
              ]
            },
            "rationing": {
              "label": "Have you ever skipped doses or split pills to make medicine last longer?"
            },
            "monthly_spend": {
              "label": "About how much do you spend on medicine each month?",
              "options": [
                "Under $50",
                "$50-$100",
                "$100-$250",
                "$250-$500",
                "$500-$1,000",
                "Over $1,000"
              ]
            },
            "financial_stress": {
              "label": "How much stress do your medicine costs cause?",
              "options": [
                "None",
                "A little",
                "Some",
                "A lot",
                "Very high"
              ]
            }
          }
        },
        "pharmacy": {
          "title": "Pharmacy",
          "description": "Getting your prescriptions filled",
          "q": {
            "pharmacy_type": {
              "label": "Where do you usually get your prescriptions?",
              "options": [
                "Local pharmacy",
                "Chain pharmacy (CVS, Walgreens)",
                "Mail order",
                "Specialty pharmacy",
                "Hospital pharmacy",
                "Different places"
              ]
            },
            "stockout_frequency": {
              "label": "How often is your medicine out of stock when you need it?",
              "options": [
                "Never",
                "Rarely",
                "Sometimes",
                "Often",
                "Very often"
              ]
            },
            "refill_difficulty": {
              "label": "How hard is it to refill your medicine?",
              "options": [
                "Very easy",
                "Easy",
                "OK",
                "Hard",
                "Very hard"
              ]
            },
            "pharmacist_helpful": {
              "label": "Is your pharmacist helpful when you have questions?",
              "options": [
                "Very helpful",
                "Somewhat helpful",
                "OK",
                "Not helpful",
                "Never talked to one"
              ]
            },
            "auto_refill": {
              "label": "Do you use automatic refills?"
            }
          }
        },
        "insurance": {
          "title": "Insurance",
          "description": "How insurance affects your medicine",
          "q": {
            "has_insurance": {
              "label": "Do you have insurance that covers medicine?"
            },
            "insurance_type": {
              "label": "What kind of insurance do you have?",
              "options": [
                "From my job",
                "Marketplace (ACA)",
                "Medicare Part D",
                "Medicaid",
                "VA",
                "No insurance",
                "Other"
              ]
            },
            "prior_auth_experienced": {
              "label": "Has your insurance ever required approval before you could get a drug?"
            },
            "prior_auth_delayed": {
              "label": "If yes, did this delay getting your medicine?"
            },
            "formulary_switch": {
              "label": "Has your insurance ever made you switch to a different drug?"
            },
            "coverage_denial": {
              "label": "Has your insurance ever said no to a drug you needed?"
            }
          }
        },
        "assistance": {
          "title": "Help Programs",
          "description": "Programs that help pay for medicine",
          "q": {
            "knows_about_pap": {
              "label": "Did you know there are programs that help pay for medicine?"
            },
            "uses_assistance": {
              "label": "Do you use any help programs now?"
            },
            "assistance_types": {
              "label": "Which types have you used? (Pick all that apply)",
              "options": [
                "Drug company copay card",
                "Free medicine program",
                "GoodRx or similar discount",
                "State program",
                "Charity or foundation",
                "None"
              ]
            },
            "assistance_found_how": {
              "label": "How did you first hear about help programs?",
              "options": [
                "Doctor",
                "Pharmacist",
                "Online search",
                "Friend or family",
                "Social worker",
                "Just found out now"
              ]
            },
            "assistance_difficulty": {
              "label": "How hard was it to apply for help?",
              "options": [
                "Very easy",
                "Easy",
                "OK",
                "Hard",
                "Very hard",
                "Have not applied"
              ]
            }
          }
        },
        "management": {
          "title": "Taking Your Medicine",
          "description": "How you manage your daily medicines",
          "q": {
            "num_medications": {
              "label": "How many prescription medicines do you take regularly?",
              "options": [
                "1-2",
                "3-5",
                "6-10",
                "11-15",
                "More than 15"
              ]
            },
            "adherence_difficulty": {
              "label": "How hard is it to take your medicines as your doctor says?",
              "options": [
                "Very easy",
                "Easy",
                "OK",
                "Hard",
                "Very hard"
              ]
            },
            "missed_doses": {
              "label": "How often do you miss doses?",
              "options": [
                "Never",
                "Rarely",
                "Sometimes",
                "Often",
                "Very often"
              ]
            },
            "understands_meds": {
              "label": "Do you know what each of your medicines does?",
              "options": [
                "Yes, completely",
                "Mostly",
                "Somewhat",
                "Not really",
                "No"
              ]
            },
            "condition_type": {
              "label": "What health issues do you have? (Pick all that apply)",
              "options": [
                "Diabetes",
                "Heart disease",
                "High blood pressure",
                "Immune system problem",
                "Mental health",
                "Ongoing pain",
                "Cancer",
                "Other ongoing condition",
                "Prefer not to say"
              ]
            }
          }
        }
      }
    }
  },
  "pilot": {
    "partnerProgram": "{{name}} Partner Program",
    "welcomeGeneric": "Welcome, Partner Patients",
    "welcomePartner": "Welcome, {{name}} Patients",
    "heroGeneric": "Your healthcare provider has partnered with us to help you find medication assistance programs.",
    "heroPartner": "{{name}} has partnered with Transplant Medication Navigator to help you find medication assistance programs.",
    "getStarted": "Get Started",
    "ctaSearchTitle": "Search Medications",
    "ctaSearchText": "Find your medications and compare prices",
    "ctaQuizTitle": "My Path Quiz",
    "ctaQuizText": "Get personalized assistance recommendations",
    "ctaGrantsTitle": "Find Grants & Foundations",
    "ctaGrantsText": "Explore financial assistance programs",
    "whatTitle": "What You Can Do Here",
    "what1": "Search for your transplant medications and compare prices",
    "what2": "Find Patient Assistance Programs (PAPs) that may provide medication at no cost if you qualify",
    "what3": "Discover copay cards and manufacturer savings programs",
    "what4": "Access foundation grants for financial assistance",
    "what5": "Learn about insurance navigation and specialty pharmacies",
    "trustFreeTitle": "Free Education",
    "trustFreeText": "Educational resources at no cost",
    "trustLoginTitle": "No Login",
    "trustLoginText": "No account required",
    "trustPrivacyTitle": "Privacy-Safe",
    "trustPrivacyText": "No personal data collected",
    "safetyLabel": "Important:",
    "safetyText": "Legitimate Patient Assistance Programs never charge you to apply. If a site claims to be a PAP but charges fees, it's a scam. Report it.",
    "aboutText": "Transplant Medication Navigator™ was built by a transplant recipient to help patients find medication assistance with free educational resources.",
    "aboutLink": "Learn more about us"
  },
  "common": {
    "closeDialog": "Close dialog",
    "loading": "Loading content",
    "helpfulTipAria": "Helpful tip"
  },
  "changes2027": {
    "banner": {
      "badge": "New for 2027",
      "text": "Medicare, Medicaid, and Marketplace rules change on January 1.",
      "link": "See what's changing"
    },
    "page": {
      "back": "Back to home",
      "badge": "Updated October 26, 2026",
      "title": "What's changing in 2027",
      "intro": "New rules start January 1, 2027. Some will lower what you pay for medicine. Others could put your coverage at risk if you miss a step. Here is what transplant patients need to know, and what to do now.",
      "datesTitle": "Key dates",
      "dates": [
        {
          "when": "Oct 15 – Dec 7, 2026",
          "what": "Medicare open enrollment. Pick or change your 2027 drug plan."
        },
        {
          "when": "Nov 1, 2026 – Jan 15, 2027",
          "what": "Marketplace (HealthCare.gov) open enrollment. Sign up by December 15 for coverage that starts January 1."
        },
        {
          "when": "By Jan 1, 2027",
          "what": "Medicaid work rules start in many states. Some states start sooner, and some may get more time."
        },
        {
          "when": "Jan 1, 2027",
          "what": "New Medicare drug limits and lower prices on 15 drugs begin."
        }
      ],
      "partD": {
        "title": "Medicare Part D: new yearly limit",
        "lead": "Part D is Medicare's drug coverage. In 2027, the most you pay for covered drugs in a year goes up.",
        "capLabel": "Yearly limit on what you pay",
        "deductibleLabel": "Highest deductible a plan can charge",
        "was": "2026",
        "now": "2027",
        "p1": "Once your share of drug costs reaches $2,400, you pay $0 for covered drugs for the rest of 2027. Your monthly plan fee does not count toward the $2,400.",
        "p2": "Many transplant patients take several costly drugs and reach the limit early in the year. You can ask your plan for the Medicare Prescription Payment Plan. It splits what you owe into monthly bills instead of one big bill in January.",
        "actionTitle": "Do this before December 7",
        "actions": [
          "Read the \"Annual Notice of Change\" letter your plan mailed you. It lists what changes in 2027.",
          "Check that every transplant drug you take is on your plan's 2027 drug list. Check the exact form too, like brand or generic, and capsules or liquid.",
          "Compare plans on Medicare.gov or call 1-800-MEDICARE (1-800-633-4227).",
          "If money is tight, apply for Extra Help. It can lower your drug costs a lot."
        ],
        "planFinder": "Compare 2027 plans on Medicare.gov"
      },
      "prices": {
        "title": "Lower Medicare prices on 15 drugs",
        "lead": "Medicare bargained with drug makers. Lower prices start January 1, 2027, for people with Medicare drug coverage. Several of these drugs are common after a transplant.",
        "colDrug": "Drug",
        "colUse": "Used for",
        "colList": "List price (30 days)",
        "colNew": "Medicare price in 2027",
        "uses": {
          "xifaxan": "Liver: confusion from liver disease (hepatic encephalopathy)",
          "semaglutide": "Diabetes, heart health, weight",
          "janumet": "Diabetes",
          "tradjenta": "Diabetes",
          "trelegy": "Lungs: asthma and COPD",
          "breo": "Lungs: asthma and COPD",
          "ofev": "Lungs: scarring (pulmonary fibrosis)"
        },
        "othersLabel": "The other 8 drugs:",
        "others": "Xtandi, Pomalyst, Ibrance, Linzess, Calquence, Austedo, Vraylar, and Otezla.",
        "note": "List prices are from 2024. What you pay still depends on your plan. A lower price can mean a lower copay, and it can help you pay less before you reach the $2,400 limit. These prices are for Medicare only.",
        "round1": "The first 10 Medicare prices started in January 2026. They include Eliquis, Xarelto, Januvia, Jardiance, Farxiga, and Entresto."
      },
      "medicaid": {
        "title": "Medicaid: new work rules",
        "warning": "If you lose Medicaid, you could lose coverage for your anti-rejection medicine. Do not ignore mail from your Medicaid office.",
        "whoTitle": "Who has to follow the new rules",
        "who": "Adults ages 19 to 64 who got Medicaid because their state expanded it. Each month, you will need to show 80 hours of work, school, job training, or volunteer work. You can mix them.",
        "exemptTitle": "Who does not have to",
        "exempt": [
          "People who are pregnant or just had a baby",
          "People with a disability, or who are \"medically frail\" (have a serious or complex health problem)",
          "Parents and caregivers of a child under 14, or of a person with a disability",
          "American Indians and Alaska Natives",
          "Some others, depending on your state"
        ],
        "transplantTitle": "What this means after a transplant",
        "transplant": "Transplant patients are not named in the rules. Many may count as \"medically frail.\" But you may have to show that your health makes it hard to do 80 hours a month. Each state decides what proof it needs.",
        "actionTitle": "Do this now",
        "actions": [
          "Ask your transplant social worker if the work rules apply to you.",
          "Ask your transplant team for a letter about your health, in case your state asks for proof.",
          "Open and answer every letter from Medicaid by its deadline.",
          "If your Medicaid stops, call your transplant center the same day."
        ],
        "notMedicare": "These rules are for Medicaid only. They do not change Medicare.",
        "stateLink": "Find your state's Medicaid office"
      },
      "marketplace": {
        "title": "Marketplace plans: less help with premiums",
        "lead": "Marketplace plans are the health plans you buy at HealthCare.gov or your state's site.",
        "p1": "The extra premium help that started in 2021 ended after 2025. Congress did not renew it. In 2027, households that earn more than 4 times the poverty level get no help with premiums at all. Most plans are also expected to cost more.",
        "p2": "Copay cards from drug makers can still be used with Marketplace plans.",
        "actionTitle": "Do this during open enrollment",
        "actions": [
          "Look at the 2027 prices. Do not let last year's plan renew without checking it.",
          "Check that your transplant drugs and transplant center are covered by the plan.",
          "Sign up by December 15 so your plan starts January 1."
        ],
        "link": "Shop 2027 plans on HealthCare.gov"
      },
      "pending": {
        "title": "Still to come",
        "p1": "Medicare has not yet set the 2027 costs for Part B or Part B-ID. Part B-ID is the drug-only coverage for kidney transplant patients. Medicare usually announces these in November. In 2026, Part B-ID costs $121.60 a month.",
        "p2": "We will update this page when Medicare announces them."
      },
      "checklistTitle": "Your 2027 checklist",
      "checklist": [
        "Medicare: check your plan's 2027 drug list by December 7.",
        "Medicaid: ask if the work rules apply to you, and get a letter from your transplant team.",
        "Marketplace: compare 2027 plans and sign up by December 15.",
        "Everyone: tell your transplant pharmacist about any change in your coverage."
      ],
      "helpTitle": "Need help paying for your medicine?",
      "helpText": "Answer 5 questions and we will show the programs that may help you.",
      "helpCta": "Find my options",
      "sourcesTitle": "Where this information comes from",
      "sourcesNote": "Figures are from the Centers for Medicare & Medicaid Services (CMS). Rules can change. Check with your plan or state before you decide.",
      "sources": {
        "partD": "CMS: 2027 Part D limits (Rate Announcement, April 2026)",
        "prices": "CMS: Negotiated prices for 2027",
        "medicaid": "CMS: Medicaid work rules (June 2026)",
        "healthcare": "HealthCare.gov: open enrollment dates"
      },
      "externalNote": "(opens in a new tab)"
    }
  }
}
